import React, { useState, useEffect, useRef } from 'react'
import { useParams, useHistory } from 'react-router'
import { useSelector, useDispatch } from 'react-redux'
import { fetchAssemblyAccessories } from '../../AssemblyTwo/assemblyAccessoriesApi'

// Reusing existing CSS file for layout consistency
import '../../PlaydynamexNew/PlaydynamexNew.css'
import renderImage from '../../../source/importImg'
import renderPrgImage from '../../../source/programImg'
import renderCompImg from '../../../source/Comp_Img'
import getBackendImage from '../../../source/imageUtil'
import ByteRecorderPopup from './ByteRecorderPopup'
import OrientationModal from './OrientationModal'
import { connectSerialDeviceIntermediate } from '../../WebSerialConnection/SerialUSBConnect'
import { webSerialConnect, deviceMismatch } from '../../../redux/actions'
import { FaRegSave } from 'react-icons/fa'
import { AiOutlineAudio } from 'react-icons/ai'
import { confirmVoiceoverGeneration } from '../voiceoverCredits'
import {
    livePortsNumber,
    compName_character,
} from '../../logic/panels/helpers/LiveButton'
// Per-board allowed-port map (TOKI1 / POKI1 / ...) — same source the live
// Assembly uses. Drives board-aware ports for the editor.
import PORT_MAP from '../../AssemblyTwo/AllowedPortTypes'

const resolveImage = (imgValue) => {
    if (!imgValue)
        return 'https://cdn-icons-png.flaticon.com/512/863/863268.png'

    if (typeof imgValue === 'string') {
        // Regex updated to handle BOTH single and double quotes: /['"]([^'"]+)['"]/

        // 1. Handle Legacy Hardcoded Project Images
        if (imgValue.includes('renderPrgImage')) {
            const match = imgValue.match(/['"]([^'"]+)['"]/)
            return match ? renderPrgImage(match[1]) : imgValue
        } else if (imgValue.includes('renderImage')) {
            const match = imgValue.match(/['"]([^'"]+)['"]/)
            return match ? renderImage(match[1]) : imgValue
        } else if (imgValue.includes('renderCompImg')) {
            const match = imgValue.match(/['"]([^'"]+)['"]/)
            return match ? renderCompImg(match[1]) : imgValue
        }
        // 2. Handle External URLs or Base64 Data
        else if (
            imgValue.startsWith('http') ||
            imgValue.startsWith('data:') ||
            imgValue.startsWith('blob:')
        ) {
            return imgValue
        }
        // 3. Handle Backend Uploaded Images
        else if (!imgValue.startsWith('/')) {
            try {
                return getBackendImage(imgValue)
            } catch (e) {
                console.error('Failed to load backend image:', e)
                return `${BACKEND_URL}/static/images/${imgValue}`
            }
        }
    }

    return imgValue
}

const BACKEND_URL = process.env.REACT_APP_API_BASE || ''
const STAGING_URL = `${BACKEND_URL}/api/staging`

const COMPONENT_LABELS = {
    board: 'Board', // generic board label
    toki_board: 'TOKI Board', // legacy – keep for old JSONs already in state
    model: '3D Animation',
    image: 'Image',
    output: 'Output Component',
    // custom_output: 'Custom Output (Manual)',
    button_control: 'Push Button',
    tack_button_control: 'Tack Button',
    hold_button_control: 'Hold Button',
    trigger_button_control: 'Trigger Button (Momentary)',
    slider_control: 'Slider',
    rgb_control: 'RGB Slider',
}

const CATEGORIES = {
    // 'board' is the canonical type; 'toki_board' kept for backward-compat
    board: ['board', 'toki_board'],
    model: ['model'],
    output: ['output', 'custom_output'],
    input: [
        'button_control',
        'tack_button_control',
        'hold_button_control',
        'trigger_button_control',
        'slider_control',
        'rgb_control',
    ],
}

const PORT_NAME_MAP = {
    servo_motor1: 'S1',
    servo_motor2: 'S2',
    M3: 'M2',
}

// --- BOARD-AWARE CONFIGURATION (Toki / Poki / ...) ---
// PlayAdmin was originally Toki-only. To support Poki (and future "-ki" boards)
// the active board is resolved from sessionStorage.allowedDevice — the same
// source the live Assembly uses — and all board-specific data is keyed off it.
const DEFAULT_BOARD_KEY = 'TOKI1'

// Per-board panel config:
//  - label: heading shown in the editor's "Board" section.
//  - image: board background image filename served from the backend
//    (/static/images, e.g. toki.png / poki.png). Stored on the board
//    component's properties.image and rendered by the live PlayScreen.
//  - device: device name used for live-byte port-number lookups
//    (livePortsNumber[device]).
const BOARD_CONFIG = {
    TOKI1: { label: 'TOKI Board', image: 'toki.png', device: 'Toki' },
    POKI1: { label: 'POKI Board', image: 'poki.png', device: 'Poki' },
}

// Resolve the AllowedPortTypes board key from the stored device name.
// Mirrors AssemblyAdmin (Toki v1 -> TOKI1, Poki v1 -> POKI1). Falls back to
// TOKI1 if the device is unknown or has no port-map entry.
const resolveBoardKey = (deviceName) => {
    const raw = (deviceName || 'Toki').toString().trim()
    const match = raw.match(/^([A-Za-z]+)(\d*)$/)
    const name = (match ? match[1] : raw).toUpperCase()
    const version = (match && match[2]) || '1'
    if (PORT_MAP[`${name}${version}`]) return `${name}${version}`
    if (PORT_MAP[`${name}1`]) return `${name}1`
    return DEFAULT_BOARD_KEY
}

const OPERATORS = ['==', '>', '<', '>=', '<=', '!=']

const BUTTON_INPUT_TYPES = [
    'button_control',
    'tack_button_control',
    'hold_button_control',
    'trigger_button_control',
]
const SLIDER_INPUT_TYPES = ['slider_control']
const RGB_SLIDER_INPUT_TYPES = ['rgb_control']

const RGB_DEFAULT_BG_IMAGE = "renderImage('Eye_bg_Svg')"
const RGB_DEFAULT_SLIDERS = [
    {
        name: 'Red',
        mapping: 'red_value',
        legacyName: 'L_red',
        activeImg: 'red_slider',
        inactiveImg: 'inactiveslider',
    },
    {
        name: 'Green',
        mapping: 'green_value',
        legacyName: 'L_green',
        activeImg: 'green_slider',
        inactiveImg: 'inactiveslider',
    },
    {
        name: 'Blue',
        mapping: 'blue_value',
        legacyName: 'L_blue',
        activeImg: 'blue_slider',
        inactiveImg: 'inactiveslider',
    },
]

// ---------------------------------------------------------------------------
// migrateToDirectLinks – translates old mapping-string schema to direct inputId
// links. Intended to be run on a deep-cloned formData object.
// ---------------------------------------------------------------------------
function migrateToDirectLinks(formData) {
    const components = formData.components || []

    // Build a lookup: { [mappingString]: inputComponent }
    const mappingToInput = {}
    components
        .filter((c) => CATEGORIES.input.includes(c.type))
        .forEach((comp) => {
            if (comp.mapping) mappingToInput[comp.mapping] = comp
            // RGB control exposes per-channel mappings
            if (comp.type === 'rgb_control') {
                ;(comp.properties?.sliders || RGB_DEFAULT_SLIDERS).forEach(
                    (slider) => {
                        if (slider.mapping)
                            mappingToInput[slider.mapping] = comp
                    }
                )
            }
        })

    const translateCondition = (cond) => {
        if (cond.inputId) return cond // Already migrated
        const inputComp = mappingToInput[cond.mapping]
        if (!inputComp) return cond // Unknown mapping – keep as-is

        if (BUTTON_INPUT_TYPES.includes(inputComp.type)) {
            const isOn =
                cond.operator === '>'
                    ? cond.value <= 0
                    : cond.operator === '!='
                      ? cond.value === 0
                      : cond.operator === '=='
                        ? cond.value > 0
                        : cond.value > 0
            return {
                inputId: inputComp.id,
                inputType: inputComp.type,
                expectedStatus: isOn ? 'ON' : 'OFF',
            }
        }

        if (SLIDER_INPUT_TYPES.includes(inputComp.type)) {
            return {
                inputId: inputComp.id,
                inputType: inputComp.type,
                operator: cond.operator || '>',
                value: cond.value ?? 0,
            }
        }

        if (RGB_SLIDER_INPUT_TYPES.includes(inputComp.type)) {
            // Identify which channel by matching the slider's mapping
            let channel = 'red'
            const matchedSlider = (
                inputComp.properties?.sliders || RGB_DEFAULT_SLIDERS
            ).find((s) => s.mapping === cond.mapping)
            if (matchedSlider) {
                channel = matchedSlider.name.toLowerCase()
            }
            return {
                inputId: inputComp.id,
                inputType: 'rgb_control',
                channel,
                operator: cond.operator || '>',
                value: cond.value ?? 0,
            }
        }

        return cond
    }

    const migratedComponents = components.map((comp) => {
        if (comp.type === 'output' || comp.type === 'custom_output') {
            const newStates = (comp.properties?.states || []).map((state) => {
                // If already migrated to conditionBlocks, just retranslate each block
                if (state.conditionBlocks) {
                    const newBlocks = state.conditionBlocks.map((block) =>
                        block.map(translateCondition)
                    )
                    return { ...state, conditionBlocks: newBlocks }
                }
                // Legacy: wrap flat conditions array into the first conditionBlock
                const translatedConditions = (state.conditions || []).map(
                    translateCondition
                )
                const newState = {
                    ...state,
                    conditionBlocks: [translatedConditions],
                }
                delete newState.conditions
                return newState
            })
            return {
                ...comp,
                properties: { ...comp.properties, states: newStates },
            }
        }

        return comp
    })

    const migratedSteps = (formData.steps || []).map((step) => {
        // Destructure to separate 'validations' from the rest of the step properties
        const { validations, ...stepWithoutValidations } = step

        if (stepWithoutValidations.conditionBlocks) {
            return {
                ...stepWithoutValidations,
                conditionBlocks: stepWithoutValidations.conditionBlocks.map(
                    (block) => block.map(translateCondition)
                ),
            }
        }

        // Legacy mapping
        const translated = (validations || []).map(translateCondition)
        return { ...stepWithoutValidations, conditionBlocks: [translated] }
    })

    return { ...formData, components: migratedComponents, steps: migratedSteps }
}

// ---------------------------------------------------------------------------
// migrateToInputDriven – inverts the output-driven state schema into the new
// input-driven event/condition schema.  Outputs lose their `states` array;
// inputs gain `events` (buttons) or `conditions` (sliders).
// Run AFTER migrateToDirectLinks so all conditions use inputId / inputType.
// ---------------------------------------------------------------------------
function migrateToInputDriven(formData) {
    const cloned = JSON.parse(JSON.stringify(formData))
    const components = cloned.components || []

    // Build lookup: compId → component (inputs only)
    const inputById = {}
    components.forEach((comp) => {
        if (CATEGORIES.input.includes(comp.type)) inputById[comp.id] = comp
    })

    // Ensure every input has a clean events / conditions structure
    components.forEach((comp) => {
        if (
            ['button_control', 'tack_button_control', 'hold_button_control'].includes(
                comp.type
            )
        ) {
            if (!comp.properties.events)
                comp.properties.events = { onON: [], onOFF: [] }
        } else if (comp.type === 'trigger_button_control') {
            if (!comp.properties.events)
                comp.properties.events = { onTrigger: [] }
        } else if (['slider_control', 'rgb_control'].includes(comp.type)) {
            if (!comp.properties.conditions) comp.properties.conditions = []
        }
    })

    // Invert: iterate output states → push actions onto the relevant input comp
    components.forEach((outputComp) => {
        if (outputComp.type !== 'output' && outputComp.type !== 'custom_output')
            return

        const states = outputComp.properties?.states || []
        states.forEach((state) => {
            const blocks = state.conditionBlocks
                ? state.conditionBlocks
                : state.conditions
                  ? [state.conditions]
                  : []

            blocks.forEach((block) => {
                if (block.length === 0) return

                if (block.length === 1) {
                    // Single-condition block → clean 1-to-1 inversion
                    const cond = block[0]
                    const inputComp = inputById[cond.inputId]
                    if (!inputComp) return

                    const action = {
                        outputId: outputComp.id,
                        targetImage: state.image || '',
                    }

                    if (
                        [
                            'button_control',
                            'tack_button_control',
                            'hold_button_control',
                        ].includes(inputComp.type)
                    ) {
                        const key =
                            cond.expectedStatus === 'OFF' ? 'onOFF' : 'onON'
                        inputComp.properties.events[key].push(action)
                    } else if (inputComp.type === 'trigger_button_control') {
                        inputComp.properties.events.onTrigger.push(action)
                    } else if (
                        ['slider_control', 'rgb_control'].includes(
                            inputComp.type
                        )
                    ) {
                        const newCond = {
                            type: 'simple',
                            operator: cond.operator || '>',
                            value: cond.value ?? 0,
                            actions: [action],
                        }
                        if (inputComp.type === 'rgb_control') {
                            newCond.channel = cond.channel || 'red'
                        }
                        inputComp.properties.conditions.push(newCond)
                    }
                }
                // Multi-condition AND blocks are skipped (too complex to auto-invert)
            })
        })

        // Remove states – output is now a dumb visual receiver
        delete outputComp.properties.states
    })

    // Strip legacy interactions array from model components
    components.forEach((comp) => {
        if (comp.type === 'model' && comp.properties) {
            delete comp.properties.interactions
        }
    })

    return { ...cloned, components }
}

const DEFAULT_OUTPUT_IMAGES = {
    geared_motor: 'component_geared_motor.png',
    mini_geared_motor: 'n20@2x.png',
    dynamex_motor: 'dynamexMotor@2x.png',
    dc_motor: 'dcmotor.png',
    servo_motor: 'component_servo_motor.png',
    servo_motor_270: 'servo_270.png',
    servo_motor_360: 'component_servo_motor_360.png',
    servo_motor_sg90: 'sg90@3x.png',
    analog_3_pin_input: 'analog_3_Pin_Input@2x.png',
    pwm_3_pin_output: 'Analog_3_Pin_Output@2x.png',
    digital_3_pin_input: 'Digital_3_Pin_Input@2x.png',
    digital_3_pin_output: 'Digital_3_Pin_Output@2x.png',
    rgb: 'TokiRGB.png',
    ultrasonic_sensor: 'ultrasonicToki@2x.png',
    // Poki-specific accessories. These have no backend /static/images asset, so
    // they reference the bundled component artwork via renderCompImg(); the
    // shared resolveImage() (in both this admin and the live PlayScreen) resolves
    // the renderCompImg('...') form to the imported image.
    oled: 'tokiOled@2x.png',
    imu: 'IMU.png',
    color_sensor: 'colorSensor@2x.png',
}

const COMPONENT_RANGES = {
    mini_geared_motor: { min: -100, max: 100 },
    geared_motor: { min: -100, max: 100 },
    dc_motor: { min: -100, max: 100 },
    dynamex_motor: { min: -100, max: 100 },
    servo_motor: { min: 0, max: 180 },
    servo_motor_270: { min: 0, max: 270 },
    servo_motor_sg90: { min: 0, max: 270 },
    servo_motor_360: { min: -100, max: 100 },
    led: { min: 0, max: 100 },
    led_1c: { min: 0, max: 100 },
    pwm_3_pin_output: { min: 0, max: 100 },
    digital_3_pin_output: { min: 0, max: 255 },
    analog_3_pin_input: { min: 0, max: 4095 },
    digital_3_pin_input: { min: 0, max: 1 },
    ultrasonic_sensor: { min: 0, max: 400 },
}

// ---------------------------------------------------------------------------
// calcLiveEntry – builds a live step entry for an input component.
// The output component is found by matching inputComp.name (normalised) against
// output component names.  For buttons the ON value defaults to max.
// Returns null if validation fails (name not set, or no matching output).
// `boardDevice` is the active board's device name (Toki / Poki / ...) used for
// livePortsNumber port lookups.
// ---------------------------------------------------------------------------
const calcLiveEntry = (inputComp, components, boardDevice) => {
    const BUTTON_TYPES = [
        'button_control',
        'tack_button_control',
        'hold_button_control',
        'trigger_button_control',
    ]
    const normalizedName =
        inputComp.name?.trim().toLowerCase().replace(/ /g, '_') || ''

    // RGB slider input: fixed 8-byte RGB packet – no output comp lookup needed.
    if (inputComp.type === 'rgb_control') {
        return {
            inputId: inputComp.id,
            fixedBytes: [76, 87, 76, 75, 0, 0, 0, 0],
            isFree: true,
            compsIds: {},
        }
    }

    // For all other types, the input's "name" field MUST be set and must match
    // an output component's name so we can look up port / char code.
    if (!normalizedName) return null

    // slider_control must not target an RGB output (use rgb_control for that)
    if (inputComp.type === 'slider_control' && normalizedName === 'rgb') {
        return null
    }

    const outputComp = (components || []).find(
        (c) =>
            (c.type === 'output' || c.type === 'custom_output') &&
            c.name?.trim().toLowerCase().replace(/ /g, '_') === normalizedName
    )
    if (!outputComp) return null

    const isRgbOutput = normalizedName === 'rgb'
    const isButtonType = BUTTON_TYPES.includes(inputComp.type)

    if (isRgbOutput && isButtonType) {
        // Button driving an RGB output — use the 8-byte RGB LW packet.
        return {
            inputId: inputComp.id,
            fixedBytes: [76, 87, 76, 75, 0, 0, 0, 0],
            isFree: true,
            compsIds: {},
        }
    }

    const device = boardDevice || 'Toki'
    const charCode = compName_character[normalizedName] ?? 80 // 'P' as fallback
    const portNum =
        livePortsNumber?.[device]?.[outputComp.port] ??
        livePortsNumber?.Ace?.[outputComp.port] ??
        0

    return {
        inputId: inputComp.id,
        fixedBytes: [76, 87, charCode, portNum, 0],
        isFree: true,
        compsIds: {},
    }
}

// --- Icons for Toggle Headers ---
const EyeVisible = () => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
        <circle cx="12" cy="12" r="3"></circle>
    </svg>
)
const EyeHidden = () => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
        <line x1="1" y1="1" x2="23" y2="23"></line>
    </svg>
)

const PlayAdmin = () => {
    const { subId, screenId } = useParams()
    const history = useHistory()

    // Resolve the active board (Toki / Poki / ...) from the same source the live
    // Assembly uses, so the panel surfaces the correct board image, label, ports
    // and live-byte device for the project being edited.
    const allowedDevice = sessionStorage.getItem('allowedDevice') || 'Toki'
    const boardKey = resolveBoardKey(allowedDevice)
    const boardConfig =
        BOARD_CONFIG[boardKey] || BOARD_CONFIG[DEFAULT_BOARD_KEY]
    const boardLabel = boardConfig.label
    const boardImage = boardConfig.image
    const boardDevice = boardConfig.device
    // Flat, de-duplicated list of physical ports for the active board, used by
    // the manual custom-output port dropdown.
    const boardPorts = Array.from(
        new Set(Object.values(PORT_MAP[boardKey] || {}).flat())
    )

    // STATE
    const [loading, setLoading] = useState(true)
    const [formData, setFormData] = useState(null)
    const [activeStepIndex, setActiveStepIndex] = useState(0)
    const [uploading, setUploading] = useState(false)
    const [assemblyConnections, setAssemblyConnections] = useState([])
    const [selectedCategory, setSelectedCategory] = useState('')
    const [pendingType, setPendingType] = useState('') // Tracks the specific type before adding
    const [showAddedFeedback, setShowAddedFeedback] = useState(false) // For the "Added!" message
    const [subprogramTitle, setSubprogramTitle] = useState('')

    const [preMigrationBackup, setPreMigrationBackup] = useState(null)

    // NEW: Animation Selection State
    const [availableAnimations, setAvailableAnimations] = useState([])
    const [showAnimModal, setShowAnimModal] = useState(false)
    const [selectedAnimation, setSelectedAnimation] = useState('')

    // Tracks whether the next animation list load is for a freshly uploaded
    // model (so we auto-open the selection modal).
    const isNewUploadRef = useRef(false)

    // (Inside the PlayAdmin functional component, with your other state declarations)
    const [assemblyScreens, setAssemblyScreens] = useState([])
    const [selectedAssemblyScreen, setSelectedAssemblyScreen] = useState('')

    // ACCORDION UI STATE
    const [expandedComps, setExpandedComps] = useState({})
    const [expandedStates, setExpandedStates] = useState({})

    // NEW STATE: TTS Generation tracking
    const [isGeneratingTTS, setIsGeneratingTTS] = useState(false)

    // Byte Recorder popup state
    const [byteRecorderOpen, setByteRecorderOpen] = useState(false)
    const [byteRecorderCompIndex, setByteRecorderCompIndex] = useState(null)
    // Action-level byte recorder context: { stepIndex, actionIndex } | null
    const [byteRecorderActionContext, setByteRecorderActionContext] =
        useState(null)

    // Raw bytes live send state (tracks which comp index is actively sending)
    const [rawBytesLiveIndex, setRawBytesLiveIndex] = useState(null)
    // Action-level live send key: `${stepIndex}-${actionIndex}` | null
    const [actionRawBytesLiveKey, setActionRawBytesLiveKey] = useState(null)

    // NEW: Tracks if we are selecting animation for 'main' or a specific interaction index
    const [animSelectionContext, setAnimSelectionContext] = useState({
        type: 'main',
        index: null,
    })
    // NEW: Tracks the URL of the model currently being "probed" for animations
    const [tempModelUrl, setTempModelUrl] = useState('')

    // Orientation modal state
    const [orientationModalOpen, setOrientationModalOpen] = useState(false)
    const [orientationModalContext, setOrientationModalContext] = useState({
        type: 'main',
        interactionIndex: null,
    })

    // Redux state for device communication
    const dispatch = useDispatch()
    const worker = useSelector((state) => state.worker)
    const webSerial = useSelector((state) => state.webSerial)
    const device = useSelector((state) => state.device)
    const socketData = useSelector((state) => state.socketData)

    const requestBluetoothConnection = async () => {
        if (webSerial?.isConnected) return
        try {
            const res = await connectSerialDeviceIntermediate(
                { isAutomatic: false },
                worker,
                socketData
            )
            if (res !== undefined) {
                if (!res.isPortOpen) {
                    dispatch(
                        deviceMismatch({
                            deviceMismatched: res.matchedObj_with_Version,
                            isMemoryPresent: res.isMemoryPresent,
                            isArmError: res.isArmError,
                            plodeNativeData: {
                                ...device.plodeNativeData,
                                mountPoint: res.mountPoint,
                            },
                        })
                    )
                } else {
                    dispatch(
                        deviceMismatch({
                            deviceMismatched: {},
                            isMemoryPresent: res.isMemoryPresent,
                            isArmError: res.isArmError,
                            plodeNativeData: {
                                ...device.plodeNativeData,
                                mountPoint: res.mountPoint,
                            },
                        })
                    )
                }
                dispatch(webSerialConnect({ port: res.port }))
            } else {
                alert('Could not connect to device. Please try again.')
            }
        } catch (err) {
            console.error('Bluetooth connection error:', err)
            alert('Error connecting to device. Please try again.')
        }
    }

    // The URL of the model whose animations we currently need. Prefer the
    // model being probed after an upload; otherwise the saved main model.
    const probeUrl =
        tempModelUrl ||
        formData?.components?.find((c) => c.type === 'model')?.modelUrl

    // Load animation names from the backend (which reads only the GLB JSON
    // chunk from GCS) instead of downloading + decoding the whole model in a
    // hidden <model-viewer>. Runs whenever the target model URL changes.
    useEffect(() => {
        if (!probeUrl) {
            setAvailableAnimations([])
            return
        }
        let cancelled = false
        ;(async () => {
            try {
                const res = await fetch(`${BACKEND_URL}/api/model-animations`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ url: probeUrl }),
                })
                if (!res.ok) throw new Error('Failed to fetch animations')
                const data = await res.json()
                if (cancelled) return

                const animations = data.animations || []
                setAvailableAnimations(animations)

                if (isNewUploadRef.current) {
                    if (animations.length > 0)
                        setSelectedAnimation(animations[0])
                    setShowAnimModal(true)
                    isNewUploadRef.current = false
                } else if (animations.length > 0) {
                    setSelectedAnimation((prev) => prev || animations[0])
                }
            } catch (err) {
                if (!cancelled)
                    console.error('Failed to load model animations:', err)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [probeUrl])

    const fetchAssemblyScreenList = async () => {
        try {
            const apiProjectId = subId || 'talking-dragon'
            const res = await fetch(
                `${BACKEND_URL}/api/staging/screens/assembly/${encodeURIComponent(apiProjectId)}`,
                {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                }
            )
            if (!res.ok) return []
            const data = await res.json()
            return data.screens || []
        } catch (e) {
            console.error('Failed to fetch assembly screens', e)
            return []
        }
    }

    const ensureAssemblyConnections = async (overrideScreenId) => {
        try {
            const apiProjectId = subId || 'talking-dragon'
            const isPreviewMode = true
            const assemblyScreenId =
                overrideScreenId !== undefined
                    ? overrideScreenId
                    : selectedAssemblyScreen

            const asmData = await fetchAssemblyAccessories(
                apiProjectId,
                isPreviewMode,
                assemblyScreenId
            )

            let freshConnections = []
            if (asmData && asmData.steps) {
                freshConnections = asmData.steps
                    .filter((s) => s.component && s.port)
                    .map((s) => ({
                        component: s.component,
                        port: s.port,
                    }))
                setAssemblyConnections(freshConnections)
            }
            return freshConnections
        } catch (asmErr) {
            console.error('Failed to fetch assembly connections:', asmErr)
            return []
        }
    }

    const handleAssemblyScreenChange = async (screenId) => {
        // Warn the user that changing this will delete hardware logic
        if (
            formData &&
            formData.components.length > 0 &&
            !window.confirm(
                'Changing the assembly screen will remove any hardware components from your project that do not exist in the new screen. Continue?'
            )
        ) {
            return
        }

        setSelectedAssemblyScreen(screenId)
        const freshConnections = await ensureAssemblyConnections(screenId)

        // Force a sync to purge old hardware from the state immediately
        setFormData((prev) => {
            if (!prev) return prev
            const synced = syncComponentsWithConnections(
                prev.components,
                prev.steps,
                freshConnections
            )
            return {
                ...prev,
                components: synced.components,
                steps: synced.steps,
            }
        })
    }

    // Helper to purge stale components and clean up steps
    const syncComponentsWithConnections = (
        currentComponents,
        currentSteps,
        connections
    ) => {
        // 1. Filter out hardware that no longer exists in the new assembly
        const updatedComponents = currentComponents
            .filter((comp) => {
                if (
                    comp.type === 'output' ||
                    comp.type === 'custom_output' ||
                    comp.type === 'slider_control' ||
                    comp.type === 'custom_slider_control' ||
                    comp.type === 'button_control' ||
                    comp.type === 'tack_button_control' ||
                    comp.type === 'hold_button_control'
                ) {
                    if (!comp.name) return true // Keep UI elements not yet linked to hardware
                    const stillExists = connections.some(
                        (c) => c.component === comp.name
                    )
                    return stillExists
                }
                return true
            })
            .map((comp) => {
                // 2. Update the port if it changed in the new assembly
                if (
                    comp.type === 'output' ||
                    comp.type === 'custom_output' ||
                    comp.type === 'slider_control' ||
                    comp.type === 'custom_slider_control' ||
                    comp.type === 'button_control' ||
                    comp.type === 'tack_button_control' ||
                    comp.type === 'hold_button_control'
                ) {
                    // Already wired to an exact connection (name + port) — leave
                    // it untouched. This is essential when the same component
                    // exists on multiple ports (e.g. dc_motor on M1 AND M2):
                    // matching by name alone would collapse both onto one port.
                    const exactMatch = connections.some(
                        (c) =>
                            c.component === comp.name && c.port === comp.port
                    )
                    if (exactMatch) return comp

                    // No exact match: the component's port may have drifted in
                    // the assembly. Only remap when it's unambiguous (exactly one
                    // connection carries this component name); with duplicates we
                    // can't tell which port this instance should take, so we keep
                    // it as-is rather than corrupt it.
                    const nameMatches = connections.filter(
                        (c) => c.component === comp.name
                    )
                    if (
                        nameMatches.length === 1 &&
                        nameMatches[0].port !== comp.port
                    ) {
                        return { ...comp, port: nameMatches[0].port }
                    }
                }
                return comp
            })

        // 3. Clean up steps (remove deleted component IDs from visibleComponents and highlight arrays)
        const validCompIds = new Set(updatedComponents.map((c) => c.id))
        const updatedSteps = currentSteps.map((step) => ({
            ...step,
            visibleComponents: (step.visibleComponents || []).filter((id) =>
                validCompIds.has(id)
            ),
            enableComponents: (step.enableComponents || []).filter((id) =>
                validCompIds.has(id)
            ),
            highlight: (step.highlight || []).filter((id) =>
                validCompIds.has(id)
            ),
        }))

        return { components: updatedComponents, steps: updatedSteps }
    }

    // Build the API path: include screenId when available
    const apiPath = screenId ? `play/${subId}/${screenId}` : `play/${subId}`

    // 1. FETCH DATA
    // 1. FETCH DATA
    useEffect(() => {
        // Check if we're returning from a preview with a pending migration
        const savedBackup = sessionStorage.getItem('migrationBackup')
        const savedMigrated = sessionStorage.getItem('migratedFormData')
        if (savedBackup && savedMigrated) {
            setPreMigrationBackup(JSON.parse(savedBackup))
            setFormData(JSON.parse(savedMigrated))
            setLoading(false)
            // Don't clear sessionStorage here — keep it until save or revert
            return
        }

        const fetchProject = async () => {
            try {
                const screens = await fetchAssemblyScreenList()

                const res = await fetch(`${STAGING_URL}/${apiPath}`, {
                    credentials: 'include',
                })
                if (!res.ok) throw new Error('Failed to fetch staging data')
                // 1. Get the raw data
                // let rawPlayData = await res.json()

                // 2. Pass it through the migrator immediately!
                // const playData = migrateLegacyIds(rawPlayData)

                const playData = await res.json()

                setSubprogramTitle(await fetchSubprogramTitle())

                const defaultScreen = screens.length > 0 ? screens[0] : ''
                const savedScreenId = playData.assemblyScreenId || defaultScreen

                // Set both together in the same synchronous block so React batches
                // them into one render, preventing an intermediate render where
                // assemblyScreens has data but selectedAssemblyScreen is still ''
                // (which the browser would display as the first option).
                setAssemblyScreens(screens)
                setSelectedAssemblyScreen(savedScreenId)
                let freshConnections =
                    await ensureAssemblyConnections(savedScreenId)

                // --- NEW MAPPING LOGIC ---
                // Harvest global settingbytes: prefer explicit top-level field,
                // else fall back to the first non-null legacy per-component value.
                let harvestedSettingbytes = playData.settingbytes || null

                let initialComponents = (playData.components || []).map(
                    (comp) => {
                        // Normalise legacy 'toki_board' type to canonical 'board'
                        if (comp.type === 'toki_board')
                            comp = { ...comp, type: 'board' }
                        if (comp.properties) {
                            // --- Harvest legacy per-component settingbytes into top-level ---
                            if (!harvestedSettingbytes) {
                                harvestedSettingbytes =
                                    comp.properties.settingbytes ||
                                    comp.properties.settingBytes ||
                                    comp.properties.enablebytes ||
                                    comp.properties.enableBytes ||
                                    null
                            }

                            // 1. New schema: fixedBytes → savedBytes, direct → rawBytes
                            if (comp.properties.fixedBytes !== undefined) {
                                comp.properties.savedBytes =
                                    comp.properties.fixedBytes || null
                                comp.properties.rawBytes = null
                                delete comp.properties.fixedBytes
                            }
                            if (
                                comp.properties.direct !== undefined &&
                                comp.properties.direct !== null
                            ) {
                                comp.properties.rawBytes =
                                    comp.properties.direct
                                comp.properties.rawBytesText =
                                    typeof comp.properties.direct === 'string'
                                        ? comp.properties.direct
                                        : JSON.stringify(comp.properties.direct)
                                comp.properties.savedBytes = null
                                delete comp.properties.direct
                            }
                            // free is re-derived at save time
                            delete comp.properties.free

                            // 2. Legacy: Handle old unified "bytes" field
                            if (comp.properties.bytes !== undefined) {
                                const b = comp.properties.bytes
                                const isRecorded =
                                    Array.isArray(b) && Array.isArray(b[0])

                                if (isRecorded) {
                                    comp.properties.savedBytes = b
                                    comp.properties.rawBytes = null
                                } else if (b !== null) {
                                    comp.properties.rawBytes = b
                                    comp.properties.rawBytesText =
                                        typeof b === 'string'
                                            ? b
                                            : JSON.stringify(b)
                                    comp.properties.savedBytes = null
                                }
                                delete comp.properties.bytes
                            }

                            // 3. Remove all per-component settingbytes fields
                            // (settingbytes now lives at top level of play object)
                            delete comp.properties.settingbytes
                            delete comp.properties.settingBytes
                            delete comp.properties.enablebytes
                            delete comp.properties.enableBytes
                        }
                        return comp
                    }
                )

                // By default, make sure a Board is included so it's not hidden
                if (
                    !initialComponents.some((c) =>
                        CATEGORIES.board.includes(c.type)
                    )
                ) {
                    initialComponents.push({
                        id: `board-${Date.now()}_${Math.floor(Math.random() * 1000)}`,
                        type: 'board',
                        name: boardLabel, // Dynamically set to 'TOKI Board' or 'POKI Board'
                        position: { x: 0.5, y: 0.5, anchor: 'center' },
                        properties: {
                            image: boardImage, // Dynamically set to 'toki.png' or 'poki.png'
                        },
                    })
                }

                // Migrate model components: move all properties to top level,
                // remove animationName (now per-step), remove the properties wrapper.
                let legacyModelAnimationName = null
                initialComponents = initialComponents.map((comp) => {
                    if (comp.type !== 'model' || !comp.properties) return comp
                    const { animationName, interactions, ...propsToLift } =
                        comp.properties
                    if (animationName) legacyModelAnimationName = animationName
                    const { properties: _p, ...rest } = comp
                    return { ...rest, ...propsToLift }
                })

                // Map onSuccessActions: fixedBytes → savedBytes, direct → rawBytes/rawBytesText/useRawBytes
                // Also migrate step.live from legacy array format to dict format.
                const mappedSteps = (playData.steps || []).map((step) => {
                    const onSuccessActions = (step.onSuccessActions || []).map(
                        (action) => {
                            const mapped = { ...action }
                            if (mapped.fixedBytes !== undefined) {
                                mapped.savedBytes = mapped.fixedBytes || null
                                mapped.useRawBytes = false
                                delete mapped.fixedBytes
                            }
                            if (
                                mapped.direct !== undefined &&
                                mapped.direct !== null
                            ) {
                                mapped.rawBytes = mapped.direct
                                mapped.rawBytesText =
                                    typeof mapped.direct === 'string'
                                        ? mapped.direct
                                        : JSON.stringify(mapped.direct)
                                mapped.useRawBytes = true
                                mapped.savedBytes = null
                                delete mapped.direct
                            }
                            delete mapped.free
                            return mapped
                        }
                    )

                    // Migrate live: old array [{inputId, ...}] → new dict {inputId: {...}}
                    let live = step.live
                    if (Array.isArray(live)) {
                        live = live.reduce((dict, entry) => {
                            const { inputId, ...rest } = entry
                            if (inputId) dict[inputId] = rest
                            return dict
                        }, {})
                    }

                    return { ...step, onSuccessActions, live }
                })

                // If the old JSON had a per-component animationName, migrate it
                // to every step's models.animationName (as the default for all steps).
                if (legacyModelAnimationName) {
                    mappedSteps.forEach((step) => {
                        if (!step.models?.animationName) {
                            step.models = {
                                ...(step.models || {}),
                                animationName: legacyModelAnimationName,
                            }
                        }
                    })
                }

                // Run our new sync function on initial load to purge any ghosts
                const synced = syncComponentsWithConnections(
                    initialComponents,
                    mappedSteps,
                    freshConnections
                )

                setFormData({
                    title: playData.title || '',
                    description: playData.description || '',
                    projectId: playData.projectId || subId,
                    layoutMode: playData.layoutMode || 'normalized_absolute',
                    totalSteps: playData.totalSteps || 0,
                    settingbytes: harvestedSettingbytes, // TOP-LEVEL: single 1D array for the whole screen
                    meta: playData.meta || {},
                    components: synced.components, // Use the synced components
                    steps: synced.steps, // Use the synced (cleaned) steps
                    finalStepTitle:
                        playData.finalStepTitle || 'Tutorial Complete!',
                    finalStepInstruction:
                        playData.finalStepInstruction ||
                        'Great job! Click Continue to proceed.',
                })

                setLoading(false)
            } catch (error) {
                console.error('Error fetching admin data:', error)
                setLoading(false)
            }
        }
        fetchProject()
    }, [subId])

    // 2. IMAGE UPLOAD HANDLER
    const handleImageUpload = async (e, callback) => {
        const file = e.target.files[0]
        if (!file) return

        // PNG Validation
        if (
            file.type !== 'image/png' &&
            !file.name.toLowerCase().endsWith('.png')
        ) {
            alert('Please select a valid .png image.')
            e.target.value = ''
            return
        }

        // 5MB Limit
        if (file.size > 5 * 1024 * 1024) {
            alert('Image is too large! Maximum allowed size is 5MB.')
            e.target.value = ''
            return
        }

        const formDataUpload = new FormData()
        formDataUpload.append('image', file) // Make sure your backend expects 'image' field

        try {
            // Adjust this route to match your actual image upload endpoint
            const res = await fetch(`${BACKEND_URL}/api/upload-image`, {
                method: 'POST',
                body: formDataUpload,
            })

            if (!res.ok) throw new Error('Upload failed')
            const data = await res.json()

            // Call the updater function with the returned filename or path
            callback(data.filename || data.filePath || data.url || file.name)
        } catch (err) {
            console.error('Upload error:', err)
            alert(
                'Failed to upload image. Make sure your backend route /api/upload-image exists.'
            )
        }
        e.target.value = '' // Reset input
    }

    const fetchSubprogramTitle = async () => {
        const BACKEND_URL = process.env.REACT_APP_API_BASE || ''

        if (!subId) return ''

        const res = await fetch(
            `${BACKEND_URL}/api/program/staging/${(sessionStorage.getItem('allowedDevice') || 'Toki').toLowerCase()}`,
            {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            }
        )

        const data = await res.json()
        if (!res.ok) {
            throw new Error(data.error || 'Failed to get program data')
        }

        const matchedSubprogram = (data.subprograms || []).find(
            (s) => s.id === subId
        )

        return matchedSubprogram?.title || ''
    }

    // 3. GENERAL SETTINGS HANDLERS
    const handleGeneralChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        // Do nothing for now. Later it will be used for the tutorial (for users on top side)
    }

    const handleTotalStepsChange = (e) => {
        const newTotal = parseInt(e.target.value) || 0
        setFormData((prev) => {
            const newSteps = [...prev.steps]
            if (newTotal > newSteps.length) {
                for (let i = newSteps.length + 1; i <= newTotal; i++) {
                    newSteps.push({
                        stepNumber: i,
                        title: '',
                        instruction: '',
                        // New steps are free by default — admin adds conditions to change
                        isFreeStep: true,
                        isLiveStep: false,
                        live: [],
                        visibleComponents: prev.components.map((c) => c.id),
                        enableComponents: prev.components.map((c) => c.id),
                        highlight: [],
                        conditionBlocks: [[]],
                        onSuccessActions: [],
                    })
                }
            } else if (newTotal < newSteps.length) {
                newSteps.length = newTotal
            }
            return { ...prev, totalSteps: newTotal, steps: newSteps }
        })
        setActiveStepIndex((prev) => Math.min(prev, Math.max(0, newTotal - 1)))
    }

    // 4. COMPONENT HANDLERS
    const addComponent = (type) => {
        const canonicalType = type === 'toki_board' ? 'board' : type
        const newId = `${canonicalType}-${Date.now()}_${Math.floor(Math.random() * 1000)}`

        // Define default properties to prevent undefined errors
        const defaultProps = {
            board: {
                image: boardImage,
            },
            // model components: modelUrl lives at top level, no properties object
            // --- NEW: Added showWire default ---
            output: { defaultImage: '', showWire: false },
            // Simple display-only image. scale=1 → base size (20vw), aspect ratio kept.
            image: { imageUrl: '', scale: 1 },
            button_control: {
                className: 'led-btn',
                onImage: 'gesture@2x_active.png',
                offImage: 'gesture@2x_inactive.png',
                savedBytes: null,
                label: '',
                events: { onON: [], onOFF: [] },
                modelActions: { onON: [], onOFF: [] },
            },
            tack_button_control: {
                className: 'led-btn',
                onImage: 'gesture@2x_active.png',
                offImage: 'gesture@2x_inactive.png',
                savedBytes: null,
                label: '',
                liveBytesEnabled: false,
                liveBytes: '',
                events: { onON: [], onOFF: [] },
                modelActions: { onON: [], onOFF: [] },
            },
            hold_button_control: {
                className: 'led-btn',
                onImage: 'gesture@2x_active.png',
                offImage: 'gesture@2x_inactive.png',
                savedBytes: null,
                label: '',
                liveBytesEnabled: false,
                liveBytes: '',
                events: { onON: [], onOFF: [] },
                modelActions: { onON: [], onOFF: [] },
            },
            trigger_button_control: {
                className: 'led-btn',
                onImage: 'gesture@2x_active.png',
                offImage: 'gesture@2x_inactive.png',
                savedBytes: null,
                label: '',
                events: { onTrigger: [] },
                modelActions: { onTrigger: [] },
            },
            slider_control: {
                rangImgName: 'frequency_slider',
                title: 'Intensity',
                label: '',
                min: 0,
                max: 100,
                conditions: [],
                modelActions: [],
            },
            custom_slider_control: {
                rangImgName: 'frequency_slider',
                title: 'Intensity',
            },
            rgb_control: {
                label: '',
                conditions: [],
                modelActions: [],
            },
        }

        // canonicalType already declared above defaultProps
        const newComp = {
            id: newId, // Hidden from user
            type: canonicalType,
            name:
                canonicalType === 'output' ||
                canonicalType === 'slider_control' ||
                canonicalType === 'button_control' ||
                canonicalType === 'tack_button_control' ||
                canonicalType === 'hold_button_control' ||
                canonicalType === 'image'
                    ? ''
                    : canonicalType === 'board'
                      ? boardLabel
                      : `New ${canonicalType.replace('_', ' ')}`,

            position: { x: 0.5, y: 0.5, anchor: 'center' },
            mapping: canonicalType.includes('control')
                ? `map_${Date.now()}`
                : undefined,
            ...(canonicalType === 'model'
                ? { modelUrl: '' }
                : { properties: defaultProps[canonicalType] || {} }),
        }

        if (!newComp.mapping) delete newComp.mapping

        // Auto-expand the newly created component so the user sees the form
        setExpandedComps((prev) => ({ ...prev, [newId]: true }))

        setFormData((prev) => {
            // CHANGE: Add the new component's ID to the visible list of ALL existing steps
            const updatedSteps = prev.steps.map((step) => ({
                ...step,
                visibleComponents: [...(step.visibleComponents || []), newId],
                enableComponents: [...(step.enableComponents || []), newId],
            }))

            return {
                ...prev,
                components: [...prev.components, newComp],
                steps: updatedSteps, // Save the modified steps
            }
        })
    }

    // Generic toggle to hide/show component in the project
    const toggleComponent = (
        type,
        overrides = {},
        isIncluded,
        existingId = null
    ) => {
        if (isIncluded && existingId) {
            if (
                window.confirm(
                    `Are you sure you want to hide this ${COMPONENT_LABELS[type] || type}? Its internal configuration will be lost.`
                )
            ) {
                setFormData((prev) => {
                    const newComps = prev.components.filter(
                        (c) => c.id !== existingId
                    )
                    const newSteps = prev.steps.map((step) => ({
                        ...step,
                        visibleComponents:
                            step.visibleComponents?.filter(
                                (id) => id !== existingId
                            ) || [],
                        enableComponents:
                            step.enableComponents?.filter(
                                (id) => id !== existingId
                            ) || [],
                        highlight:
                            step.highlight?.filter((id) => id !== existingId) ||
                            [],
                    }))
                    return { ...prev, components: newComps, steps: newSteps }
                })
            }
        } else if (!isIncluded) {
            const canonicalType = type === 'toki_board' ? 'board' : type
            const newId = `${canonicalType}-${Date.now()}_${Math.floor(Math.random() * 1000)}`
            const defaultProps = {
                board: {
                    image: 'toki.png',
                },
                // model components: modelUrl lives at top level, no properties object
                output: { defaultImage: '', showWire: true },
                button_control: {
                    className: 'led-btn',
                    onImage: 'gesture@2x_active.png',
                    offImage: 'gesture@2x_inactive.png',
                    savedBytes: null,
                    label: '',
                    events: { onON: [], onOFF: [] },
                    modelActions: { onON: [], onOFF: [] },
                },
                tack_button_control: {
                    className: 'led-btn',
                    onImage: 'gesture@2x_active.png',
                    offImage: 'gesture@2x_inactive.png',
                    savedBytes: null,
                    label: '',
                    liveBytesEnabled: false,
                    liveBytes: '',
                    events: { onON: [], onOFF: [] },
                    modelActions: { onON: [], onOFF: [] },
                },
                hold_button_control: {
                    className: 'led-btn',
                    onImage: 'gesture@2x_active.png',
                    offImage: 'gesture@2x_inactive.png',
                    savedBytes: null,
                    label: '',
                    liveBytesEnabled: false,
                    liveBytes: '',
                    events: { onON: [], onOFF: [] },
                    modelActions: { onON: [], onOFF: [] },
                },
                slider_control: {
                    rangImgName: 'frequency_slider',
                    title: 'Intensity',
                    label: '',
                    conditions: [],
                    modelActions: [],
                },
                custom_slider_control: {
                    rangImgName: 'frequency_slider',
                    title: 'Intensity',
                },
                rgb_control: {
                    label: '',
                    conditions: [],
                    modelActions: [],
                },
            }

            const newComp = {
                id: newId,
                type: canonicalType,
                name:
                    overrides.name ||
                    (canonicalType.includes('control') ||
                    canonicalType === 'output'
                        ? ''
                        : canonicalType === 'board'
                          ? boardLabel
                          : `New ${type.replace('_', ' ')}`),
                position: { x: 0.5, y: 0.5, anchor: 'center' },
                mapping: canonicalType.includes('control')
                    ? `map_${Date.now()}`
                    : undefined,
                ...(canonicalType === 'model'
                    ? { modelUrl: overrides.modelUrl || '' }
                    : {
                          properties: {
                              ...(defaultProps[canonicalType] ||
                                  defaultProps[type] ||
                                  {}),
                              ...(overrides.properties || {}),
                          },
                      }),
                port: overrides.port || undefined,
            }

            if (!newComp.mapping) delete newComp.mapping

            if (canonicalType === 'output') {
                const baseName = (overrides.name || '')
                    .split(' (')[0]
                    .toLowerCase()
                const defaultImg =
                    DEFAULT_OUTPUT_IMAGES[baseName] ||
                    DEFAULT_OUTPUT_IMAGES['default']
                newComp.properties.defaultImage = defaultImg
            }

            setExpandedComps((prev) => ({ ...prev, [newId]: true }))

            setFormData((prev) => {
                const updatedSteps = prev.steps.map((step) => ({
                    ...step,
                    visibleComponents: [
                        ...(step.visibleComponents || []),
                        newId,
                    ],
                    enableComponents:
                        step.enableComponents?.filter(
                            (id) => id !== existingId
                        ) || [],
                }))
                return {
                    ...prev,
                    components: [...prev.components, newComp],
                    steps: updatedSteps,
                }
            })
        }
    }

    const removeComponent = (idToRemove) => {
        if (!window.confirm('Are you sure you want to remove this component?'))
            return

        setFormData((prev) => {
            const newComps = prev.components.filter((c) => c.id !== idToRemove)

            // Clean up steps to prevent crashes
            const newSteps = prev.steps.map((step) => ({
                ...step,
                visibleComponents:
                    step.visibleComponents?.filter((id) => id !== idToRemove) ||
                    [],
                enableComponents:
                    step.enableComponents?.filter((id) => id !== idToRemove) ||
                    [],
                highlight:
                    step.highlight?.filter((id) => id !== idToRemove) || [],
            }))

            return { ...prev, components: newComps, steps: newSteps }
        })
    }

    const updateComponent = (index, field, value) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            newComps[index] = { ...newComps[index], [field]: value }
            return { ...prev, components: newComps }
        })
    }

    const updateComponentPosition = (index, axis, value) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            newComps[index].position = {
                ...newComps[index].position,
                [axis]: parseFloat(value) || 0,
            }
            return { ...prev, components: newComps }
        })
    }

    const updateComponentProperty = (index, propKey, value) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            newComps[index].properties = {
                ...newComps[index].properties,
                [propKey]: value,
            }
            return { ...prev, components: newComps }
        })
    }

    // --- Nested Arrays Helpers (States, Interactions) ---
    const handleArrayItemAdd = (compIndex, arrayName, newItem) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const targetArray = newComps[compIndex].properties[arrayName] || []
            newComps[compIndex].properties[arrayName] = [
                ...targetArray,
                newItem,
            ]
            return { ...prev, components: newComps }
        })
    }

    const handleArrayItemRemove = (compIndex, arrayName, itemIndex) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            newComps[compIndex].properties[arrayName] = newComps[
                compIndex
            ].properties[arrayName].filter((_, i) => i !== itemIndex)
            return { ...prev, components: newComps }
        })
    }

    const handleArrayItemUpdate = (
        compIndex,
        arrayName,
        itemIndex,
        field,
        value
    ) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            newComps[compIndex].properties[arrayName][itemIndex] = {
                ...newComps[compIndex].properties[arrayName][itemIndex],
                [field]: value,
            }
            return { ...prev, components: newComps }
        })
    }

    // --- State Conditions Helpers ---
    const addConditionBlock = (compIndex, stateIndex) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const state = newComps[compIndex].properties.states[stateIndex]
            state.conditionBlocks = [
                ...(state.conditionBlocks || []),
                [{ inputId: '', inputType: '', expectedStatus: 'ON' }],
            ]
            return { ...prev, components: newComps }
        })
    }

    const removeConditionBlock = (compIndex, stateIndex, blockIndex) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const state = newComps[compIndex].properties.states[stateIndex]
            state.conditionBlocks = state.conditionBlocks.filter(
                (_, i) => i !== blockIndex
            )
            return { ...prev, components: newComps }
        })
    }

    const addStateCondition = (compIndex, stateIndex, blockIndex) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const state = newComps[compIndex].properties.states[stateIndex]
            const blocks = state.conditionBlocks || [[]]
            const updatedBlock = [
                ...blocks[blockIndex],
                { inputId: '', inputType: '', expectedStatus: 'ON' },
            ]
            state.conditionBlocks = blocks.map((b, i) =>
                i === blockIndex ? updatedBlock : b
            )
            return { ...prev, components: newComps }
        })
    }

    // Scrub a condition object so it only contains the keys relevant to its inputType.
    const scrubCondition = (cond) => {
        const type = cond.inputType || ''
        if (BUTTON_INPUT_TYPES.includes(type)) {
            return {
                inputId: cond.inputId,
                inputType: type,
                expectedStatus: cond.expectedStatus || 'ON',
            }
        }
        if (SLIDER_INPUT_TYPES.includes(type)) {
            return {
                inputId: cond.inputId,
                inputType: type,
                operator: cond.operator || '>',
                value: cond.value ?? 0,
            }
        }
        if (RGB_SLIDER_INPUT_TYPES.includes(type)) {
            return {
                inputId: cond.inputId,
                inputType: type,
                channel: cond.channel || 'red',
                operator: cond.operator || '>',
                value: cond.value ?? 0,
            }
        }
        // Unknown / empty inputType — preserve as-is
        return cond
    }

    const updateStateCondition = (
        compIndex,
        stateIndex,
        blockIndex,
        condIndex,
        field,
        value
    ) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const state = newComps[compIndex].properties.states[stateIndex]
            const blocks = state.conditionBlocks || [[]]
            const updatedBlock = blocks[blockIndex].map((cond, i) => {
                if (i !== condIndex) return cond
                // When changing the input component, rebuild the condition from
                // scratch using the new type so no ghost keys are ever stored.
                if (field === 'inputId') {
                    const allComps = prev.components
                    const sel = allComps
                        .flatMap((c) => [c])
                        .find((c) => c.id === value)
                    const newType = sel?.type || ''
                    const fresh = scrubCondition({
                        inputId: value,
                        inputType: newType,
                    })
                    return fresh
                }
                const updated = {
                    ...cond,
                    [field]: field === 'value' ? parseFloat(value) || 0 : value,
                }
                return updated
            })
            state.conditionBlocks = blocks.map((b, i) =>
                i === blockIndex ? updatedBlock : b
            )
            return { ...prev, components: newComps }
        })
    }

    const removeStateCondition = (
        compIndex,
        stateIndex,
        blockIndex,
        condIndex
    ) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const state = newComps[compIndex].properties.states[stateIndex]
            const blocks = state.conditionBlocks || [[]]
            const updatedBlock = blocks[blockIndex].filter(
                (_, i) => i !== condIndex
            )
            state.conditionBlocks = blocks.map((b, i) =>
                i === blockIndex ? updatedBlock : b
            )
            return { ...prev, components: newComps }
        })
    }

    // 2B. MODEL UPLOAD HANDLER
    const handleModelUpload = async (e, callback, currentModelUrl) => {
        const file = e.target.files[0]
        if (!file) return

        // 100MB Limit for GLB models
        if (file.size > 100 * 1024 * 1024) {
            alert('Model file is too large! Maximum allowed size is 100MB.')
            e.target.value = ''
            return
        }

        if (!file.name.toLowerCase().endsWith('.glb')) {
            alert('Please select a valid .glb file.')
            e.target.value = ''
            return
        }

        const formDataUpload = new FormData()
        formDataUpload.append('model', file)
        // Send the old model URL so the backend can delete it from GCS
        if (currentModelUrl) {
            formDataUpload.append('oldModelUrl', currentModelUrl)
        }

        setUploading(true)
        try {
            // Use the projectId for folder organization (fallback to subId)
            const projectId = formData?.projectId || subId
            const res = await fetch(
                `${BACKEND_URL}/api/upload-model/${projectId}`,
                {
                    method: 'POST',
                    body: formDataUpload,
                }
            )

            if (!res.ok) {
                const errBody = await res.json().catch(() => ({}))
                throw new Error(errBody.error || 'Model upload failed')
            }
            const data = await res.json()

            // Mark this as a fresh upload so the animation list effect opens
            // the selection modal once names are loaded.
            isNewUploadRef.current = true

            // Save the returned GCS URL
            callback(data.filePath || data.modelUrl || data.filename)

            alert('Model uploaded to cloud storage successfully!')
        } catch (err) {
            console.error('Upload error:', err)
            alert(`Failed to upload model: ${err.message}`)
        } finally {
            setUploading(false)
        }
        e.target.value = '' // Reset input
    }

    // 5. STEPS HANDLERS

    // Returns true if conditionBlocks has at least one non-empty block with at least one condition
    const hasConditions = (conditionBlocks) => {
        if (!Array.isArray(conditionBlocks) || conditionBlocks.length === 0)
            return false
        return conditionBlocks.some(
            (block) => Array.isArray(block) && block.length > 0
        )
    }

    const handleStepChange = (field, value) => {
        if (field === 'isFreeStep') {
            const activeStep = formData?.steps?.[activeStepIndex]
            const currentlyHasConditions = hasConditions(
                activeStep?.conditionBlocks
            ) // NEW GUARD: Prevent checking isFreeStep if conditions exist

            if (value === true && currentlyHasConditions) {
                window.alert(
                    'Please remove all conditions in Success Validations before marking this step as a Free Step.'
                )
                return
            } // EXISTING GUARD: Guard against unchecking isFreeStep when there are no conditions

            if (value === false && !currentlyHasConditions) {
                window.alert(
                    'Add a condition in Success Validations before marking the step as non-free.'
                )
                return
            }
        }

        setFormData((prev) => {
            const newSteps = [...prev.steps]
            newSteps[activeStepIndex] = {
                ...newSteps[activeStepIndex],
                [field]: value,
            }
            return { ...prev, steps: newSteps }
        })
    }

    // ADD THIS function:
    const handleFinalStepChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
    }

    const collectPlayModelUrls = (components) => {
        const urls = []
        const addUrl = (value) => {
            if (typeof value !== 'string') return
            const trimmed = value.trim()
            if (!trimmed || urls.includes(trimmed)) return
            urls.push(trimmed)
        }

        components.forEach((component) => {
            if (!component) return

            if (component.type === 'model') {
                addUrl(component.modelUrl)
                ;(component.interactions || []).forEach((interaction) => {
                    addUrl(interaction?.targetModelUrl)
                })
            }

            const modelActions = component.properties?.modelActions
            if (Array.isArray(modelActions)) {
                modelActions.forEach((action) => {
                    addUrl(action?.targetModelUrl)
                })
            } else if (modelActions && typeof modelActions === 'object') {
                Object.values(modelActions).forEach((actions) => {
                    ;(actions || []).forEach((action) => {
                        addUrl(action?.targetModelUrl)
                    })
                })
            }
        })

        return urls
    }

    const buildPreviewPlayData = (data) => ({
        ...data,
        meta: {
            ...(data?.meta || {}),
            modelPath: collectPlayModelUrls(data?.components || []),
        },
    })

    // ---------------------------------------------------------------------------
    // Live Step handlers
    // ---------------------------------------------------------------------------
    const handleLiveStepToggle = (enabled) => {
        setFormData((prev) => {
            const newSteps = [...prev.steps]
            const step = { ...newSteps[activeStepIndex] }
            step.isLiveStep = enabled
            if (enabled) {
                // Initialise as dict; migrate legacy array on the fly
                if (Array.isArray(step.live)) {
                    step.live = step.live.reduce((dict, entry) => {
                        const { inputId, ...rest } = entry
                        if (inputId) dict[inputId] = rest
                        return dict
                    }, {})
                } else if (!step.live || typeof step.live !== 'object') {
                    step.live = {}
                }
            }
            newSteps[activeStepIndex] = step
            return { ...prev, steps: newSteps }
        })
    }

    const handleLiveInputToggle = (inputComp, isChecked) => {
        if (isChecked) {
            const normalizedName = inputComp.name?.trim() || ''
            if (!normalizedName) {
                window.alert(
                    `Please set the "Name" field on this ${inputComp.type} component before enabling it as a Live Step input.\n\nThe name must match the output component it drives (e.g. "RGB", "geared_motor").`
                )
                return
            }
            if (
                inputComp.type === 'slider_control' &&
                normalizedName.toLowerCase() === 'rgb'
            ) {
                window.alert(
                    'A regular Slider cannot target an RGB output.\nUse an RGB Slider (rgb_control) for that instead.'
                )
                return
            }
        }
        setFormData((prev) => {
            const newSteps = [...prev.steps]
            const step = { ...newSteps[activeStepIndex] }
            // Normalise live to dict (handles legacy array format)
            const currentLive = Array.isArray(step.live)
                ? step.live.reduce((dict, entry) => {
                      const { inputId, ...rest } = entry
                      if (inputId) dict[inputId] = rest
                      return dict
                  }, {})
                : { ...(step.live || {}) }

            if (isChecked) {
                const entry = calcLiveEntry(
                    inputComp,
                    prev.components,
                    boardDevice
                )
                if (!entry) {
                    window.alert(
                        `Could not find an output component named "${inputComp.name}".\n\nMake sure an output component exists whose Name field exactly matches "${inputComp.name}".`
                    )
                    return prev
                }
                // Strip inputId from the entry value; use inputComp.id as the key
                const { inputId: _id, ...entryWithoutId } = entry
                step.live = { ...currentLive, [inputComp.id]: entryWithoutId }
            } else {
                const { [inputComp.id]: _removed, ...rest } = currentLive
                step.live = rest
            }

            newSteps[activeStepIndex] = step
            return { ...prev, steps: newSteps }
        })
    }

    const handleLiveIsFreeToggle = (inputId, isFree) => {
        setFormData((prev) => {
            const newSteps = [...prev.steps]
            const step = { ...newSteps[activeStepIndex] }
            const currentLive = Array.isArray(step.live)
                ? step.live.reduce((dict, entry) => {
                      const { inputId: eid, ...rest } = entry
                      if (eid) dict[eid] = rest
                      return dict
                  }, {})
                : { ...(step.live || {}) }
            if (currentLive[inputId]) {
                currentLive[inputId] = { ...currentLive[inputId], isFree }
            }
            step.live = currentLive
            newSteps[activeStepIndex] = step
            return { ...prev, steps: newSteps }
        })
    }

    const handleLiveFetchCompToggle = (
        inputId,
        fetchCompId,
        isChecked,
        isRgb
    ) => {
        setFormData((prev) => {
            const newSteps = [...prev.steps]
            const step = { ...newSteps[activeStepIndex] }
            const currentLive = Array.isArray(step.live)
                ? step.live.reduce((dict, entry) => {
                      const { inputId: eid, ...rest } = entry
                      if (eid) dict[eid] = rest
                      return dict
                  }, {})
                : { ...(step.live || {}) }
            if (currentLive[inputId]) {
                const entry = { ...currentLive[inputId] }
                const compsIds = { ...(entry.compsIds || {}) }
                if (isChecked) {
                    compsIds[fetchCompId] = isRgb ? [5, 6, 7] : [4]
                } else {
                    delete compsIds[fetchCompId]
                }
                currentLive[inputId] = { ...entry, compsIds }
            }
            step.live = currentLive
            newSteps[activeStepIndex] = step
            return { ...prev, steps: newSteps }
        })
    }

    const handleLiveFetchIndexChange = (
        inputId,
        fetchCompId,
        arrayIndex,
        newByteIndex
    ) => {
        setFormData((prev) => {
            const newSteps = [...prev.steps]
            const step = { ...newSteps[activeStepIndex] }
            const currentLive = Array.isArray(step.live)
                ? step.live.reduce((dict, entry) => {
                      const { inputId: eid, ...rest } = entry
                      if (eid) dict[eid] = rest
                      return dict
                  }, {})
                : { ...(step.live || {}) }
            if (currentLive[inputId]) {
                const entry = { ...currentLive[inputId] }
                const compsIds = { ...(entry.compsIds || {}) }
                if (compsIds[fetchCompId]) {
                    const indices = [...compsIds[fetchCompId]]
                    indices[arrayIndex] = newByteIndex
                    compsIds[fetchCompId] = indices
                }
                currentLive[inputId] = { ...entry, compsIds }
            }
            step.live = currentLive
            newSteps[activeStepIndex] = step
            return { ...prev, steps: newSteps }
        })
    }

    const handleLiveFixedBytesChange = (inputId, newFixedBytes) => {
        setFormData((prev) => {
            const newSteps = [...prev.steps]
            const step = { ...newSteps[activeStepIndex] }
            const currentLive = Array.isArray(step.live)
                ? step.live.reduce((dict, entry) => {
                      const { inputId: eid, ...rest } = entry
                      if (eid) dict[eid] = rest
                      return dict
                  }, {})
                : { ...(step.live || {}) }
            if (currentLive[inputId]) {
                currentLive[inputId] = {
                    ...currentLive[inputId],
                    fixedBytes: newFixedBytes,
                }
            }
            step.live = currentLive
            newSteps[activeStepIndex] = step
            return { ...prev, steps: newSteps }
        })
    }

    // Extra targets let one live input drive several hardware ports at once, each
    // at its OWN intensity (e.g. one push button → dc_motor@M1 and dc_motor@M2 at
    // different speeds). Targets are keyed by port (conn.port), NOT by component
    // name, so identical component types on different ports stay independent.
    // char / portNum are resolved here the same way calcLiveEntry bakes the
    // primary port, so PlayScreen can send each packet without any name lookup.
    // Helper: mutate the current step's live dict for a given input entry.
    const updateLiveEntry = (inputId, mutate) => {
        setFormData((prev) => {
            const newSteps = [...prev.steps]
            const step = { ...newSteps[activeStepIndex] }
            const currentLive = Array.isArray(step.live)
                ? step.live.reduce((dict, entry) => {
                      const { inputId: eid, ...rest } = entry
                      if (eid) dict[eid] = rest
                      return dict
                  }, {})
                : { ...(step.live || {}) }
            if (currentLive[inputId]) {
                const entry = { ...currentLive[inputId] }
                mutate(entry)
                currentLive[inputId] = entry
            }
            step.live = currentLive
            newSteps[activeStepIndex] = step
            return { ...prev, steps: newSteps }
        })
    }

    const handleLiveExtraTargetToggle = (inputId, conn, isChecked) => {
        updateLiveEntry(inputId, (entry) => {
            const targets = [...(entry.extraTargets || [])]
            if (isChecked) {
                const normalized =
                    conn.component?.trim().toLowerCase().replace(/ /g, '_') || ''
                const char = compName_character[normalized] ?? 80
                const portNum =
                    livePortsNumber?.[boardDevice]?.[conn.port] ??
                    livePortsNumber?.Ace?.[conn.port] ??
                    0
                const range = COMPONENT_RANGES[normalized] || { min: 0, max: 100 }
                if (!targets.some((t) => t.port === conn.port)) {
                    targets.push({
                        component: conn.component,
                        port: conn.port,
                        portNum,
                        char,
                        value: range.max,
                    })
                }
            } else {
                const idx = targets.findIndex((t) => t.port === conn.port)
                if (idx !== -1) targets.splice(idx, 1)
            }
            entry.extraTargets = targets
        })
    }

    const handleLiveExtraTargetValue = (inputId, connPort, value) => {
        updateLiveEntry(inputId, (entry) => {
            const targets = (entry.extraTargets || []).map((t) =>
                t.port === connPort ? { ...t, value } : t
            )
            entry.extraTargets = targets
        })
    }

    const handleGenerateVoiceover = async () => {
        // Remind them to save first, because the backend reads from the JSON file
        // Single confirmation gate: save reminder + estimated credit cost.
        if (!confirmVoiceoverGeneration(formData)) return

        // Fallback to 'play1' if dealing with older legacy routes
        const currentScreenId = screenId || 'play1'

        setIsGeneratingTTS(true)
        try {
            // --- READ FROM LOCAL STORAGE ---
            const apiKey = localStorage.getItem('freepikApiKey')
            const voiceId = localStorage.getItem('elevenVoiceId')
            if (!apiKey || !voiceId) {
                window.alert(
                    'Missing TTS Configuration!\n\nPlease go back to the main Toki Engine Admin page and set the ElevenLabs API Key and Voice ID before generating voiceovers.'
                )
                setIsGeneratingTTS(false)
                return
            }

            const res = await fetch(
                `${BACKEND_URL}/api/project/${subId}/generate-tts/play/${currentScreenId}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }, // IMPORTANT: Add this header
                    body: JSON.stringify({ apiKey, voiceId }), // IMPORTANT: Send the keys
                }
            )
            const data = await res.json()

            if (!res.ok) {
                throw new Error(data.error || 'Failed to generate voiceover')
            }

            alert(`✅ ${data.message}`)
        } catch (error) {
            console.error('TTS Generation error:', error)
            alert(`❌ Error generating voiceover: ${error.message}`)
        } finally {
            setIsGeneratingTTS(false)
        }
    }

    const toggleStepComponentArray = (arrayName, compId, isChecked) => {
        setFormData((prev) => {
            const newSteps = [...prev.steps]
            const step = { ...newSteps[activeStepIndex] }

            const visibleSet = new Set(step.visibleComponents || [])
            const enableSet = new Set(step.enableComponents || [])
            const highlightSet = new Set(step.highlight || [])

            if (arrayName === 'visibleComponents') {
                if (isChecked) {
                    visibleSet.add(compId)
                } else {
                    // Unchecking visible → auto-remove from enabled and highlight too
                    visibleSet.delete(compId)
                    enableSet.delete(compId)
                    highlightSet.delete(compId)
                }
            } else if (arrayName === 'enableComponents') {
                if (isChecked) {
                    enableSet.add(compId)
                    // Auto-ensure it's also visible
                    visibleSet.add(compId)
                } else {
                    enableSet.delete(compId)
                }
            } else if (arrayName === 'highlight') {
                if (isChecked) {
                    highlightSet.add(compId)
                    // Auto-ensure it's also visible
                    visibleSet.add(compId)
                } else {
                    highlightSet.delete(compId)
                }
            }

            step.visibleComponents = Array.from(visibleSet)
            step.enableComponents = Array.from(enableSet)
            step.highlight = Array.from(highlightSet)
            newSteps[activeStepIndex] = step
            return { ...prev, steps: newSteps }
        })
    }

    const addStepConditionBlock = () => {
        setFormData((prev) => {
            const newSteps = [...prev.steps]
            const step = { ...newSteps[activeStepIndex] }
            step.conditionBlocks = [
                ...(step.conditionBlocks || []),
                [{ inputId: '', inputType: '', expectedStatus: 'ON' }],
            ]
            // Having a block means it's no longer a free step
            step.isFreeStep = false
            newSteps[activeStepIndex] = step
            return { ...prev, steps: newSteps }
        })
    }

    const removeStepConditionBlock = (blockIndex) => {
        setFormData((prev) => {
            const newSteps = [...prev.steps]
            const step = { ...newSteps[activeStepIndex] }
            const remaining = (step.conditionBlocks || []).filter(
                (_, i) => i !== blockIndex
            )
            step.conditionBlocks = remaining
            // If no non-empty blocks remain, auto-mark as free step
            if (!hasConditions(remaining)) step.isFreeStep = true
            newSteps[activeStepIndex] = step
            return { ...prev, steps: newSteps }
        })
    }

    const addStepValidation = (blockIndex) => {
        setFormData((prev) => {
            const newSteps = [...prev.steps]
            const step = { ...newSteps[activeStepIndex] }
            const blocks = step.conditionBlocks || [[]]
            const updatedBlock = [
                ...blocks[blockIndex],
                { inputId: '', inputType: '', expectedStatus: 'ON' },
            ]
            step.conditionBlocks = blocks.map((b, i) =>
                i === blockIndex ? updatedBlock : b
            )
            // Adding a condition → no longer a free step
            step.isFreeStep = false
            newSteps[activeStepIndex] = step
            return { ...prev, steps: newSteps }
        })
    }

    const updateStepValidation = (blockIndex, condIndex, field, value) => {
        setFormData((prev) => {
            const newSteps = [...prev.steps]
            const step = newSteps[activeStepIndex]
            const blocks = step.conditionBlocks || [[]]
            const updatedBlock = blocks[blockIndex].map((cond, i) => {
                if (i !== condIndex) return cond
                if (field === 'inputId') {
                    const sel = prev.components.find((c) => c.id === value)
                    const newType = sel?.type || ''
                    return scrubCondition({
                        inputId: value,
                        inputType: newType,
                    })
                }
                return {
                    ...cond,
                    [field]: field === 'value' ? parseFloat(value) || 0 : value,
                }
            })
            step.conditionBlocks = blocks.map((b, i) =>
                i === blockIndex ? updatedBlock : b
            )
            return { ...prev, steps: newSteps }
        })
    }

    const removeStepValidation = (blockIndex, condIndex) => {
        setFormData((prev) => {
            const newSteps = [...prev.steps]
            const step = { ...newSteps[activeStepIndex] }
            const blocks = step.conditionBlocks || [[]]
            const updatedBlock = blocks[blockIndex].filter(
                (_, i) => i !== condIndex
            )
            const newBlocks = blocks.map((b, i) =>
                i === blockIndex ? updatedBlock : b
            )
            step.conditionBlocks = newBlocks
            // If all remaining blocks are empty, auto-mark as free step
            if (!hasConditions(newBlocks)) step.isFreeStep = true
            newSteps[activeStepIndex] = step
            return { ...prev, steps: newSteps }
        })
    }

    // 6. SAVE
    // --- Button Event Handlers ---
    const addButtonAction = (compIndex, eventKey) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const comp = { ...newComps[compIndex] }
            const events = {
                ...(comp.properties.events || { onON: [], onOFF: [] }),
            }
            events[eventKey] = [
                ...(events[eventKey] || []),
                { outputId: '', targetImage: '' },
            ]
            comp.properties = { ...comp.properties, events }
            newComps[compIndex] = comp
            return { ...prev, components: newComps }
        })
    }

    const updateButtonAction = (
        compIndex,
        eventKey,
        actionIndex,
        field,
        value
    ) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const comp = { ...newComps[compIndex] }
            const events = { ...(comp.properties.events || {}) }
            const actions = [...(events[eventKey] || [])]
            actions[actionIndex] = { ...actions[actionIndex], [field]: value }
            events[eventKey] = actions
            comp.properties = { ...comp.properties, events }
            newComps[compIndex] = comp
            return { ...prev, components: newComps }
        })
    }

    const removeButtonAction = (compIndex, eventKey, actionIndex) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const comp = { ...newComps[compIndex] }
            const events = { ...(comp.properties.events || {}) }
            events[eventKey] = (events[eventKey] || []).filter(
                (_, i) => i !== actionIndex
            )
            comp.properties = { ...comp.properties, events }
            newComps[compIndex] = comp
            return { ...prev, components: newComps }
        })
    }

    // --- Slider Condition Handlers ---
    const addSliderCondition = (compIndex) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const comp = { ...newComps[compIndex] }
            const newCond = {
                type: 'simple',
                operator: '>',
                value: 0,
                actions: [],
            }
            comp.properties = {
                ...comp.properties,
                conditions: [...(comp.properties.conditions || []), newCond],
            }
            newComps[compIndex] = comp
            return { ...prev, components: newComps }
        })
    }

    const updateSliderCondition = (compIndex, condIndex, field, value) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const comp = { ...newComps[compIndex] }
            const conditions = [...(comp.properties.conditions || [])]
            conditions[condIndex] = { ...conditions[condIndex], [field]: value }
            comp.properties = { ...comp.properties, conditions }
            newComps[compIndex] = comp
            return { ...prev, components: newComps }
        })
    }

    const removeSliderCondition = (compIndex, condIndex) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const comp = { ...newComps[compIndex] }
            const conditions = (comp.properties.conditions || []).filter(
                (_, i) => i !== condIndex
            )
            comp.properties = { ...comp.properties, conditions }
            newComps[compIndex] = comp
            return { ...prev, components: newComps }
        })
    }

    const addConditionAction = (compIndex, condIndex) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const comp = { ...newComps[compIndex] }
            const conditions = [...(comp.properties.conditions || [])]
            const cond = { ...conditions[condIndex] }
            cond.actions = [
                ...(cond.actions || []),
                { outputId: '', targetImage: '' },
            ]
            conditions[condIndex] = cond
            comp.properties = { ...comp.properties, conditions }
            newComps[compIndex] = comp
            return { ...prev, components: newComps }
        })
    }

    const updateConditionAction = (
        compIndex,
        condIndex,
        actionIndex,
        field,
        value
    ) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const comp = { ...newComps[compIndex] }
            const conditions = [...(comp.properties.conditions || [])]
            const cond = { ...conditions[condIndex] }
            const actions = [...(cond.actions || [])]
            actions[actionIndex] = { ...actions[actionIndex], [field]: value }
            cond.actions = actions
            conditions[condIndex] = cond
            comp.properties = { ...comp.properties, conditions }
            newComps[compIndex] = comp
            return { ...prev, components: newComps }
        })
    }

    const removeConditionAction = (compIndex, condIndex, actionIndex) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const comp = { ...newComps[compIndex] }
            const conditions = [...(comp.properties.conditions || [])]
            const cond = { ...conditions[condIndex] }
            cond.actions = (cond.actions || []).filter(
                (_, i) => i !== actionIndex
            )
            conditions[condIndex] = cond
            comp.properties = { ...comp.properties, conditions }
            newComps[compIndex] = comp
            return { ...prev, components: newComps }
        })
    }

    // --- Step Action Handlers ---
    const addStepAction = (stepIndex) => {
        setFormData((prev) => {
            const newSteps = [...prev.steps]
            const step = { ...newSteps[stepIndex] }
            step.onSuccessActions = [
                ...(step.onSuccessActions || []),
                { outputId: '', targetImage: '' },
            ]
            newSteps[stepIndex] = step
            return { ...prev, steps: newSteps }
        })
    }

    const updateStepAction = (stepIndex, actionIndex, field, value) => {
        setFormData((prev) => {
            const newSteps = [...prev.steps]
            const step = { ...newSteps[stepIndex] }
            const actions = [...(step.onSuccessActions || [])]
            actions[actionIndex] = { ...actions[actionIndex], [field]: value }
            step.onSuccessActions = actions
            newSteps[stepIndex] = step
            return { ...prev, steps: newSteps }
        })
    }

    const removeStepAction = (stepIndex, actionIndex) => {
        setFormData((prev) => {
            const newSteps = [...prev.steps]
            const step = { ...newSteps[stepIndex] }
            step.onSuccessActions = (step.onSuccessActions || []).filter(
                (_, i) => i !== actionIndex
            )
            newSteps[stepIndex] = step
            return { ...prev, steps: newSteps }
        })
    }

    // --- Button Model Action Handlers ---
    const addButtonModelAction = (compIndex, eventKey) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const comp = { ...newComps[compIndex] }
            const modelActions = { ...(comp.properties.modelActions || {}) }
            modelActions[eventKey] = [
                ...(modelActions[eventKey] || []),
                { targetModelId: '', action: 'play' },
            ]
            comp.properties = { ...comp.properties, modelActions }
            newComps[compIndex] = comp
            return { ...prev, components: newComps }
        })
    }

    const updateButtonModelAction = (
        compIndex,
        eventKey,
        actionIndex,
        field,
        value
    ) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const comp = { ...newComps[compIndex] }
            const modelActions = { ...(comp.properties.modelActions || {}) }
            const actions = [...(modelActions[eventKey] || [])]
            actions[actionIndex] = { ...actions[actionIndex], [field]: value }
            modelActions[eventKey] = actions
            comp.properties = { ...comp.properties, modelActions }
            newComps[compIndex] = comp
            return { ...prev, components: newComps }
        })
    }

    const removeButtonModelAction = (compIndex, eventKey, actionIndex) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const comp = { ...newComps[compIndex] }
            const modelActions = { ...(comp.properties.modelActions || {}) }
            modelActions[eventKey] = (modelActions[eventKey] || []).filter(
                (_, i) => i !== actionIndex
            )
            comp.properties = { ...comp.properties, modelActions }
            newComps[compIndex] = comp
            return { ...prev, components: newComps }
        })
    }

    // --- Slider Model Action Handlers ---
    const addSliderModelAction = (compIndex) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const comp = { ...newComps[compIndex] }
            comp.properties = {
                ...comp.properties,
                modelActions: [
                    ...(comp.properties.modelActions || []),
                    {
                        type: 'trigger',
                        operator: '>',
                        value: 50,
                        targetModelId: '',
                        action: 'play',
                    },
                ],
            }
            newComps[compIndex] = comp
            return { ...prev, components: newComps }
        })
    }

    const updateSliderModelAction = (compIndex, actionIndex, field, value) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const comp = { ...newComps[compIndex] }
            const modelActions = [...(comp.properties.modelActions || [])]
            modelActions[actionIndex] = {
                ...modelActions[actionIndex],
                [field]: value,
            }
            comp.properties = { ...comp.properties, modelActions }
            newComps[compIndex] = comp
            return { ...prev, components: newComps }
        })
    }

    const removeSliderModelAction = (compIndex, actionIndex) => {
        setFormData((prev) => {
            const newComps = [...prev.components]
            const comp = { ...newComps[compIndex] }
            comp.properties = {
                ...comp.properties,
                modelActions: (comp.properties.modelActions || []).filter(
                    (_, i) => i !== actionIndex
                ),
            }
            newComps[compIndex] = comp
            return { ...prev, components: newComps }
        })
    }

    const validateAndParseRawBytes = (text) => {
        if (!text || !text.trim())
            return { valid: true, value: [], type: 'array' }

        const trimmed = text.trim()
        const FORMAT_MSG =
            'Provide either:\n• A 2D array of bytes: [[76, 87, 76, 75, 90], [76, 0, 0, 0, 0]]\n• A 1D array (auto-wrapped into one packet): [76, 87, 76, 75, 90]\n\nEach value must be an integer between 0 and 255.'

        if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
            try {
                const parsed = JSON.parse(trimmed)
                if (!Array.isArray(parsed)) {
                    alert(`❌ Invalid Raw Bytes format.\n\n${FORMAT_MSG}`)
                    return { valid: false }
                }

                const isAllIntegers = (arr) =>
                    arr.every(
                        (n) =>
                            typeof n === 'number' &&
                            Number.isInteger(n) &&
                            n >= 0 &&
                            n <= 255
                    )

                // Case 1: Already a valid 2D array
                if (parsed.length > 0 && Array.isArray(parsed[0])) {
                    if (
                        parsed.every(
                            (row) => Array.isArray(row) && isAllIntegers(row)
                        )
                    ) {
                        return { valid: true, value: parsed, type: 'array' }
                    }
                    alert(
                        `❌ Invalid Raw Bytes format.\n\nEach inner array must contain integers between 0 and 255.\n\n${FORMAT_MSG}`
                    )
                    return { valid: false }
                }

                // Case 2: 1D array of integers — auto-wrap into a 2D array
                if (parsed.length === 0 || isAllIntegers(parsed)) {
                    return {
                        valid: true,
                        value: parsed.length > 0 ? [parsed] : [],
                        type: 'array',
                    }
                }

                alert(`❌ Invalid Raw Bytes format.\n\n${FORMAT_MSG}`)
                return { valid: false }
            } catch {
                alert(
                    `❌ Invalid Raw Bytes format — could not parse as JSON.\n\n${FORMAT_MSG}`
                )
                return { valid: false }
            }
        }

        // Case 3: Plain strings and any other format are rejected
        alert(
            `❌ Invalid Raw Bytes format.\n\nPlain strings are no longer accepted.\n\n${FORMAT_MSG}`
        )
        return { valid: false }
    }

    // --- MIGRATION HANDLERS ---
    const handleMigrateIds = () => {
        if (!formData || !formData.components) return

        setPreMigrationBackup(JSON.parse(JSON.stringify(formData)))

        const BUTTON_TYPES = [
            'button_control',
            'tack_button_control',
            'hold_button_control',
            'trigger_button_control',
        ]
        let boardCount = 0
        let gestureCount = 0
        let imageCount = 0

        const migratedComponents = formData.components.map((comp) => {
            let updated = {
                ...comp,
                properties: { ...(comp.properties || {}) },
            }

            // 1. Remap toki_board -> board canonical type
            if (updated.type === 'toki_board') {
                updated.type = 'board'
                boardCount++
            }

            // 2. Hardware Synchronization: Force name and image to match current allowedDevice
            if (updated.type === 'board') {
                if (updated.name !== boardLabel) {
                    updated.name = boardLabel // Rewrites to 'POKI Board' or 'TOKI Board'
                    boardCount++
                }
                if (updated.properties.image !== boardImage) {
                    updated.properties.image = boardImage // Rewrites to 'poki.png' or 'toki.png'
                    imageCount++
                }
            }

            // 3. Button controls: fix images to gesture assets
            if (BUTTON_TYPES.includes(updated.type)) {
                if (updated.properties.onImage !== 'gesture@2x_active.png') {
                    updated.properties.onImage = 'gesture@2x_active.png'
                    gestureCount++
                }
                if (updated.properties.offImage !== 'gesture@2x_inactive.png') {
                    updated.properties.offImage = 'gesture@2x_inactive.png'
                }
            }

            return updated
        })

        const migratedFormData = { ...formData, components: migratedComponents }
        setFormData(migratedFormData)

        sessionStorage.setItem('migrationBackup', JSON.stringify(formData))
        sessionStorage.setItem(
            'migratedFormData',
            JSON.stringify(migratedFormData)
        )

        const lines = []
        if (boardCount > 0)
            lines.push(
                `• Synchronized hardware device schema to current mode: ${boardLabel}.`
            )
        if (imageCount > 0)
            lines.push(
                `• Updated canvas view background asset to: ${boardImage}.`
            )
        if (gestureCount > 0)
            lines.push(`• Fixed button layout asset mapping URLs.`)
        if (lines.length === 0)
            lines.push('• Device schema matches current session perfectly.')

        alert(
            `Device Alignment Complete!\n\n${lines.join('\n')}\n\nClick 'Save' to commit to staging JSON storage.`
        )
    }

    const handleRevertMigration = () => {
        if (preMigrationBackup) {
            // Restore the backup
            setFormData(preMigrationBackup)
            setPreMigrationBackup(null)
            sessionStorage.removeItem('tempPreviewData')
            sessionStorage.removeItem('migrationBackup')
            sessionStorage.removeItem('migratedFormData')
            alert('Migration reverted. Your original IDs have been restored.')
        }
    }

    // True if any non-free step has a conditionBlock entry with an empty inputId
    const hasEmptyConditionInputs = (formData?.steps || []).some((step) => {
        if (step.isFreeStep) return false
        return (step.conditionBlocks || []).some((block) =>
            block.some((cond) => !cond.inputId)
        )
    })

    const handleSave = async () => {
        const comps = formData.components || []
        for (let i = 0; i < comps.length; i++) {
            const c = comps[i]
            // Skip if rawBytes is totally disabled (null/undefined)
            if (
                c.properties?.rawBytes === undefined ||
                c.properties?.rawBytes === null
            )
                continue

            const text = c.properties.rawBytesText ?? ''
            const result = validateAndParseRawBytes(text)
            if (!result.valid) {
                // validateAndParseRawBytes already showed a descriptive alert;
                // just surface which component failed so the admin can find it.
                alert(
                    `❌ Save blocked on component "${c.properties.label || c.name || c.type}" (#${i + 1}) — see the format error above.`
                )
                return
            }
        }

        // Validate: enableComponents and highlight must be subsets of visibleComponents
        const compNameMap = Object.fromEntries(
            (formData.components || []).map((c) => [
                c.id,
                `${COMPONENT_LABELS[c.type] || c.type} (${c.name})`,
            ])
        )
        const visibilityErrors = []
        ;(formData.steps || []).forEach((step, idx) => {
            const visible = new Set(step.visibleComponents || [])
            const offenders = [
                ...(step.enableComponents || []).filter(
                    (id) => !visible.has(id)
                ),
                ...(step.highlight || []).filter((id) => !visible.has(id)),
            ]
            if (offenders.length > 0) {
                const names = [...new Set(offenders)]
                    .map((id) => compNameMap[id] || id)
                    .join(', ')
                visibilityErrors.push(`  Step ${idx + 1}: ${names}`)
            }
        })
        if (visibilityErrors.length > 0) {
            alert(
                `❌ Save blocked — the following components are set as Enabled or Highlighted but are NOT in Visible Components:\n\n` +
                    visibilityErrors.join('\n') +
                    `\n\nPlease make sure every Enabled / Highlighted component is also marked Visible.`
            )
            return
        }

        // Scrub ghost values from conditionBlocks before saving (steps + components)
        const scrubConditionForSave = (cond) => {
            const type = cond.inputType || ''
            if (BUTTON_INPUT_TYPES.includes(type)) {
                return {
                    inputId: cond.inputId,
                    inputType: type,
                    expectedStatus: cond.expectedStatus || 'ON',
                }
            }
            if (SLIDER_INPUT_TYPES.includes(type)) {
                return {
                    inputId: cond.inputId,
                    inputType: type,
                    operator: cond.operator || '>',
                    value: cond.value ?? 0,
                }
            }
            if (RGB_SLIDER_INPUT_TYPES.includes(type)) {
                return {
                    inputId: cond.inputId,
                    inputType: type,
                    channel: cond.channel || 'red',
                    operator: cond.operator || '>',
                    value: cond.value ?? 0,
                }
            }
            return cond
        }
        // Sanitize ghost IDs + scrub step conditionBlocks
        const validComponentIds = new Set(comps.map((c) => c.id))
        const sanitizedSteps = (formData.steps || []).map((step) => {
            // Forcefully drop 'validations' right before saving
            const { validations, ...cleanStep } = step

            // Determine the ground-truth for isFreeStep and conditionBlocks:
            // conditionBlocks takes precedence as the source of truth.
            const rawBlocks = cleanStep.conditionBlocks || [[]]
            const scrubbedBlocks = rawBlocks.map((block) =>
                block.map(scrubConditionForSave)
            )
            const stepsHaveConditions = scrubbedBlocks.some(
                (block) => block.length > 0
            )
            // Enforce consistency:
            //   has conditions → isFreeStep MUST be false
            //   no conditions  → isFreeStep MUST be true
            const resolvedIsFreeStep = !stepsHaveConditions
            // If free step, emit empty conditionBlocks; otherwise keep scrubbed blocks
            const resolvedConditionBlocks = resolvedIsFreeStep
                ? [[]]
                : scrubbedBlocks

            // Strip legacy fields from live entries; pass through fixedBytes, isFree, compsIds.
            const scrubbedLive =
                cleanStep.live && typeof cleanStep.live === 'object'
                    ? Object.fromEntries(
                          Object.entries(cleanStep.live).map(
                              ([inputId, entry]) => {
                                  const {
                                      min,
                                      max,
                                      uiMin,
                                      uiMax,
                                      onValue,
                                      uiOnValue,
                                      index,
                                      compType,
                                      ...rest
                                  } = entry
                                  return [
                                      inputId,
                                      {
                                          isFree: true,
                                          compsIds: {},
                                          ...rest,
                                      },
                                  ]
                              }
                          )
                      )
                    : undefined

            return {
                ...cleanStep,
                isFreeStep: resolvedIsFreeStep,
                conditionBlocks: resolvedConditionBlocks,
                ...(scrubbedLive !== undefined ? { live: scrubbedLive } : {}),
                onSuccessActions: (cleanStep.onSuccessActions || [])
                    .map((action) => {
                        // Parse rawBytesText → direct for raw-mode actions; savedBytes → fixedBytes
                        const {
                            useRawBytes,
                            rawBytesText,
                            savedBytes,
                            rawBytes: _legacyRawBytes,
                            ...restAction
                        } = action
                        let parsedDirect = null
                        if (useRawBytes && rawBytesText) {
                            const { value, valid } =
                                validateAndParseRawBytes(rawBytesText)
                            if (
                                valid &&
                                value !== null &&
                                value !== undefined &&
                                (Array.isArray(value) ? value.length > 0 : true)
                            ) {
                                parsedDirect = value
                            }
                        }
                        const fixedBytes = savedBytes || null
                        const directBytes = parsedDirect
                        // free: true = use fixedBytes, false = use direct
                        let free = null
                        if (fixedBytes !== null) free = true
                        else if (directBytes !== null) free = false
                        return {
                            ...restAction,
                            ...(fixedBytes !== null ? { fixedBytes } : {}),
                            ...(directBytes !== null
                                ? { direct: directBytes }
                                : {}),
                            ...(free !== null ? { free } : {}),
                        }
                    })
                    .filter(
                        (a) =>
                            (a.outputId && a.targetImage) ||
                            a.fixedBytes ||
                            a.direct
                    ),
                visibleComponents: (cleanStep.visibleComponents || []).filter(
                    (id) => validComponentIds.has(id)
                ),
                enableComponents: (cleanStep.enableComponents || []).filter(
                    (id) => validComponentIds.has(id)
                ),
                highlight: (cleanStep.highlight || []).filter((id) =>
                    validComponentIds.has(id)
                ),
            }
        })

        const scrubbedComponents = comps.map((c) => {
            // Forcefully align the canvas background descriptor to current runtime state
            if (c.type === 'toki_board' || c.type === 'board') {
                return {
                    ...c,
                    type: 'board',
                    name: boardLabel, // Persists 'POKI Board' or 'TOKI Board' directly into DB
                    properties: {
                        ...c.properties,
                        image: boardImage, // Persists 'poki.png' or 'toki.png' directly into DB
                    },
                }
            }
            // Outputs: scrub legacy states conditionBlocks + strip empty states array
            if (c.type === 'output' || c.type === 'custom_output') {
                const cleanedStates = (c.properties?.states || []).map(
                    (state) => ({
                        ...state,
                        conditionBlocks: (state.conditionBlocks || []).map(
                            (block) => block.map(scrubConditionForSave)
                        ),
                    })
                )
                const props = { ...c.properties }
                if (cleanedStates.length === 0) delete props.states
                else props.states = cleanedStates
                return { ...c, properties: props }
            }
            // Input buttons: scrub empty actions from events
            if (
                [
                    'button_control',
                    'tack_button_control',
                    'hold_button_control',
                    'trigger_button_control',
                ].includes(c.type)
            ) {
                const events = c.properties?.events
                if (!events) return c
                const cleanEvents = {}
                Object.entries(events).forEach(([key, actions]) => {
                    cleanEvents[key] = (actions || []).filter(
                        (a) => a.outputId && a.targetImage
                    )
                })
                return {
                    ...c,
                    properties: { ...c.properties, events: cleanEvents },
                }
            }
            // Input sliders: scrub empty actions from conditions
            if (['slider_control', 'rgb_control'].includes(c.type)) {
                const conditions = c.properties?.conditions
                if (!conditions) return c
                const cleanConds = conditions.map((cond) => ({
                    ...cond,
                    actions: (cond.actions || []).filter(
                        (a) => a.outputId && a.targetImage
                    ),
                }))
                const cleanedProps = { ...c.properties, conditions: cleanConds } // ADD THIS TO SCRUB OLD RGB DATA
                if (c.type === 'rgb_control') {
                    delete cleanedProps.sliders
                    delete cleanedProps.bgImage
                    delete cleanedProps.bg_image
                }
                return {
                    ...c,
                    properties: cleanedProps,
                }
            }
            return c
        })

        // Generate settingbytes (LS = Live Start) for all active output components.
        // Format mirrors LiveButton.js handelClick exactly:
        //   data = Array(len).fill(79)  — len = Object.keys(portMap).length + 2
        //   data[0] = 'L' (76), data[1] = 'S' (83)
        //   data[portNum + 1] = charCode  — positional slot per-port, NOT appended pairs
        // The board component type is always 'board' (device-agnostic), so the
        // live-byte device comes from the active board resolved at the top.
        const deviceName = boardDevice
        const portMap = livePortsNumber[deviceName] || livePortsNumber.Ace || {}
        const lsLen = Object.keys(portMap).length + 2
        const generatedSettingBytes = Array(lsLen).fill(79) // 79 = 'O' (off/unused)
        generatedSettingBytes[0] = 76 // 'L'
        generatedSettingBytes[1] = 83 // 'S'

        // Component types that can receive live bytes (matches LiveButton.js allow-list)
        const LIVE_OUTPUT_TYPES = new Set([
            'led_1c',
            'led',
            'geared_motor',
            'mini_geared_motor',
            'dc_motor',
            'dynamex_motor',
            'servo_motor',
            'servo_motor_270',
            'servo_motor_sg90',
            'servo_motor_360',
            'pwm_3_pin_output',
            'PWM_3_Pin_Output',
            'rgb',
            // NEW POKI ONLY COMPONENTS
            'oled',
            'imu',
            'color_sensor',
        ])

        scrubbedComponents.forEach((c) => {
            if (c.type !== 'output' && c.type !== 'custom_output') return
            const normalizedName =
                c.name?.trim().toLowerCase().replace(/ /g, '_') || ''
            if (!normalizedName) return
            // Accept both lowercase variant (from admin name field) and original casing
            if (
                !LIVE_OUTPUT_TYPES.has(normalizedName) &&
                !LIVE_OUTPUT_TYPES.has(c.name?.trim())
            )
                return
            // charCode: look up by exact name first, then normalized, fallback to 'P'
            const charCode =
                compName_character[c.name?.trim()] ??
                compName_character[normalizedName] ??
                80 // 'P'
            const portNum = portMap[c.port] ?? livePortsNumber.Ace?.[c.port]
            if (portNum === undefined || portNum === null) return
            generatedSettingBytes[portNum + 1] = charCode
        })

        const modelPath = collectPlayModelUrls(scrubbedComponents)

        // Build save payload
        const saveData = {
            ...formData,
            settingbytes: generatedSettingBytes,
            meta: {
                ...(formData.meta || {}),
                modelPath,
            },
            steps: sanitizedSteps,
            assemblyScreenId: selectedAssemblyScreen, // <--- CRITICAL FIX: Saving the screen ID
            components: scrubbedComponents.map((c) => {
                if (c.type === 'model') {
                    const { properties, ...rest } = c
                    return rest
                }

                // Extract all possible UI-state and legacy fields
                const {
                    rawBytes,
                    rawBytesText,
                    savedBytes,
                    enablebytes,
                    enableBytes,
                    settingBytes,
                    settingbytes: legacySettingbytes,
                    free: _free, // strip any stale free flag from UI state
                    ...restProps
                } = c.properties || {}

                // fixedBytes: recorded 2D array from ByteRecorderPopup
                const fixedBytes = savedBytes || null

                // direct: raw pasted bytes (flat array or string)
                let directBytes = null
                if (rawBytes !== undefined && rawBytes !== null) {
                    const text = rawBytesText ?? ''
                    const { value } = validateAndParseRawBytes(text)
                    const hasValue =
                        value !== null &&
                        value !== undefined &&
                        (Array.isArray(value) ? value.length > 0 : true)
                    if (hasValue) directBytes = value
                }

                // free: true = use fixedBytes, false = use direct
                let free = null
                if (fixedBytes !== null) free = true
                else if (directBytes !== null) free = false

                // --- Auto-inject min and max for slider_control ---
                let injectedProps = {}
                if (c.type === 'slider_control') {
                    const hardwareType = (c.name || '')
                        .toLowerCase()
                        .replace(/ /g, '_')
                    const range = COMPONENT_RANGES[hardwareType] || {
                        min: 0,
                        max: 100,
                    }
                    injectedProps.min = range.min
                    injectedProps.max = range.max
                }

                return {
                    ...c,
                    properties: {
                        ...restProps,
                        ...(fixedBytes !== null && { fixedBytes }),
                        ...(directBytes !== null && { direct: directBytes }),
                        ...(free !== null && { free }),
                        ...injectedProps,
                        // settingbytes is now TOP-LEVEL, never per-component
                    },
                }
            }),
        }

        try {
            const res = await fetch(`${STAGING_URL}/${apiPath}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(saveData),
                credentials: 'include',
            })

            if (!res.ok) throw new Error('Failed to save data')
            // Migration is now saved permanently — clear migration state
            setPreMigrationBackup(null)
            sessionStorage.removeItem('migrationBackup')
            sessionStorage.removeItem('migratedFormData')
            sessionStorage.removeItem('tempPreviewData')
            alert('Play tutorial saved successfully to staging!')
        } catch (error) {
            console.error('Save error:', error)
            alert('Failed to save tutorial.')
        }
    }

    const handlePreviewClick = () => {
        const isMigrated = !!preMigrationBackup
        const previewData = buildPreviewPlayData(formData)

        if (isMigrated) {
            // Migration is active — preview the in-memory (migrated) data
            if (
                !window.confirm(
                    'You have unsaved migrated data. Click OK to preview the migrated version (nothing is saved yet).'
                )
            )
                return
            sessionStorage.setItem(
                'tempPreviewData',
                JSON.stringify(previewData)
            )
            // Keep migratedFormData in sync so it persists when we come back
            sessionStorage.setItem(
                'migratedFormData',
                JSON.stringify(previewData)
            )
            history.push(
                `/playScreen?preview=true&unsaved=true&projectId=${subId}${screenId ? `&screenId=${screenId}` : ''}`
            )
        } else {
            // Normal flow — preview the last saved version
            if (
                !window.confirm(
                    'Have you saved all your changes? Unsaved changes will be lost. Click OK to continue and preview the last saved version.'
                )
            )
                return
            // Clear any stale temp data so PlayScreen fetches from backend
            sessionStorage.removeItem('tempPreviewData')
            history.push(
                `/playScreen?preview=true&projectId=${subId}${screenId ? `&screenId=${screenId}` : ''}`
            )
        }
    }

    // Derived: find current board component (if any)
    const currentBoard = formData?.components?.find((c) =>
        CATEGORIES.board.includes(c.type)
    )

    const handleConfirmAdd = () => {
        if (!pendingType) return

        // Limit boards to max 1
        if (CATEGORIES.board.includes(pendingType)) {
            const existingBoard = formData.components.find((c) =>
                CATEGORIES.board.includes(c.type)
            )
            if (existingBoard) {
                window.alert(
                    'Only one board can be added at a time. Remove the existing board first.'
                )
                return
            }
        }

        addComponent(pendingType)
        setShowAddedFeedback(true)
        setPendingType('')
        setSelectedCategory('') // Adding this resets the whole toolbar to the start

        setTimeout(() => {
            setShowAddedFeedback(false)
        }, 3000)
    }

    const openByteRecorder = (compIndex) => {
        if (!currentBoard) {
            window.alert(
                'No board has been added! Please add a board first (e.g. TOKI Board) before recording bytes.'
            )
            return
        }
        setByteRecorderActionContext(null)
        setByteRecorderCompIndex(compIndex)
        setByteRecorderOpen(true)
    }

    const openByteRecorderForAction = (stepIndex, actionIndex) => {
        if (!currentBoard) {
            window.alert(
                'No board has been added! Please add a board first (e.g. TOKI Board) before recording bytes.'
            )
            return
        }
        setByteRecorderCompIndex(null)
        setByteRecorderActionContext({ stepIndex, actionIndex })
        setByteRecorderOpen(true)
    }

    const handleByteRecorded = (bytes, sliderValue, settingBytes) => {
        // Action-level recording: save bytes into the step's onSuccessActions entry
        if (byteRecorderActionContext != null) {
            const { stepIndex, actionIndex } = byteRecorderActionContext
            setFormData((prev) => {
                const newSteps = [...prev.steps]
                const step = { ...newSteps[stepIndex] }
                const actions = [...(step.onSuccessActions || [])]
                actions[actionIndex] = {
                    ...actions[actionIndex],
                    savedBytes: bytes,
                    recordedValue: sliderValue,
                    rawBytes: undefined,
                    rawBytesText: '',
                    useRawBytes: false,
                }
                step.onSuccessActions = actions
                newSteps[stepIndex] = step
                return {
                    ...prev,
                    steps: newSteps,
                }
            })
            setByteRecorderOpen(false)
            setByteRecorderActionContext(null)
            return
        }

        // Component-level recording (legacy path)
        if (byteRecorderCompIndex == null) return
        setFormData((prev) => {
            const newComps = [...prev.components]
            newComps[byteRecorderCompIndex].properties = {
                ...newComps[byteRecorderCompIndex].properties,
                savedBytes: bytes,
                recordedValue: sliderValue,
                // --- NEW: WIPE RAW BYTES ---
                rawBytes: null,
                rawBytesText: '',
            }
            return {
                ...prev,
                components: newComps,
            }
        })

        setByteRecorderOpen(false)
        setByteRecorderCompIndex(null)
    }

    // ==========================================
    // RENDER HELPERS
    // ==========================================

    // Reusable Image Upload Field UI
    const ImageUploadField = ({ label, value, onChange }) => (
        <div style={{ marginBottom: '10px' }}>
            <label
                style={{
                    display: 'block',
                    fontSize: '0.85rem',
                    color: '#555',
                    marginBottom: '4px',
                }}
            >
                {label}
            </label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    style={{
                        flex: 1,
                        padding: '6px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                    }}
                    placeholder="e.g. renderImage('myBtn') or my-image.png"
                />
                <input
                    type="file"
                    accept=".png, image/png"
                    onChange={(e) => handleImageUpload(e, onChange)}
                    style={{ width: '190px', fontSize: '0.8rem' }}
                />
            </div>
        </div>
    )

    // Reusable Model Upload Field UI
    const ModelUploadField = ({ label, value, onChange }) => (
        <div style={{ marginBottom: '10px' }}>
            <label
                style={{
                    display: 'block',
                    fontSize: '0.85rem',
                    color: '#555',
                    marginBottom: '4px',
                }}
            >
                {label}
            </label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    style={{
                        flex: 1,
                        padding: '6px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                    }}
                    placeholder="e.g. https://storage.googleapis.com/..."
                />
                <input
                    type="file"
                    accept=".glb"
                    onChange={(e) => handleModelUpload(e, onChange, value)}
                    disabled={uploading}
                    style={{ width: '190px', fontSize: '0.8rem' }}
                />
            </div>
            {uploading && (
                <p
                    style={{
                        color: '#1976d2',
                        fontWeight: 'bold',
                        margin: '8px 0 0',
                    }}
                >
                    Uploading model to cloud storage...
                </p>
            )}
        </div>
    )

    // Reusable States Editor (For Output comp)
    // ── renderButtonModelAnimationsSection ───────────────────────
    // Renders the 3D Model Animations UI for button/tack/trigger/rgb_button inputs.
    const renderButtonModelAnimationsSection = (comp, index) => {
        const eventKeys =
            comp.type === 'trigger_button_control'
                ? ['onTrigger']
                : ['onON', 'onOFF']
        const modelComps = (formData.components || []).filter(
            (c) => c.type === 'model'
        )
        const modelActions = comp.properties.modelActions || {}

        const renderActionFields = (ma, maIdx, eventKey) => {
            const upd = (field, val) =>
                updateButtonModelAction(index, eventKey, maIdx, field, val)
            const action = ma.action || 'play'
            return (
                <>
                    {action === 'jumpTo' && (
                        <div style={{ flex: 1, minWidth: '90px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    fontSize: '0.72rem',
                                    color: '#555',
                                    marginBottom: '2px',
                                }}
                            >
                                Frame (0-1)
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="1"
                                value={ma.targetFrame ?? 0}
                                onChange={(e) =>
                                    upd(
                                        'targetFrame',
                                        parseFloat(e.target.value)
                                    )
                                }
                                style={{
                                    width: '100%',
                                    padding: '5px',
                                    borderRadius: '4px',
                                    border: '1px solid #9fa8da',
                                }}
                            />
                        </div>
                    )}
                    {action === 'glideToFrame' && (
                        <>
                            <div style={{ flex: 1, minWidth: '80px' }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '0.72rem',
                                        color: '#555',
                                        marginBottom: '2px',
                                    }}
                                >
                                    Start Frame (0-1)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={ma.startFrame ?? 0}
                                    onChange={(e) =>
                                        upd(
                                            'startFrame',
                                            parseFloat(e.target.value)
                                        )
                                    }
                                    style={{
                                        width: '100%',
                                        padding: '5px',
                                        borderRadius: '4px',
                                        border: '1px solid #9fa8da',
                                    }}
                                />
                            </div>
                            <div style={{ flex: 1, minWidth: '80px' }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '0.72rem',
                                        color: '#555',
                                        marginBottom: '2px',
                                    }}
                                >
                                    End Frame (0-1)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={ma.endFrame ?? 1}
                                    onChange={(e) =>
                                        upd(
                                            'endFrame',
                                            parseFloat(e.target.value)
                                        )
                                    }
                                    style={{
                                        width: '100%',
                                        padding: '5px',
                                        borderRadius: '4px',
                                        border: '1px solid #9fa8da',
                                    }}
                                />
                            </div>
                            <div style={{ flex: 1, minWidth: '80px' }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '0.72rem',
                                        color: '#555',
                                        marginBottom: '2px',
                                    }}
                                >
                                    Speed (0-100)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={ma.speed ?? 50}
                                    onChange={(e) =>
                                        upd('speed', parseFloat(e.target.value))
                                    }
                                    style={{
                                        width: '100%',
                                        padding: '5px',
                                        borderRadius: '4px',
                                        border: '1px solid #9fa8da',
                                    }}
                                />
                            </div>
                        </>
                    )}
                    {action === 'switchModelAnim' && (
                        <>
                            <div style={{ flex: 2, minWidth: '130px' }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '0.72rem',
                                        color: '#555',
                                        marginBottom: '2px',
                                    }}
                                >
                                    Animation Name
                                </label>
                                <input
                                    type="text"
                                    value={ma.targetAnimation || ''}
                                    onChange={(e) =>
                                        upd('targetAnimation', e.target.value)
                                    }
                                    placeholder="e.g. Spin, Run"
                                    style={{
                                        width: '100%',
                                        padding: '5px',
                                        borderRadius: '4px',
                                        border: '1px solid #9fa8da',
                                        boxSizing: 'border-box',
                                    }}
                                />
                            </div>
                            <button
                                onClick={() => {
                                    setOrientationModalContext({
                                        type: 'buttonModelAction',
                                        compIndex: index,
                                        eventKey,
                                        actionIndex: maIdx,
                                    })
                                    setOrientationModalOpen(true)
                                }}
                                style={{
                                    padding: '5px 10px',
                                    background: '#ff9800',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    alignSelf: 'flex-end',
                                }}
                            >
                                Set Orientation
                            </button>
                            {ma.targetOrbit && (
                                <span
                                    style={{
                                        fontSize: '0.75rem',
                                        color: '#555',
                                        alignSelf: 'center',
                                    }}
                                >
                                    H={ma.targetOrbit.theta}° V=
                                    {ma.targetOrbit.phi}° Z=
                                    {ma.targetOrbit.radius}%
                                </span>
                            )}
                            {/* Full-width GLB upload row */}
                            <div style={{ flex: '0 0 100%', marginTop: '4px' }}>
                                <ModelUploadField
                                    label="Target .glb File (optional — leave blank to reuse the same model)"
                                    value={ma.targetModelUrl || ''}
                                    onChange={(val) =>
                                        upd('targetModelUrl', val)
                                    }
                                />
                            </div>
                        </>
                    )}
                </>
            )
        }

        return (
            <div
                style={{
                    marginTop: '10px',
                    padding: '12px',
                    background: '#e8eaf6',
                    borderRadius: '6px',
                    border: '1px solid #9fa8da',
                }}
            >
                <strong
                    style={{
                        color: '#283593',
                        fontSize: '0.9rem',
                        display: 'block',
                        marginBottom: '10px',
                    }}
                >
                    🎬 3D Model Animations
                </strong>
                {eventKeys.map((eventKey) => (
                    <div key={eventKey} style={{ marginBottom: '14px' }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '6px',
                            }}
                        >
                            <label
                                style={{
                                    fontWeight: 'bold',
                                    fontSize: '0.85rem',
                                    color:
                                        eventKey === 'onON'
                                            ? '#2e7d32'
                                            : eventKey === 'onOFF'
                                              ? '#c62828'
                                              : '#6a1b9a',
                                }}
                            >
                                {eventKey === 'onON'
                                    ? '🟢 When turned ON'
                                    : eventKey === 'onOFF'
                                      ? '🔴 When turned OFF'
                                      : '▶ When Triggered'}
                            </label>
                            <button
                                onClick={() =>
                                    addButtonModelAction(index, eventKey)
                                }
                                style={{
                                    padding: '4px 10px',
                                    background: '#3949ab',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                }}
                            >
                                + Add Animation
                            </button>
                        </div>
                        {(modelActions[eventKey] || []).length === 0 && (
                            <div
                                style={{
                                    color: '#bbb',
                                    fontSize: '0.8rem',
                                    fontStyle: 'italic',
                                    padding: '4px',
                                }}
                            >
                                No animations yet
                            </div>
                        )}
                        {(modelActions[eventKey] || []).map((ma, maIdx) => (
                            <div
                                key={maIdx}
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                    alignItems: 'flex-end',
                                    marginBottom: '6px',
                                    padding: '8px',
                                    background: '#c5cae9',
                                    borderRadius: '4px',
                                    border: '1px solid #9fa8da',
                                }}
                            >
                                <div style={{ flex: 1.5, minWidth: '120px' }}>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: '0.72rem',
                                            color: '#555',
                                            marginBottom: '2px',
                                        }}
                                    >
                                        Target Model
                                    </label>
                                    <select
                                        value={ma.targetModelId || ''}
                                        onChange={(e) =>
                                            updateButtonModelAction(
                                                index,
                                                eventKey,
                                                maIdx,
                                                'targetModelId',
                                                e.target.value
                                            )
                                        }
                                        style={{
                                            width: '100%',
                                            padding: '5px',
                                            borderRadius: '4px',
                                            border: '1px solid #9fa8da',
                                            fontSize: '0.85rem',
                                        }}
                                    >
                                        <option value="">
                                            -- Select Model --
                                        </option>
                                        {modelComps.map((mc) => (
                                            <option key={mc.id} value={mc.id}>
                                                {mc.name || mc.id}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={{ flex: 1, minWidth: '120px' }}>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: '0.72rem',
                                            color: '#555',
                                            marginBottom: '2px',
                                        }}
                                    >
                                        Action
                                    </label>
                                    <select
                                        value={ma.action || 'play'}
                                        onChange={(e) =>
                                            updateButtonModelAction(
                                                index,
                                                eventKey,
                                                maIdx,
                                                'action',
                                                e.target.value
                                            )
                                        }
                                        style={{
                                            width: '100%',
                                            padding: '5px',
                                            borderRadius: '4px',
                                            border: '1px solid #9fa8da',
                                            fontSize: '0.85rem',
                                        }}
                                    >
                                        <option value="play">Play</option>
                                        <option value="pause">Pause</option>
                                        <option value="togglePlay">
                                            Toggle Play
                                        </option>
                                        <option value="jumpTo">
                                            Jump To Frame
                                        </option>
                                        <option value="glideToFrame">
                                            Glide To Frame
                                        </option>
                                        <option value="switchModelAnim">
                                            Switch Anim/Model
                                        </option>
                                    </select>
                                </div>
                                {renderActionFields(ma, maIdx, eventKey)}
                                <button
                                    onClick={() =>
                                        removeButtonModelAction(
                                            index,
                                            eventKey,
                                            maIdx
                                        )
                                    }
                                    style={{
                                        padding: '6px 10px',
                                        background: '#f44336',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        )
    }

    // ── renderSliderModelAnimationsSection ────────────────────────
    // Renders the 3D Model Animations UI for slider_control and rgb_control inputs.
    const renderSliderModelAnimationsSection = (comp, index) => {
        const isRgb = comp.type === 'rgb_control'
        const modelComps = (formData.components || []).filter(
            (c) => c.type === 'model'
        )
        const modelActions = comp.properties.modelActions || []

        const renderTriggerActionFields = (ma, maIdx) => {
            const upd = (field, val) =>
                updateSliderModelAction(index, maIdx, field, val)
            const action = ma.action || 'play'
            return (
                <>
                    {action === 'jumpTo' && (
                        <div style={{ flex: 1, minWidth: '80px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    fontSize: '0.72rem',
                                    color: '#555',
                                    marginBottom: '2px',
                                }}
                            >
                                Frame (0-1)
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="1"
                                value={ma.targetFrame ?? 0}
                                onChange={(e) =>
                                    upd(
                                        'targetFrame',
                                        parseFloat(e.target.value)
                                    )
                                }
                                style={{
                                    width: '100%',
                                    padding: '5px',
                                    borderRadius: '4px',
                                    border: '1px solid #9fa8da',
                                }}
                            />
                        </div>
                    )}
                    {action === 'glideToFrame' && (
                        <>
                            <div style={{ flex: 1, minWidth: '80px' }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '0.72rem',
                                        color: '#555',
                                        marginBottom: '2px',
                                    }}
                                >
                                    Start Frame (0-1)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={ma.startFrame ?? 0}
                                    onChange={(e) =>
                                        upd(
                                            'startFrame',
                                            parseFloat(e.target.value)
                                        )
                                    }
                                    style={{
                                        width: '100%',
                                        padding: '5px',
                                        borderRadius: '4px',
                                        border: '1px solid #9fa8da',
                                    }}
                                />
                            </div>
                            <div style={{ flex: 1, minWidth: '80px' }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '0.72rem',
                                        color: '#555',
                                        marginBottom: '2px',
                                    }}
                                >
                                    End Frame (0-1)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={ma.endFrame ?? 1}
                                    onChange={(e) =>
                                        upd(
                                            'endFrame',
                                            parseFloat(e.target.value)
                                        )
                                    }
                                    style={{
                                        width: '100%',
                                        padding: '5px',
                                        borderRadius: '4px',
                                        border: '1px solid #9fa8da',
                                    }}
                                />
                            </div>
                            <div style={{ flex: 1, minWidth: '80px' }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '0.72rem',
                                        color: '#555',
                                        marginBottom: '2px',
                                    }}
                                >
                                    Speed (0-100)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={ma.speed ?? 50}
                                    onChange={(e) =>
                                        upd('speed', parseFloat(e.target.value))
                                    }
                                    style={{
                                        width: '100%',
                                        padding: '5px',
                                        borderRadius: '4px',
                                        border: '1px solid #9fa8da',
                                    }}
                                />
                            </div>
                        </>
                    )}
                    {action === 'switchModelAnim' && (
                        <>
                            <div style={{ flex: 2, minWidth: '130px' }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '0.72rem',
                                        color: '#555',
                                        marginBottom: '2px',
                                    }}
                                >
                                    Animation Name
                                </label>
                                <input
                                    type="text"
                                    value={ma.targetAnimation || ''}
                                    onChange={(e) =>
                                        upd('targetAnimation', e.target.value)
                                    }
                                    placeholder="e.g. Spin, Run"
                                    style={{
                                        width: '100%',
                                        padding: '5px',
                                        borderRadius: '4px',
                                        border: '1px solid #9fa8da',
                                        boxSizing: 'border-box',
                                    }}
                                />
                            </div>
                            <button
                                onClick={() => {
                                    setOrientationModalContext({
                                        type: 'sliderModelAction',
                                        compIndex: index,
                                        actionIndex: maIdx,
                                    })
                                    setOrientationModalOpen(true)
                                }}
                                style={{
                                    padding: '5px 10px',
                                    background: '#ff9800',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    alignSelf: 'flex-end',
                                }}
                            >
                                Set Orientation
                            </button>
                            {ma.targetOrbit && (
                                <span
                                    style={{
                                        fontSize: '0.75rem',
                                        color: '#555',
                                        alignSelf: 'center',
                                    }}
                                >
                                    H={ma.targetOrbit.theta}° V=
                                    {ma.targetOrbit.phi}° Z=
                                    {ma.targetOrbit.radius}%
                                </span>
                            )}
                            {/* Full-width GLB upload row */}
                            <div style={{ flex: '0 0 100%', marginTop: '4px' }}>
                                <ModelUploadField
                                    label="Target .glb File (optional — leave blank to reuse the same model)"
                                    value={ma.targetModelUrl || ''}
                                    onChange={(val) =>
                                        upd('targetModelUrl', val)
                                    }
                                />
                            </div>
                        </>
                    )}
                </>
            )
        }

        return (
            <div
                style={{
                    marginTop: '10px',
                    padding: '12px',
                    background: '#e8eaf6',
                    borderRadius: '6px',
                    border: '1px solid #9fa8da',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                    }}
                >
                    <strong style={{ color: '#283593', fontSize: '0.9rem' }}>
                        🎬 3D Model Animations
                    </strong>
                    <button
                        onClick={() => addSliderModelAction(index)}
                        style={{
                            padding: '4px 10px',
                            background: '#3949ab',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                        }}
                    >
                        + Add Animation
                    </button>
                </div>
                {modelActions.length === 0 && (
                    <div
                        style={{
                            color: '#bbb',
                            fontSize: '0.8rem',
                            fontStyle: 'italic',
                            padding: '4px',
                        }}
                    >
                        No animations yet
                    </div>
                )}
                {modelActions.map((ma, maIdx) => {
                    const upd = (field, val) =>
                        updateSliderModelAction(index, maIdx, field, val)
                    const isTrigger = (ma.type || 'trigger') === 'trigger'
                    return (
                        <div
                            key={maIdx}
                            style={{
                                marginBottom: '10px',
                                padding: '10px',
                                background: '#c5cae9',
                                borderRadius: '6px',
                                border: '1px solid #9fa8da',
                            }}
                        >
                            {/* Row 1: type, channel (rgb), condition fields, model & action */}
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '8px',
                                    flexWrap: 'wrap',
                                    alignItems: 'flex-end',
                                    marginBottom: '6px',
                                }}
                            >
                                <div style={{ flex: 1, minWidth: '120px' }}>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: '0.72rem',
                                            color: '#555',
                                            marginBottom: '2px',
                                        }}
                                    >
                                        Type
                                    </label>
                                    <select
                                        value={ma.type || 'trigger'}
                                        onChange={(e) =>
                                            upd('type', e.target.value)
                                        }
                                        style={{
                                            width: '100%',
                                            padding: '5px',
                                            borderRadius: '4px',
                                            border: '1px solid #9fa8da',
                                        }}
                                    >
                                        <option value="trigger">
                                            Trigger on Value
                                        </option>
                                        <option value="scrub">
                                            Scrub Timeline
                                        </option>
                                    </select>
                                </div>
                                {isRgb && (
                                    <div style={{ flex: 1, minWidth: '90px' }}>
                                        <label
                                            style={{
                                                display: 'block',
                                                fontSize: '0.72rem',
                                                color: '#555',
                                                marginBottom: '2px',
                                            }}
                                        >
                                            Channel
                                        </label>
                                        <select
                                            value={ma.channel || 'red'}
                                            onChange={(e) =>
                                                upd('channel', e.target.value)
                                            }
                                            style={{
                                                width: '100%',
                                                padding: '5px',
                                                borderRadius: '4px',
                                                border: '1px solid #9fa8da',
                                            }}
                                        >
                                            <option value="red">Red</option>
                                            <option value="green">Green</option>
                                            <option value="blue">Blue</option>
                                        </select>
                                    </div>
                                )}
                                {isTrigger && (
                                    <>
                                        <div
                                            style={{
                                                flex: 1,
                                                minWidth: '70px',
                                            }}
                                        >
                                            <label
                                                style={{
                                                    display: 'block',
                                                    fontSize: '0.72rem',
                                                    color: '#555',
                                                    marginBottom: '2px',
                                                }}
                                            >
                                                Operator
                                            </label>
                                            <select
                                                value={ma.operator || '>'}
                                                onChange={(e) =>
                                                    upd(
                                                        'operator',
                                                        e.target.value
                                                    )
                                                }
                                                style={{
                                                    width: '100%',
                                                    padding: '5px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #9fa8da',
                                                }}
                                            >
                                                {[
                                                    '>',
                                                    '>=',
                                                    '<',
                                                    '<=',
                                                    '==',
                                                    '!=',
                                                ].map((op) => (
                                                    <option key={op} value={op}>
                                                        {op}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div
                                            style={{
                                                flex: 1,
                                                minWidth: '70px',
                                            }}
                                        >
                                            <label
                                                style={{
                                                    display: 'block',
                                                    fontSize: '0.72rem',
                                                    color: '#555',
                                                    marginBottom: '2px',
                                                }}
                                            >
                                                Value
                                            </label>
                                            <input
                                                type="number"
                                                value={ma.value ?? 50}
                                                onChange={(e) =>
                                                    upd(
                                                        'value',
                                                        parseFloat(
                                                            e.target.value
                                                        )
                                                    )
                                                }
                                                style={{
                                                    width: '100%',
                                                    padding: '5px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #9fa8da',
                                                }}
                                            />
                                        </div>
                                    </>
                                )}
                                <div style={{ flex: 2, minWidth: '130px' }}>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: '0.72rem',
                                            color: '#555',
                                            marginBottom: '2px',
                                        }}
                                    >
                                        Target Model
                                    </label>
                                    <select
                                        value={ma.targetModelId || ''}
                                        onChange={(e) =>
                                            upd('targetModelId', e.target.value)
                                        }
                                        style={{
                                            width: '100%',
                                            padding: '5px',
                                            borderRadius: '4px',
                                            border: '1px solid #9fa8da',
                                            fontSize: '0.85rem',
                                        }}
                                    >
                                        <option value="">
                                            -- Select Model --
                                        </option>
                                        {modelComps.map((mc) => (
                                            <option key={mc.id} value={mc.id}>
                                                {mc.name || mc.id}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {isTrigger && (
                                    <div style={{ flex: 1, minWidth: '110px' }}>
                                        <label
                                            style={{
                                                display: 'block',
                                                fontSize: '0.72rem',
                                                color: '#555',
                                                marginBottom: '2px',
                                            }}
                                        >
                                            Action
                                        </label>
                                        <select
                                            value={ma.action || 'play'}
                                            onChange={(e) =>
                                                upd('action', e.target.value)
                                            }
                                            style={{
                                                width: '100%',
                                                padding: '5px',
                                                borderRadius: '4px',
                                                border: '1px solid #9fa8da',
                                                fontSize: '0.85rem',
                                            }}
                                        >
                                            <option value="play">Play</option>
                                            <option value="pause">Pause</option>
                                            <option value="togglePlay">
                                                Toggle Play
                                            </option>
                                            <option value="jumpTo">
                                                Jump To Frame
                                            </option>
                                            <option value="glideToFrame">
                                                Glide To Frame
                                            </option>
                                            <option value="switchModelAnim">
                                                Switch Anim/Model
                                            </option>
                                        </select>
                                    </div>
                                )}
                                {!isTrigger && (
                                    <>
                                        <div
                                            style={{
                                                flex: 1,
                                                minWidth: '80px',
                                            }}
                                        >
                                            <label
                                                style={{
                                                    display: 'block',
                                                    fontSize: '0.72rem',
                                                    color: '#555',
                                                    marginBottom: '2px',
                                                }}
                                            >
                                                Start Frame (0-1)
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="1"
                                                value={ma.startFrame ?? 0}
                                                onChange={(e) =>
                                                    upd(
                                                        'startFrame',
                                                        parseFloat(
                                                            e.target.value
                                                        )
                                                    )
                                                }
                                                style={{
                                                    width: '100%',
                                                    padding: '5px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #9fa8da',
                                                }}
                                            />
                                        </div>
                                        <div
                                            style={{
                                                flex: 1,
                                                minWidth: '80px',
                                            }}
                                        >
                                            <label
                                                style={{
                                                    display: 'block',
                                                    fontSize: '0.72rem',
                                                    color: '#555',
                                                    marginBottom: '2px',
                                                }}
                                            >
                                                End Frame (0-1)
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="1"
                                                value={ma.endFrame ?? 1}
                                                onChange={(e) =>
                                                    upd(
                                                        'endFrame',
                                                        parseFloat(
                                                            e.target.value
                                                        )
                                                    )
                                                }
                                                style={{
                                                    width: '100%',
                                                    padding: '5px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #9fa8da',
                                                }}
                                            />
                                        </div>
                                    </>
                                )}
                                <button
                                    onClick={() =>
                                        removeSliderModelAction(index, maIdx)
                                    }
                                    style={{
                                        padding: '6px 10px',
                                        background: '#f44336',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                            {/* Row 2: action-specific fields for trigger */}
                            {isTrigger &&
                                (ma.action === 'jumpTo' ||
                                    ma.action === 'glideToFrame' ||
                                    ma.action === 'switchModelAnim') && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            gap: '8px',
                                            flexWrap: 'wrap',
                                            alignItems: 'flex-end',
                                        }}
                                    >
                                        {renderTriggerActionFields(ma, maIdx)}
                                    </div>
                                )}
                        </div>
                    )
                })}
            </div>
        )
    }

    // ── renderButtonEventsEditor ─────────────────────────────────
    // Shared events UI for button controls (onON / onOFF lists).
    // The button_control / tack_button_control / trigger_button_control cases
    // render their events inline; this helper is for standalone use.
    const renderButtonEventsEditor = (comp, index) => {
        const eventKeys =
            comp.type === 'trigger_button_control'
                ? ['onTrigger']
                : ['onON', 'onOFF']
        return (
            <div
                style={{
                    marginTop: '15px',
                    padding: '12px',
                    background: '#f3e5f5',
                    borderRadius: '6px',
                    border: '1px solid #ce93d8',
                }}
            >
                <strong
                    style={{
                        color: '#6a1b9a',
                        fontSize: '0.9rem',
                        display: 'block',
                        marginBottom: '10px',
                    }}
                >
                    ⚡ Events
                </strong>
                {eventKeys.map((eventKey) => (
                    <div key={eventKey} style={{ marginBottom: '14px' }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '6px',
                            }}
                        >
                            <label
                                style={{
                                    fontWeight: 'bold',
                                    fontSize: '0.85rem',
                                    color:
                                        eventKey === 'onON'
                                            ? '#2e7d32'
                                            : eventKey === 'onOFF'
                                              ? '#c62828'
                                              : '#6a1b9a',
                                }}
                            >
                                {eventKey === 'onON'
                                    ? '🟢 When turned ON'
                                    : eventKey === 'onOFF'
                                      ? '🔴 When turned OFF'
                                      : '▶ When Triggered'}
                            </label>
                            <button
                                onClick={() => addButtonAction(index, eventKey)}
                                style={{
                                    padding: '4px 10px',
                                    background: '#8e24aa',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                }}
                            >
                                + Add Action
                            </button>
                        </div>
                        {(comp.properties.events?.[eventKey] || []).length ===
                            0 && (
                            <div
                                style={{
                                    color: '#bbb',
                                    fontSize: '0.8rem',
                                    fontStyle: 'italic',
                                    padding: '4px',
                                }}
                            >
                                No actions yet
                            </div>
                        )}
                        {(comp.properties.events?.[eventKey] || []).map(
                            (action, actionIndex) => (
                                <div
                                    key={actionIndex}
                                    style={{
                                        marginBottom: '6px',
                                        padding: '8px',
                                        background: '#fce4ec',
                                        borderRadius: '4px',
                                        border: '1px solid #f48fb1',
                                    }}
                                >
                                    {/* Output + Image + Remove row */}
                                    <div
                                        style={{
                                            display: 'flex',
                                            gap: '8px',
                                            alignItems: 'flex-end',
                                        }}
                                    >
                                        <div style={{ flex: 1 }}>
                                            <label
                                                style={{
                                                    display: 'block',
                                                    fontSize: '0.75rem',
                                                    color: '#555',
                                                    marginBottom: '2px',
                                                }}
                                            >
                                                Target Output
                                            </label>
                                            <select
                                                value={action.outputId || ''}
                                                onChange={(e) =>
                                                    updateButtonAction(
                                                        index,
                                                        eventKey,
                                                        actionIndex,
                                                        'outputId',
                                                        e.target.value
                                                    )
                                                }
                                                style={{
                                                    width: '100%',
                                                    padding: '6px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ccc',
                                                    fontSize: '0.85rem',
                                                }}
                                            >
                                                <option value="">
                                                    -- Select Output --
                                                </option>
                                                {(formData.components || [])
                                                    .filter(
                                                        (c) =>
                                                            c.type ===
                                                                'output' ||
                                                            c.type ===
                                                                'custom_output'
                                                    )
                                                    .map((oc) => (
                                                        <option
                                                            key={oc.id}
                                                            value={oc.id}
                                                        >
                                                            {oc.name || oc.id}{' '}
                                                            (Port:{' '}
                                                            {
                                                                PORT_NAME_MAP[
                                                                    oc.port
                                                                ]
                                                            }
                                                            )
                                                        </option>
                                                    ))}
                                            </select>
                                        </div>
                                        <div style={{ flex: 2 }}>
                                            <ImageUploadField
                                                label="Target Image"
                                                value={action.targetImage || ''}
                                                onChange={(val) =>
                                                    updateButtonAction(
                                                        index,
                                                        eventKey,
                                                        actionIndex,
                                                        'targetImage',
                                                        val
                                                    )
                                                }
                                            />
                                        </div>
                                        <button
                                            onClick={() =>
                                                removeButtonAction(
                                                    index,
                                                    eventKey,
                                                    actionIndex
                                                )
                                            }
                                            style={{
                                                padding: '6px 10px',
                                                background: '#f44336',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                flexShrink: 0,
                                                marginBottom: '2px',
                                            }}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                ))}
            </div>
        )
    }

    // ── renderConditionsEditor ────────────────────────────────────
    // Shared conditions UI for slider_control and rgb_control.
    // For rgb_control, each condition also has a 'channel' select.
    const renderConditionsEditor = (comp, index) => {
        const isRgb = comp.type === 'rgb_control'
        return (
            <div
                style={{
                    marginTop: '15px',
                    padding: '12px',
                    background: '#e8f5e9',
                    borderRadius: '6px',
                    border: '1px solid #a5d6a7',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                    }}
                >
                    <strong style={{ color: '#2e7d32', fontSize: '0.9rem' }}>
                        ⚡ Conditions &amp; Actions
                    </strong>
                    <button
                        onClick={() => addSliderCondition(index)}
                        style={{
                            padding: '4px 10px',
                            background: '#388e3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                        }}
                    >
                        + Add Condition
                    </button>
                </div>
                {(comp.properties.conditions || []).length === 0 && (
                    <div
                        style={{
                            color: '#bbb',
                            fontSize: '0.8rem',
                            fontStyle: 'italic',
                            padding: '4px',
                        }}
                    >
                        No conditions yet
                    </div>
                )}
                {(comp.properties.conditions || []).map((cond, condIndex) => (
                    <div
                        key={condIndex}
                        style={{
                            padding: '10px',
                            background: '#fff',
                            border: '1px solid #c8e6c9',
                            borderRadius: '6px',
                            marginBottom: '10px',
                        }}
                    >
                        {/* Row 1: channel (rgb only) + type + condition fields + remove */}
                        <div
                            style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'flex-end',
                                marginBottom: '8px',
                            }}
                        >
                            {isRgb && (
                                <div style={{ flex: 1 }}>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: '0.75rem',
                                            color: '#555',
                                            marginBottom: '2px',
                                        }}
                                    >
                                        Channel
                                    </label>
                                    <select
                                        value={cond.channel || 'red'}
                                        onChange={(e) =>
                                            updateSliderCondition(
                                                index,
                                                condIndex,
                                                'channel',
                                                e.target.value
                                            )
                                        }
                                        style={{
                                            width: '100%',
                                            padding: '6px',
                                            borderRadius: '4px',
                                            border: '1px solid #ccc',
                                        }}
                                    >
                                        <option value="red">Red</option>
                                        <option value="green">Green</option>
                                        <option value="blue">Blue</option>
                                    </select>
                                </div>
                            )}
                            <div style={{ flex: 1 }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '0.75rem',
                                        color: '#555',
                                        marginBottom: '2px',
                                    }}
                                >
                                    Type
                                </label>
                                <select
                                    value={cond.type || 'simple'}
                                    onChange={(e) =>
                                        updateSliderCondition(
                                            index,
                                            condIndex,
                                            'type',
                                            e.target.value
                                        )
                                    }
                                    style={{
                                        width: '100%',
                                        padding: '6px',
                                        borderRadius: '4px',
                                        border: '1px solid #ccc',
                                    }}
                                >
                                    <option value="simple">Value</option>
                                    <option value="range">In between</option>
                                </select>
                            </div>
                            {(cond.type || 'simple') === 'range' ? (
                                <>
                                    <div style={{ flex: 1 }}>
                                        <label
                                            style={{
                                                display: 'block',
                                                fontSize: '0.75rem',
                                                color: '#555',
                                                marginBottom: '2px',
                                            }}
                                        >
                                            From
                                        </label>
                                        <input
                                            type="number"
                                            value={cond.min ?? 0}
                                            onChange={(e) =>
                                                updateSliderCondition(
                                                    index,
                                                    condIndex,
                                                    'min',
                                                    Number(e.target.value)
                                                )
                                            }
                                            style={{
                                                width: '100%',
                                                padding: '6px',
                                                borderRadius: '4px',
                                                border: '1px solid #ccc',
                                            }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label
                                            style={{
                                                display: 'block',
                                                fontSize: '0.75rem',
                                                color: '#555',
                                                marginBottom: '2px',
                                            }}
                                        >
                                            To
                                        </label>
                                        <input
                                            type="number"
                                            value={cond.max ?? 100}
                                            onChange={(e) =>
                                                updateSliderCondition(
                                                    index,
                                                    condIndex,
                                                    'max',
                                                    Number(e.target.value)
                                                )
                                            }
                                            style={{
                                                width: '100%',
                                                padding: '6px',
                                                borderRadius: '4px',
                                                border: '1px solid #ccc',
                                            }}
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div style={{ flex: 1 }}>
                                        <label
                                            style={{
                                                display: 'block',
                                                fontSize: '0.75rem',
                                                color: '#555',
                                                marginBottom: '2px',
                                            }}
                                        >
                                            Operator
                                        </label>
                                        <select
                                            value={cond.operator || '>'}
                                            onChange={(e) =>
                                                updateSliderCondition(
                                                    index,
                                                    condIndex,
                                                    'operator',
                                                    e.target.value
                                                )
                                            }
                                            style={{
                                                width: '100%',
                                                padding: '6px',
                                                borderRadius: '4px',
                                                border: '1px solid #ccc',
                                            }}
                                        >
                                            {[
                                                '>',
                                                '>=',
                                                '<',
                                                '<=',
                                                '==',
                                                '!=',
                                            ].map((op) => (
                                                <option key={op} value={op}>
                                                    {op}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label
                                            style={{
                                                display: 'block',
                                                fontSize: '0.75rem',
                                                color: '#555',
                                                marginBottom: '2px',
                                            }}
                                        >
                                            Value
                                        </label>
                                        <input
                                            type="number"
                                            value={cond.value ?? 0}
                                            onChange={(e) =>
                                                updateSliderCondition(
                                                    index,
                                                    condIndex,
                                                    'value',
                                                    Number(e.target.value)
                                                )
                                            }
                                            style={{
                                                width: '100%',
                                                padding: '6px',
                                                borderRadius: '4px',
                                                border: '1px solid #ccc',
                                            }}
                                        />
                                    </div>
                                </>
                            )}
                            <button
                                onClick={() =>
                                    removeSliderCondition(index, condIndex)
                                }
                                style={{
                                    padding: '6px 10px',
                                    background: '#f44336',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    marginBottom: '2px',
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        {/* Actions sub-list */}
                        <div
                            style={{
                                paddingLeft: '8px',
                                borderLeft: '3px solid #a5d6a7',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '6px',
                                }}
                            >
                                <label
                                    style={{
                                        fontSize: '0.8rem',
                                        color: '#388e3c',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    Actions (when condition met)
                                </label>
                                <button
                                    onClick={() =>
                                        addConditionAction(index, condIndex)
                                    }
                                    style={{
                                        padding: '3px 8px',
                                        background: '#388e3c',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '0.75rem',
                                    }}
                                >
                                    + Add Action
                                </button>
                            </div>
                            {(cond.actions || []).length === 0 && (
                                <div
                                    style={{
                                        color: '#bbb',
                                        fontSize: '0.75rem',
                                        fontStyle: 'italic',
                                    }}
                                >
                                    No actions
                                </div>
                            )}
                            {(cond.actions || []).map((action, actionIndex) => (
                                <div
                                    key={actionIndex}
                                    style={{
                                        marginBottom: '6px',
                                        padding: '6px',
                                        background: '#f1f8e9',
                                        borderRadius: '4px',
                                        border: '1px solid #c8e6c9',
                                    }}
                                >
                                    {/* Output + Image + Remove row */}
                                    <div
                                        style={{
                                            display: 'flex',
                                            gap: '8px',
                                            alignItems: 'flex-end',
                                        }}
                                    >
                                        <div style={{ flex: 1 }}>
                                            <label
                                                style={{
                                                    display: 'block',
                                                    fontSize: '0.75rem',
                                                    color: '#555',
                                                    marginBottom: '2px',
                                                }}
                                            >
                                                Target Output
                                            </label>
                                            <select
                                                value={action.outputId || ''}
                                                onChange={(e) =>
                                                    updateConditionAction(
                                                        index,
                                                        condIndex,
                                                        actionIndex,
                                                        'outputId',
                                                        e.target.value
                                                    )
                                                }
                                                style={{
                                                    width: '100%',
                                                    padding: '6px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ccc',
                                                    fontSize: '0.85rem',
                                                }}
                                            >
                                                <option value="">
                                                    -- Select Output --
                                                </option>
                                                {(formData.components || [])
                                                    .filter(
                                                        (c) =>
                                                            c.type ===
                                                                'output' ||
                                                            c.type ===
                                                                'custom_output'
                                                    )
                                                    .map((oc) => (
                                                        <option
                                                            key={oc.id}
                                                            value={oc.id}
                                                        >
                                                            {oc.name || oc.id}{' '}
                                                            (Port:{' '}
                                                            {
                                                                PORT_NAME_MAP[
                                                                    oc.port
                                                                ]
                                                            }
                                                            )
                                                        </option>
                                                    ))}
                                            </select>
                                        </div>
                                        <div style={{ flex: 2 }}>
                                            <ImageUploadField
                                                label="Target Image"
                                                value={action.targetImage || ''}
                                                onChange={(val) =>
                                                    updateConditionAction(
                                                        index,
                                                        condIndex,
                                                        actionIndex,
                                                        'targetImage',
                                                        val
                                                    )
                                                }
                                            />
                                        </div>
                                        <button
                                            onClick={() =>
                                                removeConditionAction(
                                                    index,
                                                    condIndex,
                                                    actionIndex
                                                )
                                            }
                                            style={{
                                                padding: '6px 10px',
                                                background: '#f44336',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                flexShrink: 0,
                                                marginBottom: '2px',
                                            }}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        )
    }

    const renderStatesEditor = (comp, index) => {
        const isStatesExpanded = expandedStates[comp.id]
        return (
            <div
                style={{
                    padding: '10px',
                    background: '#f0f0f0',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                }}
            >
                <div
                    onClick={() =>
                        setExpandedStates((prev) => ({
                            ...prev,
                            [comp.id]: !prev[comp.id],
                        }))
                    }
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        marginBottom: isStatesExpanded ? '10px' : '0',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                            'rgba(0, 0, 0, 0.16)'
                        e.currentTarget.style.boxShadow =
                            '0 0 40px rgba(0, 0, 0, 0.5)'
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                            'rgba(0, 0, 0, 0)'
                        e.currentTarget.style.boxShadow = 'none'
                    }}
                >
                    <h4 style={{ margin: 0, color: '#1a73e8' }}>
                        States & Conditions
                    </h4>
                    <span
                        style={{
                            fontSize: '0.8rem',
                            color: '#555',
                            transition: 'transform 0.3s ease',
                            transform: isStatesExpanded
                                ? 'rotate(180deg)'
                                : 'rotate(0deg)',
                        }}
                    >
                        ▼
                    </span>
                </div>

                {isStatesExpanded && (
                    <div>
                        {(comp.properties.states || []).length === 0 && (
                            <span style={{ fontSize: '0.8rem', color: '#888' }}>
                                No states added. Default image will be used.
                            </span>
                        )}

                        {(comp.properties.states || []).map((state, sIdx) => (
                            <div
                                key={sIdx}
                                style={{
                                    marginBottom: '15px',
                                    background: '#fff',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    padding: '10px',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '10px',
                                        marginBottom: '10px',
                                    }}
                                >
                                    <input
                                        type="text"
                                        placeholder="State Name (e.g. LED On)"
                                        value={state.name || ''}
                                        onChange={(e) =>
                                            handleArrayItemUpdate(
                                                index,
                                                'states',
                                                sIdx,
                                                'name',
                                                e.target.value
                                            )
                                        }
                                        style={{
                                            flex: 1,
                                            padding: '6px',
                                            borderRadius: '4px',
                                            border: '1px solid #ccc',
                                        }}
                                    />
                                    <button
                                        onClick={() =>
                                            handleArrayItemRemove(
                                                index,
                                                'states',
                                                sIdx
                                            )
                                        }
                                        style={{
                                            color: 'white',
                                            background: '#ef4444',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '0 10px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Delete
                                    </button>
                                </div>

                                <ImageUploadField
                                    label="State Image"
                                    value={state.image}
                                    onChange={(val) =>
                                        handleArrayItemUpdate(
                                            index,
                                            'states',
                                            sIdx,
                                            'image',
                                            val
                                        )
                                    }
                                />

                                {/* Condition Builder — OR / AND blocks */}
                                <div
                                    style={{
                                        marginTop: '10px',
                                        padding: '10px',
                                        background: '#f8f9fa',
                                        borderRadius: '4px',
                                        borderLeft: '3px solid #fbbc04',
                                    }}
                                >
                                    <strong style={{ fontSize: '0.85rem' }}>
                                        Show this state when:
                                    </strong>

                                    {(state.conditionBlocks || [[]]).map(
                                        (block, bIdx) => (
                                            <div key={bIdx}>
                                                {/* OR divider between blocks */}
                                                {bIdx > 0 && (
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            gap: '8px',
                                                            margin: '10px 0',
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                flex: 1,
                                                                height: '1px',
                                                                background:
                                                                    '#ccc',
                                                            }}
                                                        />
                                                        <span
                                                            style={{
                                                                fontSize:
                                                                    '0.75rem',
                                                                fontWeight:
                                                                    'bold',
                                                                color: '#e65100',
                                                                padding:
                                                                    '2px 8px',
                                                                background:
                                                                    '#fff3e0',
                                                                border: '1px solid #e65100',
                                                                borderRadius:
                                                                    '10px',
                                                            }}
                                                        >
                                                            OR
                                                        </span>
                                                        <div
                                                            style={{
                                                                flex: 1,
                                                                height: '1px',
                                                                background:
                                                                    '#ccc',
                                                            }}
                                                        />
                                                    </div>
                                                )}

                                                {/* AND block container */}
                                                <div
                                                    style={{
                                                        padding: '8px',
                                                        background: '#eef2f7',
                                                        borderRadius: '4px',
                                                        border: '1px solid #d0d8e4',
                                                        marginTop:
                                                            bIdx === 0
                                                                ? '8px'
                                                                : '0',
                                                    }}
                                                >
                                                    {/* Header row: "All of:" + remove block */}
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent:
                                                                'space-between',
                                                            alignItems:
                                                                'center',
                                                            marginBottom: '6px',
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                fontSize:
                                                                    '0.75rem',
                                                                color: '#555',
                                                                fontWeight:
                                                                    'bold',
                                                            }}
                                                        >
                                                            All of (AND):
                                                        </span>
                                                        <button
                                                            onClick={() =>
                                                                removeConditionBlock(
                                                                    index,
                                                                    sIdx,
                                                                    bIdx
                                                                )
                                                            }
                                                            title="Remove this OR block"
                                                            style={{
                                                                fontSize:
                                                                    '0.75rem',
                                                                color: '#c0392b',
                                                                background:
                                                                    'none',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            ✕ Remove Block
                                                        </button>
                                                    </div>

                                                    {/* Conditions inside this block */}
                                                    {block.map((cond, cIdx) => {
                                                        const inputComps =
                                                            formData.components.filter(
                                                                (c) =>
                                                                    CATEGORIES.input.includes(
                                                                        c.type
                                                                    )
                                                            )
                                                        const selType =
                                                            cond.inputType || ''
                                                        const isBtn =
                                                            BUTTON_INPUT_TYPES.includes(
                                                                selType
                                                            )
                                                        const isSlider =
                                                            SLIDER_INPUT_TYPES.includes(
                                                                selType
                                                            )
                                                        const isRgb =
                                                            RGB_SLIDER_INPUT_TYPES.includes(
                                                                selType
                                                            )
                                                        return (
                                                            <div
                                                                key={cIdx}
                                                                style={{
                                                                    display:
                                                                        'flex',
                                                                    flexWrap:
                                                                        'wrap',
                                                                    gap: '5px',
                                                                    marginBottom:
                                                                        '6px',
                                                                    padding:
                                                                        '6px',
                                                                    background:
                                                                        '#fff',
                                                                    borderRadius:
                                                                        '4px',
                                                                    border: '1px solid #e0e0e0',
                                                                    alignItems:
                                                                        'center',
                                                                }}
                                                            >
                                                                {/* Input selector */}
                                                                <select
                                                                    value={
                                                                        cond.inputId ||
                                                                        ''
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) => {
                                                                        // A single call is enough — updateStateCondition
                                                                        // rebuilds and scrubs the whole condition
                                                                        // when field === 'inputId'.
                                                                        updateStateCondition(
                                                                            index,
                                                                            sIdx,
                                                                            bIdx,
                                                                            cIdx,
                                                                            'inputId',
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }}
                                                                    style={{
                                                                        flex: 2,
                                                                        minWidth:
                                                                            '130px',
                                                                        padding:
                                                                            '4px',
                                                                    }}
                                                                >
                                                                    <option value="">
                                                                        --
                                                                        Select
                                                                        Input --
                                                                    </option>
                                                                    {inputComps.map(
                                                                        (
                                                                            ic
                                                                        ) => (
                                                                            <option
                                                                                key={
                                                                                    ic.id
                                                                                }
                                                                                value={
                                                                                    ic.id
                                                                                }
                                                                            >
                                                                                {COMPONENT_LABELS[
                                                                                    ic
                                                                                        .type
                                                                                ] ||
                                                                                    ic.type}{' '}
                                                                                (
                                                                                {ic
                                                                                    .properties
                                                                                    ?.label ||
                                                                                    ic.name ||
                                                                                    ic.id}

                                                                                )
                                                                                (ID:{' '}
                                                                                {
                                                                                    ic.id
                                                                                }

                                                                                )
                                                                            </option>
                                                                        )
                                                                    )}
                                                                </select>

                                                                {/* Button → ON/OFF */}
                                                                {isBtn && (
                                                                    <select
                                                                        value={
                                                                            cond.expectedStatus ||
                                                                            'ON'
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            updateStateCondition(
                                                                                index,
                                                                                sIdx,
                                                                                bIdx,
                                                                                cIdx,
                                                                                'expectedStatus',
                                                                                e
                                                                                    .target
                                                                                    .value
                                                                            )
                                                                        }
                                                                        style={{
                                                                            flex: 1,
                                                                            padding:
                                                                                '4px',
                                                                        }}
                                                                    >
                                                                        <option value="ON">
                                                                            is
                                                                            ON
                                                                        </option>
                                                                        <option value="OFF">
                                                                            is
                                                                            OFF
                                                                        </option>
                                                                    </select>
                                                                )}

                                                                {/* Slider → operator + value */}
                                                                {isSlider && (
                                                                    <>
                                                                        <select
                                                                            value={
                                                                                cond.operator ||
                                                                                '>'
                                                                            }
                                                                            onChange={(
                                                                                e
                                                                            ) =>
                                                                                updateStateCondition(
                                                                                    index,
                                                                                    sIdx,
                                                                                    bIdx,
                                                                                    cIdx,
                                                                                    'operator',
                                                                                    e
                                                                                        .target
                                                                                        .value
                                                                                )
                                                                            }
                                                                            style={{
                                                                                flex: 1,
                                                                                padding:
                                                                                    '4px',
                                                                            }}
                                                                        >
                                                                            {OPERATORS.map(
                                                                                (
                                                                                    op
                                                                                ) => (
                                                                                    <option
                                                                                        key={
                                                                                            op
                                                                                        }
                                                                                        value={
                                                                                            op
                                                                                        }
                                                                                    >
                                                                                        {
                                                                                            op
                                                                                        }
                                                                                    </option>
                                                                                )
                                                                            )}
                                                                        </select>
                                                                        <input
                                                                            type="number"
                                                                            placeholder="Value"
                                                                            value={
                                                                                cond.value ??
                                                                                0
                                                                            }
                                                                            onChange={(
                                                                                e
                                                                            ) =>
                                                                                updateStateCondition(
                                                                                    index,
                                                                                    sIdx,
                                                                                    bIdx,
                                                                                    cIdx,
                                                                                    'value',
                                                                                    e
                                                                                        .target
                                                                                        .value
                                                                                )
                                                                            }
                                                                            style={{
                                                                                flex: 1,
                                                                                padding:
                                                                                    '4px',
                                                                            }}
                                                                        />
                                                                    </>
                                                                )}

                                                                {/* RGB Control → channel + operator + value */}
                                                                {isRgb && (
                                                                    <>
                                                                        <select
                                                                            value={
                                                                                cond.channel ||
                                                                                'red'
                                                                            }
                                                                            onChange={(
                                                                                e
                                                                            ) =>
                                                                                updateStateCondition(
                                                                                    index,
                                                                                    sIdx,
                                                                                    bIdx,
                                                                                    cIdx,
                                                                                    'channel',
                                                                                    e
                                                                                        .target
                                                                                        .value
                                                                                )
                                                                            }
                                                                            style={{
                                                                                flex: 1,
                                                                                padding:
                                                                                    '4px',
                                                                            }}
                                                                        >
                                                                            <option value="red">
                                                                                Red
                                                                            </option>
                                                                            <option value="green">
                                                                                Green
                                                                            </option>
                                                                            <option value="blue">
                                                                                Blue
                                                                            </option>
                                                                        </select>
                                                                        <select
                                                                            value={
                                                                                cond.operator ||
                                                                                '>'
                                                                            }
                                                                            onChange={(
                                                                                e
                                                                            ) =>
                                                                                updateStateCondition(
                                                                                    index,
                                                                                    sIdx,
                                                                                    bIdx,
                                                                                    cIdx,
                                                                                    'operator',
                                                                                    e
                                                                                        .target
                                                                                        .value
                                                                                )
                                                                            }
                                                                            style={{
                                                                                flex: 1,
                                                                                padding:
                                                                                    '4px',
                                                                            }}
                                                                        >
                                                                            {OPERATORS.map(
                                                                                (
                                                                                    op
                                                                                ) => (
                                                                                    <option
                                                                                        key={
                                                                                            op
                                                                                        }
                                                                                        value={
                                                                                            op
                                                                                        }
                                                                                    >
                                                                                        {
                                                                                            op
                                                                                        }
                                                                                    </option>
                                                                                )
                                                                            )}
                                                                        </select>
                                                                        <input
                                                                            type="number"
                                                                            placeholder="Value"
                                                                            value={
                                                                                cond.value ??
                                                                                0
                                                                            }
                                                                            onChange={(
                                                                                e
                                                                            ) =>
                                                                                updateStateCondition(
                                                                                    index,
                                                                                    sIdx,
                                                                                    bIdx,
                                                                                    cIdx,
                                                                                    'value',
                                                                                    e
                                                                                        .target
                                                                                        .value
                                                                                )
                                                                            }
                                                                            style={{
                                                                                flex: 1,
                                                                                padding:
                                                                                    '4px',
                                                                            }}
                                                                        />
                                                                    </>
                                                                )}

                                                                <button
                                                                    onClick={() =>
                                                                        removeStateCondition(
                                                                            index,
                                                                            sIdx,
                                                                            bIdx,
                                                                            cIdx
                                                                        )
                                                                    }
                                                                    style={{
                                                                        color: 'red',
                                                                        background:
                                                                            'none',
                                                                        border: 'none',
                                                                        cursor: 'pointer',
                                                                        fontWeight:
                                                                            'bold',
                                                                    }}
                                                                >
                                                                    X
                                                                </button>
                                                            </div>
                                                        )
                                                    })}

                                                    {/* + Add Condition (AND) */}
                                                    <button
                                                        onClick={() =>
                                                            addStateCondition(
                                                                index,
                                                                sIdx,
                                                                bIdx
                                                            )
                                                        }
                                                        style={{
                                                            fontSize: '0.78rem',
                                                            padding: '2px 8px',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            marginTop: '4px',
                                                        }}
                                                    >
                                                        + Add Condition (AND)
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    )}

                                    {/* + Add OR Block */}
                                    <button
                                        onClick={() =>
                                            addConditionBlock(index, sIdx)
                                        }
                                        style={{
                                            fontSize: '0.8rem',
                                            padding: '4px 10px',
                                            marginTop: '10px',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            background: '#fff3e0',
                                            border: '1px solid #e65100',
                                            color: '#e65100',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        + Add OR Block
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button
                            onClick={() =>
                                handleArrayItemAdd(index, 'states', {
                                    name: '',
                                    image: '',
                                    conditionBlocks: [
                                        [
                                            {
                                                inputId: '',
                                                inputType: '',
                                                expectedStatus: 'ON',
                                            },
                                        ],
                                    ],
                                })
                            }
                            style={{
                                padding: '6px 12px',
                                marginTop: '5px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                            }}
                        >
                            + Add State
                        </button>
                    </div>
                )}
            </div>
        )
    }

    const renderComponentPropertiesForm = (comp, index) => {
        switch (comp.type) {
            case 'toki_board':
            case 'board':
                return (
                    <div
                        style={{
                            padding: '8px 10px',
                            background: '#f0f4ff',
                            borderRadius: '4px',
                            border: '1px solid #c5cae9',
                            fontSize: '0.82rem',
                            color: '#3c4a6e',
                        }}
                    >
                        Board image is fixed to <code>{boardImage}</code>
                    </div>
                )

            case 'image':
                return (
                    <>
                        <ImageUploadField
                            label="Image"
                            value={comp.properties.imageUrl || ''}
                            onChange={(val) =>
                                updateComponentProperty(index, 'imageUrl', val)
                            }
                        />
                        <div style={{ marginTop: '12px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    fontSize: '0.85rem',
                                    color: '#555',
                                    fontWeight: 'bold',
                                    marginBottom: '4px',
                                }}
                            >
                                Scale (0 - 100)
                            </label>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={comp.properties.scale ?? 1}
                                onChange={(e) =>
                                    updateComponentProperty(
                                        index,
                                        'scale',
                                        e.target.value === ''
                                            ? ''
                                            : Number(e.target.value)
                                    )
                                }
                                style={{
                                    width: '100%',
                                    padding: '6px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    boxSizing: 'border-box',
                                }}
                            />
                            <div
                                style={{
                                    fontSize: '0.75rem',
                                    color: '#666',
                                    marginTop: '4px',
                                }}
                            >
                                1 = default size (20vw). The image keeps its
                                original aspect ratio.
                            </div>
                        </div>
                    </>
                )

            case 'output':
                return (
                    <>
                        {/* --- NEW: Wire Configuration Box --- */}
                        <div
                            style={{
                                marginBottom: '15px',
                                padding: '10px',
                                background: '#e3f2fd',
                                borderRadius: '6px',
                                border: '1px solid #bbdefb',
                            }}
                        >
                            <label
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    color: '#1565c0',
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={comp.properties.showWire || false}
                                    onChange={(e) =>
                                        updateComponentProperty(
                                            index,
                                            'showWire',
                                            e.target.checked
                                        )
                                    }
                                />
                                Draw wire from board to this component
                            </label>

                            {comp.properties.showWire && (
                                <div style={{ marginTop: '10px' }}>
                                    <div style={{ marginTop: '10px' }}>
                                        <label
                                            style={{
                                                display: 'block',
                                                fontSize: '0.85rem',
                                                color: '#555',
                                                marginBottom: '4px',
                                            }}
                                        >
                                            Target Port
                                        </label>
                                        <div
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                borderRadius: '4px',
                                                border: '1px solid #ccc',
                                                background: '#f9f9f9',
                                                color: '#555',
                                                boxSizing: 'border-box',
                                            }}
                                        >
                                            {PORT_NAME_MAP[comp.port] ||
                                                comp.port ||
                                                'Not set'}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <ImageUploadField
                            label="Default Image"
                            value={comp.properties.defaultImage}
                            onChange={(val) =>
                                updateComponentProperty(
                                    index,
                                    'defaultImage',
                                    val
                                )
                            }
                        />
                    </>
                )

            case 'custom_output':
                return (
                    <>
                        {/* --- NEW: Wire Configuration Box --- */}
                        <div
                            style={{
                                marginBottom: '15px',
                                padding: '10px',
                                background: '#e3f2fd',
                                borderRadius: '6px',
                                border: '1px solid #bbdefb',
                            }}
                        >
                            <label
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    color: '#1565c0',
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={comp.properties.showWire || false}
                                    onChange={(e) =>
                                        updateComponentProperty(
                                            index,
                                            'showWire',
                                            e.target.checked
                                        )
                                    }
                                />
                                Draw wire from board to this component
                            </label>

                            {comp.properties.showWire && (
                                <div style={{ marginTop: '10px' }}>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: '0.85rem',
                                            color: '#555',
                                            marginBottom: '4px',
                                        }}
                                    >
                                        Board Port (e.g. S1, M1, A1)
                                    </label>
                                    <select
                                        value={comp.port || ''}
                                        onChange={(e) =>
                                            updateComponent(
                                                index,
                                                'port',
                                                e.target.value
                                            )
                                        }
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            borderRadius: '4px',
                                            border: '1px solid #ccc',
                                            background: '#fff',
                                        }}
                                    >
                                        <option value="" disabled>
                                            Select Port...
                                        </option>
                                        {boardPorts.map((p) => (
                                            <option key={p} value={p}>
                                                {PORT_NAME_MAP[p] || p}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        <ImageUploadField
                            label="Default Image"
                            value={comp.properties.defaultImage}
                            onChange={(val) =>
                                updateComponentProperty(
                                    index,
                                    'defaultImage',
                                    val
                                )
                            }
                        />
                    </>
                )

            case 'model':
                return (
                    <>
                        <ModelUploadField
                            label="Model URL (.glb file)"
                            value={comp.modelUrl}
                            onChange={(val) => {
                                updateComponent(index, 'modelUrl', val)
                                setAnimSelectionContext({
                                    type: 'main',
                                    index: null,
                                })
                                setTempModelUrl(val)
                                isNewUploadRef.current = true
                            }}
                        />

                        {/* Set Orientation button */}
                        {comp.modelUrl && (
                            <div
                                style={{
                                    marginBottom: '15px',
                                    display: 'flex',
                                    gap: '8px',
                                }}
                            >
                                <button
                                    onClick={() => {
                                        setOrientationModalContext({
                                            type: 'main',
                                            interactionIndex: null,
                                        })
                                        setOrientationModalOpen(true)
                                    }}
                                    style={{
                                        padding: '6px 12px',
                                        background: '#ff9800',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    Set Orientation
                                </button>
                            </div>
                        )}
                        {/* Orientation info */}
                        {comp.cameraOrbit && (
                            <div
                                style={{
                                    marginBottom: '10px',
                                    fontSize: '0.85rem',
                                    color: '#555',
                                }}
                            >
                                Camera View: Horizontal=
                                {comp.cameraOrbit.theta}° Vertical=
                                {comp.cameraOrbit.phi}° Zoom=
                                {comp.cameraOrbit.radius}%
                            </div>
                        )}
                    </>
                )

            case 'button_control':
            case 'tack_button_control':
            case 'hold_button_control':
            case 'trigger_button_control': {
                const rgbOn = comp.properties.onValue || {
                    r: 100,
                    g: 100,
                    b: 100,
                }
                const rgbOff = comp.properties.offValue || { r: 0, g: 0, b: 0 }
                const updateRgbValue = (key, channel, val) => {
                    const current =
                        comp.properties[key] ||
                        (key === 'onValue'
                            ? { r: 100, g: 100, b: 100 }
                            : { r: 0, g: 0, b: 0 })
                    updateComponentProperty(index, key, {
                        ...current,
                        [channel]: val,
                    })
                }
                return (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                        }}
                    >
                        <ImageUploadField
                            label="ON Image (Active)"
                            value={comp.properties.onImage}
                            onChange={(val) =>
                                updateComponentProperty(index, 'onImage', val)
                            }
                        />
                        <ImageUploadField
                            label="OFF Image (Inactive)"
                            value={comp.properties.offImage}
                            onChange={(val) =>
                                updateComponentProperty(index, 'offImage', val)
                            }
                        />

                        <div style={{ marginBottom: '10px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    fontSize: '0.85rem',
                                    color: '#555',
                                }}
                            >
                                Text Label
                            </label>
                            <input
                                type="text"
                                value={comp.properties.label || ''}
                                onChange={(e) =>
                                    updateComponentProperty(
                                        index,
                                        'label',
                                        e.target.value
                                    )
                                }
                                style={{ width: '100%', padding: '6px' }}
                            />
                        </div>
                        {/* Live Bytes – tack & hold buttons only */}
                        {(comp.type === 'tack_button_control' ||
                            comp.type === 'hold_button_control') && (
                            <div
                                style={{
                                    marginBottom: '10px',
                                    padding: '10px',
                                    background: '#e8f5e9',
                                    borderRadius: '6px',
                                    border: '1px solid #a5d6a7',
                                }}
                            >
                                <label
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontSize: '0.85rem',
                                        color: '#2e7d32',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={
                                            !!comp.properties.liveBytesEnabled
                                        }
                                        onChange={(e) =>
                                            updateComponentProperty(
                                                index,
                                                'liveBytesEnabled',
                                                e.target.checked
                                            )
                                        }
                                    />
                                    Live Bytes
                                </label>
                                {comp.properties.liveBytesEnabled && (
                                    <div style={{ marginTop: '8px' }}>
                                        <input
                                            type="text"
                                            value={
                                                comp.properties.liveBytes || ''
                                            }
                                            onChange={(e) =>
                                                updateComponentProperty(
                                                    index,
                                                    'liveBytes',
                                                    e.target.value.replace(
                                                        /[^a-zA-Z0-9]/g,
                                                        ''
                                                    )
                                                )
                                            }
                                            placeholder="e.g. W1 or WOOF (letters & numbers only)"
                                            style={{
                                                width: '100%',
                                                padding: '6px',
                                                borderRadius: '4px',
                                                border: '1px solid #a5d6a7',
                                                boxSizing: 'border-box',
                                            }}
                                        />
                                        <div
                                            style={{
                                                fontSize: '0.75rem',
                                                color: '#666',
                                                marginTop: '4px',
                                            }}
                                        >
                                            {comp.type === 'hold_button_control'
                                                ? 'Sent repeatedly (every 100ms) while the button is held down.'
                                                : 'Sent once each time the button is pressed.'}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {/* RGB Values – only when component name is RGB */}
                        {(comp.name || '').toLowerCase() === 'rgb' && (
                            <>
                                {/* <div style={{ marginBottom: '10px' }}>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: '0.85rem',
                                            color: '#555',
                                            fontWeight: 'bold',
                                            marginBottom: '4px',
                                        }}
                                    >
                                        ON Value (RGB sent to device)
                                    </label>
                                    <div
                                        style={{ display: 'flex', gap: '8px' }}
                                    >
                                        {['r', 'g', 'b'].map((ch) => (
                                            <div key={ch} style={{ flex: 1 }}>
                                                <label
                                                    style={{
                                                        fontSize: '0.75rem',
                                                        color:
                                                            ch === 'r'
                                                                ? '#d32f2f'
                                                                : ch === 'g'
                                                                  ? '#388e3c'
                                                                  : '#1565c0',
                                                    }}
                                                >
                                                    {ch.toUpperCase()}
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={rgbOn[ch] ?? 0}
                                                    onChange={(e) =>
                                                        updateRgbValue(
                                                            'onValue',
                                                            ch,
                                                            Number(
                                                                e.target.value
                                                            )
                                                        )
                                                    }
                                                    style={{
                                                        width: '100%',
                                                        padding: '6px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #ccc',
                                                        boxSizing: 'border-box',
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div style={{ marginBottom: '10px' }}>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: '0.85rem',
                                            color: '#555',
                                            fontWeight: 'bold',
                                            marginBottom: '4px',
                                        }}
                                    >
                                        OFF Value (RGB sent to device)
                                    </label>
                                    <div
                                        style={{ display: 'flex', gap: '8px' }}
                                    >
                                        {['r', 'g', 'b'].map((ch) => (
                                            <div key={ch} style={{ flex: 1 }}>
                                                <label
                                                    style={{
                                                        fontSize: '0.75rem',
                                                        color:
                                                            ch === 'r'
                                                                ? '#d32f2f'
                                                                : ch === 'g'
                                                                  ? '#388e3c'
                                                                  : '#1565c0',
                                                    }}
                                                >
                                                    {ch.toUpperCase()}
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={rgbOff[ch] ?? 0}
                                                    onChange={(e) =>
                                                        updateRgbValue(
                                                            'offValue',
                                                            ch,
                                                            Number(
                                                                e.target.value
                                                            )
                                                        )
                                                    }
                                                    style={{
                                                        width: '100%',
                                                        padding: '6px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #ccc',
                                                        boxSizing: 'border-box',
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div> */}
                            </>
                        )}
                        {/* ⚡ Events Section */}
                        <div
                            style={{
                                marginTop: '15px',
                                padding: '12px',
                                background: '#f3e5f5',
                                borderRadius: '6px',
                                border: '1px solid #ce93d8',
                            }}
                        >
                            <strong
                                style={{
                                    color: '#6a1b9a',
                                    fontSize: '0.9rem',
                                    display: 'block',
                                    marginBottom: '10px',
                                }}
                            >
                                ⚡ Events
                            </strong>
                            {(comp.type === 'trigger_button_control'
                                ? ['onTrigger']
                                : ['onON', 'onOFF']
                            ).map((eventKey) => (
                                <div
                                    key={eventKey}
                                    style={{ marginBottom: '14px' }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '6px',
                                        }}
                                    >
                                        <label
                                            style={{
                                                fontWeight: 'bold',
                                                fontSize: '0.85rem',
                                                color:
                                                    eventKey === 'onON'
                                                        ? '#2e7d32'
                                                        : eventKey === 'onOFF'
                                                          ? '#c62828'
                                                          : '#6a1b9a',
                                            }}
                                        >
                                            {eventKey === 'onON'
                                                ? '🟢 When turned ON'
                                                : eventKey === 'onOFF'
                                                  ? '🔴 When turned OFF'
                                                  : '▶ When Triggered'}
                                        </label>
                                        <button
                                            onClick={() =>
                                                addButtonAction(index, eventKey)
                                            }
                                            style={{
                                                padding: '4px 10px',
                                                background: '#8e24aa',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '0.8rem',
                                            }}
                                        >
                                            + Add Action
                                        </button>
                                    </div>
                                    {(comp.properties.events?.[eventKey] || [])
                                        .length === 0 && (
                                        <div
                                            style={{
                                                color: '#bbb',
                                                fontSize: '0.8rem',
                                                fontStyle: 'italic',
                                                padding: '4px',
                                            }}
                                        >
                                            No actions yet
                                        </div>
                                    )}
                                    {(
                                        comp.properties.events?.[eventKey] || []
                                    ).map((action, actionIndex) => (
                                        <div
                                            key={actionIndex}
                                            style={{
                                                marginBottom: '6px',
                                                padding: '8px',
                                                background: '#fce4ec',
                                                borderRadius: '4px',
                                                border: '1px solid #f48fb1',
                                            }}
                                        >
                                            {/* Output + Image + Remove */}
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: '8px',
                                                    alignItems: 'flex-end',
                                                }}
                                            >
                                                <div style={{ flex: 1 }}>
                                                    <label
                                                        style={{
                                                            display: 'block',
                                                            fontSize: '0.75rem',
                                                            color: '#555',
                                                            marginBottom: '2px',
                                                        }}
                                                    >
                                                        Target Output
                                                    </label>
                                                    <select
                                                        value={
                                                            action.outputId ||
                                                            ''
                                                        }
                                                        onChange={(e) =>
                                                            updateButtonAction(
                                                                index,
                                                                eventKey,
                                                                actionIndex,
                                                                'outputId',
                                                                e.target.value
                                                            )
                                                        }
                                                        style={{
                                                            width: '100%',
                                                            padding: '6px',
                                                            borderRadius: '4px',
                                                            border: '1px solid #ccc',
                                                            fontSize: '0.85rem',
                                                        }}
                                                    >
                                                        <option value="">
                                                            -- Select Output --
                                                        </option>
                                                        {(
                                                            formData.components ||
                                                            []
                                                        )
                                                            .filter(
                                                                (c) =>
                                                                    c.type ===
                                                                        'output' ||
                                                                    c.type ===
                                                                        'custom_output'
                                                            )
                                                            .map((oc) => (
                                                                <option
                                                                    key={oc.id}
                                                                    value={
                                                                        oc.id
                                                                    }
                                                                >
                                                                    {oc.name ||
                                                                        oc.id}{' '}
                                                                    (Port:{' '}
                                                                    {
                                                                        PORT_NAME_MAP[
                                                                            oc
                                                                                .port
                                                                        ]
                                                                    }
                                                                    )
                                                                </option>
                                                            ))}
                                                    </select>
                                                </div>
                                                <div style={{ flex: 2 }}>
                                                    <ImageUploadField
                                                        label="Target Image"
                                                        value={
                                                            action.targetImage ||
                                                            ''
                                                        }
                                                        onChange={(val) =>
                                                            updateButtonAction(
                                                                index,
                                                                eventKey,
                                                                actionIndex,
                                                                'targetImage',
                                                                val
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <button
                                                    onClick={() =>
                                                        removeButtonAction(
                                                            index,
                                                            eventKey,
                                                            actionIndex
                                                        )
                                                    }
                                                    style={{
                                                        padding: '6px 10px',
                                                        background: '#f44336',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        flexShrink: 0,
                                                        marginBottom: '2px',
                                                    }}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                        {renderButtonModelAnimationsSection(comp, index)}
                    </div>
                )
            }

            case 'slider_control':
                return (
                    <>
                        <div style={{ marginBottom: '10px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    fontSize: '0.85rem',
                                    color: '#555',
                                }}
                            >
                                Text Label
                            </label>
                            <input
                                type="text"
                                value={comp.properties.label || ''}
                                onChange={(e) =>
                                    updateComponentProperty(
                                        index,
                                        'label',
                                        e.target.value
                                    )
                                }
                                style={{ width: '100%', padding: '6px' }}
                            />
                        </div>
                        {renderConditionsEditor(comp, index)}
                        {renderSliderModelAnimationsSection(comp, index)}
                    </>
                )

            case 'custom_slider_control':
                return (
                    // <div
                    //     style={{
                    //         padding: '10px',
                    //         background: '#e3f2fd',
                    //         color: '#1565c0',
                    //         borderRadius: '6px',
                    //         fontSize: '0.9rem',
                    //         fontStyle: 'italic',
                    //     }}
                    // >
                    //     Slider UI properties (rangImgName, title) are locked to
                    //     the standard format. Just set the mapping variable
                    //     above!
                    // </div>
                    <div></div>
                )

            case 'rgb_control':
                return (
                    <>
                        {/* <ImageUploadField
                            label="Background/Device Image"
                            value={comp.properties.bgImage}
                            onChange={(val) =>
                                updateComponentProperty(index, 'bgImage', val)
                            }
                        /> */}
                        <div style={{ marginBottom: '10px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    fontSize: '0.85rem',
                                    color: '#555',
                                }}
                            >
                                Text Label
                            </label>
                            <input
                                type="text"
                                value={comp.properties.label || ''}
                                onChange={(e) =>
                                    updateComponentProperty(
                                        index,
                                        'label',
                                        e.target.value
                                    )
                                }
                                style={{ width: '100%', padding: '6px' }}
                            />
                        </div>
                        {renderConditionsEditor(comp, index)}
                        {renderSliderModelAnimationsSection(comp, index)}
                    </>
                )

            default:
                return null
        }
    }

    // A unified Content renderer so it doesn't clutter the JSX directly
    const renderCardContent = (comp, compIndex, isInput = false) => {
        return (
            <div
                style={{
                    marginTop: '15px',
                    paddingTop: '15px',
                    borderTop: '2px solid #f0f0f0',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '15px',
                        background: '#f9f9f9',
                        padding: '10px',
                        borderRadius: '4px',
                    }}
                >
                    <div style={{ flex: 1 }}>
                        <label
                            style={{
                                display: 'block',
                                fontSize: '0.85rem',
                                color: '#555',
                                fontWeight: 'bold',
                            }}
                        >
                            Pos X (0-1)
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            value={comp.position.x}
                            onChange={(e) =>
                                updateComponentPosition(
                                    compIndex,
                                    'x',
                                    e.target.value
                                )
                            }
                            style={{
                                width: '100%',
                                padding: '6px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                            }}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label
                            style={{
                                display: 'block',
                                fontSize: '0.85rem',
                                color: '#555',
                                fontWeight: 'bold',
                            }}
                        >
                            Pos Y (0-1)
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            value={comp.position.y}
                            onChange={(e) =>
                                updateComponentPosition(
                                    compIndex,
                                    'y',
                                    e.target.value
                                )
                            }
                            style={{
                                width: '100%',
                                padding: '6px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                            }}
                        />
                    </div>
                </div>

                {isInput && (
                    <div style={{ marginBottom: '15px' }}>
                        <label
                            style={{
                                display: 'block',
                                fontSize: '0.85rem',
                                color: '#555',
                                fontWeight: 'bold',
                                marginBottom: '4px',
                            }}
                        >
                            Scale (0.1 - 100)
                        </label>
                        <input
                            type="number"
                            min="0.1"
                            max="100"
                            step="0.1"
                            value={comp.properties.scale ?? 1}
                            onChange={(e) =>
                                updateComponentProperty(
                                    compIndex,
                                    'scale',
                                    e.target.value === ''
                                        ? ''
                                        : Number(e.target.value)
                                )
                            }
                            style={{
                                width: '100%',
                                padding: '6px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                boxSizing: 'border-box',
                            }}
                        />
                        <div
                            style={{
                                fontSize: '0.75rem',
                                color: '#666',
                                marginTop: '4px',
                            }}
                        >
                            1 = current size. Keeps the original aspect ratio.
                        </div>
                    </div>
                )}

                {isInput && (
                    <div style={{ marginBottom: '10px' }}>
                        <label
                            style={{
                                display: 'block',
                                fontSize: '0.85rem',
                                color: '#555',
                                fontWeight: 'bold',
                                marginBottom: '4px',
                            }}
                        >
                            Target Hardware Connection
                        </label>
                        {[
                            'slider_control',
                            'button_control',
                            'tack_button_control',
                            'hold_button_control',
                            'trigger_button_control',
                            'rgb_control',
                        ].includes(comp.type) ? (
                            <select
                                value={comp.name}
                                onChange={(e) => {
                                    const selectedComp = e.target.value
                                    const connection = assemblyConnections.find(
                                        (c) => c.component === selectedComp
                                    )

                                    setFormData((prev) => {
                                        const newComps = [...prev.components]
                                        newComps[compIndex] = {
                                            ...newComps[compIndex],
                                            name: selectedComp,
                                            port: connection
                                                ? connection.port
                                                : '',
                                        }
                                        return { ...prev, components: newComps }
                                    })
                                }}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    boxSizing: 'border-box',
                                }}
                            >
                                <option value="" disabled>
                                    Select Hardware...
                                </option>
                                {assemblyConnections
                                    .filter((conn) => {
                                        const name =
                                            conn.component.toLowerCase()
                                        // rgb_control only shows RGB hardware; all other types show all connections
                                        if (comp.type === 'rgb_control')
                                            return name.includes('rgb')
                                        return true
                                    })
                                    .map((conn, idx) => (
                                        <option
                                            key={idx}
                                            value={conn.component}
                                        >
                                            {conn.component.replace(/_/g, ' ')}{' '}
                                            (Port:{' '}
                                            {PORT_NAME_MAP[conn.port] ||
                                                conn.port}
                                            )
                                        </option>
                                    ))}
                            </select>
                        ) : (
                            <input
                                type="text"
                                value={comp.name}
                                onChange={(e) =>
                                    updateComponent(
                                        compIndex,
                                        'name',
                                        e.target.value
                                    )
                                }
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    boxSizing: 'border-box',
                                }}
                            />
                        )}
                    </div>
                )}

                <div style={{ marginTop: '15px' }}>
                    {renderComponentPropertiesForm(comp, compIndex)}
                </div>
            </div>
        )
    }

    // A unified toggleable card design used for Board, Outputs, and Models
    const renderToggleableCard = (
        title,
        subtitle,
        type,
        overrides,
        isIncluded,
        compIndex,
        comp
    ) => {
        const isExpanded = isIncluded ? expandedComps[comp.id] : false

        return (
            <div
                style={{
                    background: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '15px',
                    opacity: isIncluded ? 1 : 0.6,
                    transition: 'all 0.3s ease',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                        }}
                    >
                        <button
                            onClick={() =>
                                toggleComponent(
                                    type,
                                    overrides,
                                    isIncluded,
                                    comp?.id
                                )
                            }
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                    'rgba(0, 0, 0, 0.16)'
                                e.currentTarget.style.boxShadow =
                                    '0 0 40px rgba(0, 0, 0, 0.79)'
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                    'rgba(0, 0, 0, 0)'
                                e.currentTarget.style.boxShadow = 'none'
                            }}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: isIncluded ? '#1a73e8' : '#999',
                                display: 'flex',
                                alignItems: 'center',
                                padding: 0,
                                borderRadius: '4px',
                            }}
                            title={
                                isIncluded ? 'Hide Component' : 'Show Component'
                            }
                        >
                            {isIncluded ? <EyeVisible /> : <EyeHidden />}
                        </button>
                        <div
                            style={{
                                cursor: isIncluded ? 'pointer' : 'default',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}
                            onClick={() => {
                                if (isIncluded)
                                    setExpandedComps((prev) => ({
                                        ...prev,
                                        [comp.id]: !prev[comp.id],
                                    }))
                            }}
                        >
                            <span
                                style={{
                                    fontWeight: 'bold',
                                    fontSize: '1rem',
                                    color: isIncluded ? '#1a73e8' : '#555',
                                    textTransform: 'uppercase',
                                }}
                            >
                                {title}
                            </span>
                            {subtitle && (
                                <span
                                    style={{
                                        color: '#888',
                                        fontSize: '0.9rem',
                                    }}
                                >
                                    ({subtitle})
                                </span>
                            )}
                        </div>
                    </div>
                    {isIncluded && (
                        <button
                            onClick={() =>
                                setExpandedComps((prev) => ({
                                    ...prev,
                                    [comp.id]: !prev[comp.id],
                                }))
                            }
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#555',
                                fontSize: '0.9rem',
                                transition: 'transform 0.3s',
                                transform: isExpanded
                                    ? 'rotate(180deg)'
                                    : 'rotate(0deg)',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                    'rgba(0, 0, 0, 0.12)'
                                e.currentTarget.style.boxShadow =
                                    '0 0 40px rgba(0, 0, 0, 0.5)'
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                    'rgba(0, 0, 0, 0)'
                                e.currentTarget.style.boxShadow = 'none'
                            }}
                        >
                            ▼
                        </button>
                    )}
                </div>
                {isIncluded &&
                    isExpanded &&
                    renderCardContent(comp, compIndex, false)}
            </div>
        )
    }

    if (loading)
        return <div style={{ padding: '20px' }}>Loading Admin Panel...</div>
    if (!formData)
        return (
            <div style={{ padding: '20px' }}>
                Error loading data. Try logging in again.
            </div>
        )

    const activeStepData = formData.steps[activeStepIndex]

    const toTitleCase = (str) => {
        return str
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
    }

    const confirmAnimationSelection = () => {
        if (animSelectionContext.type === 'main') {
            // Animation is now per-step — save to the currently active step
            setFormData((prev) => {
                const newSteps = [...prev.steps]
                newSteps[activeStepIndex] = {
                    ...newSteps[activeStepIndex],
                    models: {
                        ...(newSteps[activeStepIndex]?.models || {}),
                        animationName: selectedAnimation,
                    },
                }
                return { ...prev, steps: newSteps }
            })
        } else if (animSelectionContext.type === 'interaction') {
            setFormData((prev) => {
                const newComps = [...prev.components]
                const modelIdx = newComps.findIndex((c) => c.type === 'model')
                if (modelIdx === -1) return prev
                const intIdx = animSelectionContext.index
                const interactions = [
                    ...(newComps[modelIdx].interactions || []),
                ]
                if (interactions[intIdx]) {
                    interactions[intIdx].targetAnimation = selectedAnimation
                }
                newComps[modelIdx] = {
                    ...newComps[modelIdx],
                    interactions,
                }
                return { ...prev, components: newComps }
            })
        }

        setShowAnimModal(false)
        setTempModelUrl('')
    }

    return (
        <div
            style={{
                padding: '20px 40px',
                maxWidth: '1200px',
                margin: '0 auto',
                fontFamily: 'sans-serif',
                height: '100vh',
                boxSizing: 'border-box',
                overflowY: 'auto',
            }}
        >
            {/* Subprogram heading row */}
            <div
                style={{
                    marginBottom: '12px',
                    padding: '10px 14px',
                    background:
                        'linear-gradient(90deg, #eef4ff 0%, #f8fbff 100%)',
                    border: '1px solid #dbe7ff',
                    borderRadius: '10px',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <div
                        style={{
                            fontSize: '0.9rem',
                            fontWeight: 700,
                            color: '#5b6b8c',
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                        }}
                    >
                        Current Project
                    </div>

                    <h1
                        style={{
                            margin: 0,
                            fontSize: '1.65rem',
                            fontWeight: 800,
                            color: '#1f3b77',
                            lineHeight: 1.2,
                        }}
                    >
                        {subprogramTitle}
                    </h1>
                </div>
            </div>

            {/* --- NEW: Assembly screen picker --- */}
            {assemblyScreens.length > 0 && (
                <div
                    style={{
                        marginBottom: '20px',
                        padding: '8px 14px',
                        background:
                            'linear-gradient(90deg, #fff4e6 0%, #fffbf5 100%)',
                        border: '1px solid #ffe0b2',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                    }}
                >
                    <label
                        style={{
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            color: '#8c6b3a',
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Assembly Screen ID (for hardware connections)
                    </label>
                    <select
                        style={{
                            flex: 1,
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #e0c89a',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: '#5b4a2a',
                            background: '#fff',
                        }}
                        value={selectedAssemblyScreen}
                        onChange={(e) =>
                            handleAssemblyScreenChange(e.target.value)
                        }
                    >
                        {assemblyScreens.map((screen) => (
                            <option key={screen} value={screen}>
                                {screen}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                }}
            >
                <h1 style={{ margin: 0, fontSize: '1.8rem' }}>Play Editor</h1>
                <div style={{ display: 'flex', gap: '10px' }}>
                    {/* --- NEW BUTTONS START HERE --- */}
                    {preMigrationBackup && (
                        <button
                            onClick={handleRevertMigration}
                            style={{
                                padding: '10px 20px',
                                background: '#ef4444', // Red for danger/revert
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                            }}
                        >
                            Undo Migration
                        </button>
                    )}

                    <button
                        onClick={handleMigrateIds}
                        disabled={!!preMigrationBackup} // Disable if already migrated
                        style={{
                            padding: '10px 20px',
                            background: preMigrationBackup ? '#ccc' : '#fbbc04', // Yellow/Warning color
                            color: preMigrationBackup ? '#666' : '#000',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: preMigrationBackup
                                ? 'not-allowed'
                                : 'pointer',
                            fontWeight: 'bold',
                        }}
                    >
                        Migrate Legacy IDs
                    </button>
                    {/* --- NEW BUTTONS END HERE --- */}

                    {/* NEW: Generate Voiceover Button */}
                    <button
                        onClick={handleGenerateVoiceover}
                        disabled={isGeneratingTTS}
                        style={{
                            padding: '10px 20px',
                            background: isGeneratingTTS ? '#9e9e9e' : '#ff9800', // Orange color for audio tools
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: isGeneratingTTS ? 'not-allowed' : 'pointer',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                        }}
                    >
                        {isGeneratingTTS ? (
                            'Generating...'
                        ) : (
                            <>
                                <AiOutlineAudio
                                    style={{
                                        fontSize: '22px',
                                        marginRight: '12px',
                                    }}
                                />{' '}
                                Generate Voiceover
                            </>
                        )}
                    </button>
                    <button
                        onClick={handlePreviewClick}
                        style={{
                            padding: '10px 20px',
                            background: '#6200ea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                        }}
                    >
                        Preview
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={hasEmptyConditionInputs}
                        title={
                            hasEmptyConditionInputs
                                ? 'Fix empty "Select Input" dropdowns in Success Validations before saving.'
                                : 'Save'
                        }
                        style={{
                            padding: '10px 20px',
                            background: hasEmptyConditionInputs
                                ? '#b0bec5'
                                : '#1a73e8',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: hasEmptyConditionInputs
                                ? 'not-allowed'
                                : 'pointer',
                            fontWeight: 'bold',
                            opacity: hasEmptyConditionInputs ? 0.7 : 1,
                        }}
                    >
                        <FaRegSave
                            style={{
                                fontSize: '22px',
                                marginRight: '12px',
                            }}
                        />
                        Save
                    </button>
                </div>
            </div>

            {/* GENERAL SETTINGS */}
            <div
                style={{
                    background: '#f4f4f9',
                    padding: '20px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '1px solid #e0e0e0',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        gap: '20px',
                        marginBottom: '15px',
                    }}
                >
                    <div style={{ flex: 2 }}>
                        <label
                            style={{
                                display: 'block',
                                fontWeight: 'bold',
                                marginBottom: '5px',
                            }}
                        >
                            Tutorial Title
                        </label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) =>
                                handleGeneralChange('title', e.target.value)
                            }
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                            }}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label
                            style={{
                                display: 'block',
                                fontWeight: 'bold',
                                marginBottom: '5px',
                            }}
                        >
                            Total Steps
                        </label>
                        <input
                            type="number"
                            min="0"
                            value={formData.totalSteps}
                            onChange={handleTotalStepsChange}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* SEGMENTED COMPONENTS EDITOR */}
            <div style={{ marginBottom: '30px' }}>
                <h2
                    style={{
                        marginBottom: '20px',
                        borderBottom: '2px solid #ccc',
                        paddingBottom: '10px',
                    }}
                >
                    Components Setup
                </h2>

                {/* 1. Board Section */}
                <div style={{ marginBottom: '30px' }}>
                    <h3 style={{ color: '#333', marginBottom: '15px' }}>
                        1. Board
                    </h3>
                    {(() => {
                        const boardComp = formData.components.find((c) =>
                            CATEGORIES.board.includes(c.type)
                        )
                        const isIncluded = !!boardComp
                        const compIndex = boardComp
                            ? formData.components.findIndex(
                                  (c) => c.id === boardComp.id
                              )
                            : -1
                        return renderToggleableCard(
                            boardLabel,
                            '',
                            'board',
                            {},
                            isIncluded,
                            compIndex,
                            boardComp
                        )
                    })()}
                </div>

                {/* 2. Outputs Section */}
                <div style={{ marginBottom: '30px' }}>
                    <h3 style={{ color: '#333', marginBottom: '15px' }}>
                        2. Outputs
                    </h3>
                    {assemblyConnections.length === 0 && (
                        <p
                            style={{
                                color: '#888',
                                fontStyle: 'italic',
                                background: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '6px',
                            }}
                        >
                            No hardware connected in Assembly. Go to the
                            Assembly screen and add some connection steps.
                        </p>
                    )}
                    {assemblyConnections.map((conn, idx) => {
                        const outputComp = formData.components.find(
                            (c) =>
                                c.type === 'output' &&
                                c.name === conn.component &&
                                c.port === conn.port
                        )
                        const isIncluded = !!outputComp
                        const compIndex = outputComp
                            ? formData.components.findIndex(
                                  (c) => c.id === outputComp.id
                              )
                            : -1
                        return (
                            <div key={idx}>
                                {renderToggleableCard(
                                    'Output Component',
                                    `${conn.component.replace(/_/g, ' ')} (Port: ${PORT_NAME_MAP[conn.port] || conn.port})`,
                                    'output',
                                    { name: conn.component, port: conn.port },
                                    isIncluded,
                                    compIndex,
                                    outputComp
                                )}
                            </div>
                        )
                    })}
                </div>

                {/* 3. Inputs Section */}
                <div style={{ marginBottom: '30px' }}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '15px',
                        }}
                    >
                        <h3 style={{ color: '#333', margin: 0 }}>3. Inputs</h3>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <select
                                value={pendingType}
                                onChange={(e) => setPendingType(e.target.value)}
                                style={{
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                }}
                            >
                                <option value="">+ Choose Input...</option>
                                {CATEGORIES.input.map((type) => (
                                    <option key={type} value={type}>
                                        {COMPONENT_LABELS[type]}
                                    </option>
                                ))}
                            </select>
                            <button
                                onClick={() => {
                                    if (pendingType) {
                                        toggleComponent(
                                            pendingType,
                                            {},
                                            false,
                                            null
                                        )
                                        setPendingType('')
                                    }
                                }}
                                disabled={!pendingType}
                                style={{
                                    padding: '8px 20px',
                                    background: !pendingType
                                        ? '#ccc'
                                        : '#6200ea',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: !pendingType
                                        ? 'not-allowed'
                                        : 'pointer',
                                    fontWeight: 'bold',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                Add
                            </button>
                        </div>
                    </div>

                    {formData.components
                        .filter((c) => CATEGORIES.input.includes(c.type))
                        .map((comp) => {
                            const compIndex = formData.components.findIndex(
                                (c) => c.id === comp.id
                            )
                            const isExpanded = expandedComps[comp.id]
                            return (
                                <div
                                    key={comp.id}
                                    style={{
                                        background: '#fff',
                                        border: '1px solid #ccc',
                                        borderRadius: '8px',
                                        padding: '15px',
                                        marginBottom: '15px',
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() =>
                                                setExpandedComps((prev) => ({
                                                    ...prev,
                                                    [comp.id]: !prev[comp.id],
                                                }))
                                            }
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor =
                                                    'rgba(0, 0, 0, 0.16)'
                                                e.currentTarget.style.boxShadow =
                                                    '0 0 40px rgba(0, 0, 0, 0.5)'
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor =
                                                    'rgba(0, 0, 0, 0)'
                                                e.currentTarget.style.boxShadow =
                                                    'none'
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: '0.9rem',
                                                    color: '#555',
                                                    transition:
                                                        'transform 0.3s ease',
                                                    transform: isExpanded
                                                        ? 'rotate(180deg)'
                                                        : 'rotate(0deg)',
                                                }}
                                            >
                                                ▼
                                            </span>
                                            <span
                                                style={{
                                                    fontWeight: 'bold',
                                                    fontSize: '1rem',
                                                    color: '#1a73e8',
                                                    textTransform: 'uppercase',
                                                }}
                                            >
                                                {COMPONENT_LABELS[comp.type] ||
                                                    comp.type}
                                                {comp.name && (
                                                    <span
                                                        style={{
                                                            color: '#767676',
                                                            textTransform:
                                                                'none',
                                                            fontWeight:
                                                                'normal',
                                                            marginLeft: '6px',
                                                        }}
                                                    >
                                                        ({comp.name})
                                                    </span>
                                                )}
                                                {comp.id && (
                                                    <span
                                                        style={{
                                                            color: '#c42020',
                                                            textTransform:
                                                                'none',
                                                            fontWeight:
                                                                'normal',
                                                            marginLeft: '6px',
                                                        }}
                                                    >
                                                        (ID: {comp.id})
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                removeComponent(comp.id)
                                            }}
                                            style={{
                                                background: '#ffebee',
                                                border: 'none',
                                                color: '#d32f2f',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                    {isExpanded &&
                                        renderCardContent(
                                            comp,
                                            compIndex,
                                            true
                                        )}
                                </div>
                            )
                        })}
                </div>

                {/* 4. Images Section */}
                <div style={{ marginBottom: '30px' }}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '15px',
                        }}
                    >
                        <h3 style={{ color: '#333', margin: 0 }}>4. Images</h3>
                        <button
                            onClick={() => addComponent('image')}
                            style={{
                                padding: '8px 20px',
                                background: '#6200ea',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                transition: 'all 0.2s ease',
                            }}
                        >
                            + Add Image
                        </button>
                    </div>

                    {formData.components
                        .filter((c) => c.type === 'image')
                        .map((comp) => {
                            const compIndex = formData.components.findIndex(
                                (c) => c.id === comp.id
                            )
                            const isExpanded = expandedComps[comp.id]
                            return (
                                <div
                                    key={comp.id}
                                    style={{
                                        background: '#fff',
                                        border: '1px solid #ccc',
                                        borderRadius: '8px',
                                        padding: '15px',
                                        marginBottom: '15px',
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() =>
                                                setExpandedComps((prev) => ({
                                                    ...prev,
                                                    [comp.id]: !prev[comp.id],
                                                }))
                                            }
                                        >
                                            <span
                                                style={{
                                                    fontSize: '0.9rem',
                                                    color: '#555',
                                                    transition:
                                                        'transform 0.3s ease',
                                                    transform: isExpanded
                                                        ? 'rotate(180deg)'
                                                        : 'rotate(0deg)',
                                                }}
                                            >
                                                ▼
                                            </span>
                                            <span
                                                style={{
                                                    fontWeight: 'bold',
                                                    fontSize: '1rem',
                                                    color: '#1a73e8',
                                                    textTransform: 'uppercase',
                                                }}
                                            >
                                                {COMPONENT_LABELS[comp.type] ||
                                                    comp.type}
                                                {comp.id && (
                                                    <span
                                                        style={{
                                                            color: '#c42020',
                                                            textTransform:
                                                                'none',
                                                            fontWeight:
                                                                'normal',
                                                            marginLeft: '6px',
                                                        }}
                                                    >
                                                        (ID: {comp.id})
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                removeComponent(comp.id)
                                            }}
                                            style={{
                                                background: '#ffebee',
                                                border: 'none',
                                                color: '#d32f2f',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                    {isExpanded &&
                                        renderCardContent(
                                            comp,
                                            compIndex,
                                            false
                                        )}
                                </div>
                            )
                        })}
                </div>

                {/* 5. 3D Animation Model */}
                <div style={{ marginBottom: '30px' }}>
                    <h3 style={{ color: '#333', marginBottom: '15px' }}>
                        5. 3D Animation Model
                    </h3>
                    {(() => {
                        const modelComp = formData.components.find(
                            (c) => c.type === 'model'
                        )
                        const isIncluded = !!modelComp
                        const compIndex = modelComp
                            ? formData.components.findIndex(
                                  (c) => c.id === modelComp.id
                              )
                            : -1
                        return renderToggleableCard(
                            '3D Animation Model',
                            '',
                            'model',
                            {},
                            isIncluded,
                            compIndex,
                            modelComp
                        )
                    })()}
                </div>
            </div>

            {/* STEPS EDITOR */}
            <div style={{ paddingBottom: '50px' }}>
                <h2 style={{ marginBottom: '10px' }}>Timeline Steps</h2>
                {formData.totalSteps === 0 ? (
                    <p style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                        Set "Total Steps" above to start adding step logic.
                    </p>
                ) : (
                    <div
                        style={{
                            border: '1px solid #ddd',
                            padding: '20px',
                            borderRadius: '8px',
                            background: '#fff',
                        }}
                    >
                        {/* Pagination Header */}
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '20px',
                                paddingBottom: '15px',
                                borderBottom: '1px solid #eee',
                            }}
                        >
                            <h3 style={{ margin: 0, color: '#6200ea' }}>
                                Step {activeStepData?.stepNumber}{' '}
                                <span
                                    style={{
                                        color: '#888',
                                        fontSize: '1rem',
                                        fontWeight: 'normal',
                                    }}
                                >
                                    of {formData.totalSteps}
                                </span>
                            </h3>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    onClick={() =>
                                        setActiveStepIndex((prev) =>
                                            Math.max(0, prev - 1)
                                        )
                                    }
                                    disabled={activeStepIndex === 0}
                                    style={{
                                        padding: '8px 16px',
                                        background:
                                            activeStepIndex === 0
                                                ? '#f0f0f0'
                                                : '#fff',
                                        border: '1px solid #ccc',
                                        borderRadius: '6px',
                                        cursor:
                                            activeStepIndex === 0
                                                ? 'not-allowed'
                                                : 'pointer',
                                    }}
                                >
                                    &larr; Previous
                                </button>
                                <button
                                    onClick={() =>
                                        setActiveStepIndex((prev) =>
                                            Math.min(
                                                formData.totalSteps - 1,
                                                prev + 1
                                            )
                                        )
                                    }
                                    disabled={
                                        activeStepIndex ===
                                        formData.totalSteps - 1
                                    }
                                    style={{
                                        padding: '8px 16px',
                                        background:
                                            activeStepIndex ===
                                            formData.totalSteps - 1
                                                ? '#f0f0f0'
                                                : '#fff',
                                        border: '1px solid #ccc',
                                        borderRadius: '6px',
                                        cursor:
                                            activeStepIndex ===
                                            formData.totalSteps - 1
                                                ? 'not-allowed'
                                                : 'pointer',
                                    }}
                                >
                                    Next &rarr;
                                </button>
                            </div>
                        </div>

                        {/* Step Text Details */}
                        <div
                            style={{
                                display: 'flex',
                                gap: '20px',
                                marginBottom: '20px',
                            }}
                        >
                            <div style={{ flex: 1 }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontWeight: 'bold',
                                        marginBottom: '5px',
                                    }}
                                >
                                    Step Title (Header)
                                </label>
                                <input
                                    type="text"
                                    value={activeStepData?.title || ''}
                                    onChange={(e) =>
                                        handleStepChange(
                                            'title',
                                            e.target.value
                                        )
                                    }
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '4px',
                                        border: '1px solid #ccc',
                                        boxSizing: 'border-box',
                                    }}
                                />
                            </div>
                            <div style={{ flex: 2 }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontWeight: 'bold',
                                        marginBottom: '5px',
                                    }}
                                >
                                    Instruction Text (Voiceover / Subtitles)
                                </label>
                                <textarea
                                    value={activeStepData?.instruction || ''}
                                    onChange={(e) =>
                                        handleStepChange(
                                            'instruction',
                                            e.target.value
                                        )
                                    }
                                    rows="3"
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '4px',
                                        border: '1px solid #ccc',
                                        resize: 'none',
                                        boxSizing: 'border-box',
                                    }}
                                />
                            </div>
                        </div>

                        {/* Component Visibilities, Enables & Highlights */}
                        {/* Model Animation (per-step) */}
                        {formData.components.some(
                            (c) => c.type === 'model'
                        ) && (
                            <div
                                style={{
                                    marginBottom: '15px',
                                    padding: '10px 14px',
                                    background: '#e8f5e9',
                                    borderRadius: '6px',
                                    border: '1px solid #a5d6a7',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                }}
                            >
                                <span
                                    style={{
                                        fontWeight: 'bold',
                                        color: '#2e7d32',
                                        minWidth: '130px',
                                    }}
                                >
                                    Model Animation:
                                </span>
                                <span style={{ flex: 1, color: '#333' }}>
                                    {activeStepData?.models?.animationName ||
                                        '(none)'}
                                </span>
                                <button
                                    onClick={() => {
                                        setAnimSelectionContext({
                                            type: 'main',
                                            index: null,
                                        })
                                        const currentAnim =
                                            activeStepData?.models
                                                ?.animationName
                                        if (currentAnim)
                                            setSelectedAnimation(currentAnim)
                                        setShowAnimModal(true)
                                    }}
                                    style={{
                                        padding: '5px 12px',
                                        background: '#388e3c',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    Set Animation
                                </button>
                                {activeStepData?.models?.animationName && (
                                    <button
                                        onClick={() =>
                                            handleStepChange('models', {
                                                ...(activeStepData.models ||
                                                    {}),
                                                animationName: '',
                                            })
                                        }
                                        style={{
                                            padding: '5px 10px',
                                            background: '#c62828',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Component Visibilities, Enables & Highlights */}
                        <div
                            style={{
                                display: 'flex',
                                gap: '20px',
                                marginBottom: '20px',
                                background: '#f8f9fa',
                                padding: '15px',
                                borderRadius: '6px',
                                border: '1px dashed #ccc',
                            }}
                        >
                            {/* COLUMN 1: Visible Components */}
                            <div style={{ flex: 1 }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontWeight: 'bold',
                                        color: '#1a73e8',
                                        marginBottom: '10px',
                                    }}
                                >
                                    Visible Components
                                </label>
                                <div
                                    style={{
                                        maxHeight: '150px',
                                        overflowY: 'auto',
                                    }}
                                >
                                    {formData.components.map((comp) => (
                                        <label
                                            key={comp.id}
                                            style={{
                                                display: 'block',
                                                marginBottom: '6px',
                                                cursor: 'pointer',
                                                fontSize: '0.95rem',
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={(
                                                    activeStepData?.visibleComponents ||
                                                    []
                                                ).includes(comp.id)}
                                                onChange={(e) =>
                                                    toggleStepComponentArray(
                                                        'visibleComponents',
                                                        comp.id,
                                                        e.target.checked
                                                    )
                                                }
                                                style={{ marginRight: '8px' }}
                                            />
                                            {COMPONENT_LABELS[comp.type] ||
                                                comp.type}{' '}
                                            <span style={{ color: '#767676' }}>
                                                ({comp.name})
                                            </span>
                                            <span
                                                style={{
                                                    color: '#ff6a00',
                                                    fontStyle: 'italic',
                                                }}
                                            >
                                                {comp.properties?.label &&
                                                    ` - ${comp.properties?.label}`}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* COLUMN 2: Enabled Components (NEW) */}
                            <div style={{ flex: 1 }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontWeight: 'bold',
                                        color: '#1565c0',
                                        marginBottom: '10px',
                                    }}
                                >
                                    Enabled Components (Clickable)
                                </label>
                                <div
                                    style={{
                                        maxHeight: '150px',
                                        overflowY: 'auto',
                                    }}
                                >
                                    {formData.components.map((comp) => {
                                        const isVisible = (
                                            activeStepData?.visibleComponents ||
                                            []
                                        ).includes(comp.id)
                                        return (
                                            <label
                                                key={comp.id}
                                                title={
                                                    !isVisible
                                                        ? 'Component must be visible first'
                                                        : undefined
                                                }
                                                style={{
                                                    display: 'block',
                                                    marginBottom: '6px',
                                                    cursor: isVisible
                                                        ? 'pointer'
                                                        : 'not-allowed',
                                                    fontSize: '0.95rem',
                                                    opacity: isVisible
                                                        ? 1
                                                        : 0.4,
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    disabled={!isVisible}
                                                    checked={(
                                                        activeStepData?.enableComponents ||
                                                        []
                                                    ).includes(comp.id)}
                                                    onChange={(e) =>
                                                        toggleStepComponentArray(
                                                            'enableComponents',
                                                            comp.id,
                                                            e.target.checked
                                                        )
                                                    }
                                                    style={{
                                                        marginRight: '8px',
                                                    }}
                                                />
                                                {COMPONENT_LABELS[comp.type] ||
                                                    comp.type}{' '}
                                                <span
                                                    style={{ color: '#767676' }}
                                                >
                                                    ({comp.name})
                                                </span>
                                                <span
                                                    style={{
                                                        color: '#ff6a00',
                                                        fontStyle: 'italic',
                                                    }}
                                                >
                                                    {comp.properties?.label &&
                                                        ` - ${comp.properties?.label}`}
                                                </span>
                                            </label>
                                        )
                                    })}
                                </div>
                            </div>

                            {/* COLUMN 3: Highlighted Components */}
                            <div style={{ flex: 1 }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontWeight: 'bold',
                                        color: '#d93025',
                                        marginBottom: '10px',
                                    }}
                                >
                                    Highlighted Components
                                </label>
                                <div
                                    style={{
                                        maxHeight: '150px',
                                        overflowY: 'auto',
                                    }}
                                >
                                    {formData.components.map((comp) => {
                                        const isVisible = (
                                            activeStepData?.visibleComponents ||
                                            []
                                        ).includes(comp.id)
                                        return (
                                            <label
                                                key={comp.id}
                                                title={
                                                    !isVisible
                                                        ? 'Component must be visible first'
                                                        : undefined
                                                }
                                                style={{
                                                    display: 'block',
                                                    marginBottom: '6px',
                                                    cursor: isVisible
                                                        ? 'pointer'
                                                        : 'not-allowed',
                                                    fontSize: '0.95rem',
                                                    opacity: isVisible
                                                        ? 1
                                                        : 0.4,
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    disabled={!isVisible}
                                                    checked={(
                                                        activeStepData?.highlight ||
                                                        []
                                                    ).includes(comp.id)}
                                                    onChange={(e) =>
                                                        toggleStepComponentArray(
                                                            'highlight',
                                                            comp.id,
                                                            e.target.checked
                                                        )
                                                    }
                                                    style={{
                                                        marginRight: '8px',
                                                    }}
                                                />
                                                {COMPONENT_LABELS[comp.type] ||
                                                    comp.type}{' '}
                                                <span
                                                    style={{ color: '#767676' }}
                                                >
                                                    ({comp.name})
                                                </span>
                                                <span
                                                    style={{
                                                        color: '#ff6a00',
                                                        fontStyle: 'italic',
                                                    }}
                                                >
                                                    {comp.properties?.label &&
                                                        ` - ${comp.properties?.label}`}
                                                </span>
                                            </label>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Live Step Toggle */}
                        <div
                            style={{
                                marginBottom: '15px',
                                padding: '10px 14px',
                                background: activeStepData?.isLiveStep
                                    ? '#e3f2fd'
                                    : '#f5f5f5',
                                borderRadius: '6px',
                                border: activeStepData?.isLiveStep
                                    ? '1.5px solid #1565c0'
                                    : '1px solid #e0e0e0',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    marginBottom: activeStepData?.isLiveStep
                                        ? '12px'
                                        : '0',
                                }}
                            >
                                <input
                                    type="checkbox"
                                    id="isLiveStep"
                                    checked={!!activeStepData?.isLiveStep}
                                    onChange={(e) =>
                                        handleLiveStepToggle(e.target.checked)
                                    }
                                    style={{
                                        width: '16px',
                                        height: '16px',
                                        cursor: 'pointer',
                                    }}
                                />
                                <label
                                    htmlFor="isLiveStep"
                                    style={{
                                        fontWeight: 'bold',
                                        color: '#1565c0',
                                        cursor: 'pointer',
                                        margin: 0,
                                    }}
                                >
                                    Live Step
                                </label>
                                <span
                                    style={{
                                        fontSize: '0.85rem',
                                        color: '#888',
                                    }}
                                >
                                    Send hardware bytes instantly when an input
                                    changes during this step.
                                </span>
                            </div>

                            {activeStepData?.isLiveStep && (
                                <div
                                    style={{
                                        background: '#fff',
                                        borderRadius: '4px',
                                        padding: '10px',
                                        border: '1px solid #bbdefb',
                                    }}
                                >
                                    <p
                                        style={{
                                            margin: '0 0 8px',
                                            fontSize: '0.85rem',
                                            color: '#555',
                                        }}
                                    >
                                        Select which input components should
                                        send live bytes to hardware. Each
                                        input's{' '}
                                        <strong>
                                            Target Hardware Connection
                                        </strong>{' '}
                                        must be specified.
                                    </p>
                                    {formData.components.filter((c) =>
                                        CATEGORIES.input.includes(c.type)
                                    ).length === 0 && (
                                        <p
                                            style={{
                                                color: '#888',
                                                fontStyle: 'italic',
                                                fontSize: '0.9rem',
                                            }}
                                        >
                                            No input components found. Add
                                            button or slider controls first.
                                        </p>
                                    )}
                                    {formData.components
                                        .filter((c) =>
                                            CATEGORIES.input.includes(c.type)
                                        )
                                        .map((inputComp) => {
                                            const liveEntry =
                                                (activeStepData.live || {})[
                                                    inputComp.id
                                                ] || null
                                            const isLiveChecked = !!liveEntry
                                            const isButtonType = [
                                                'button_control',
                                                'tack_button_control',
                                                'hold_button_control',
                                                'trigger_button_control',
                                            ].includes(inputComp.type)
                                            // RGB target → fixedBytes is 8 bytes; non-RGB → 5 bytes
                                            const isRgbFixedBytes =
                                                (liveEntry?.fixedBytes
                                                    ?.length ?? 0) === 8
                                            // Value range of the hardware this input
                                            // drives (by name), so the ON-value field
                                            // matches the component (e.g. dc_motor is
                                            // -100..100, servo_270 is 0..270).
                                            const primaryRange =
                                                COMPONENT_RANGES[
                                                    inputComp.name
                                                        ?.trim()
                                                        .toLowerCase()
                                                        .replace(/ /g, '_')
                                                ] || { min: 0, max: 100 }
                                            return (
                                                <div
                                                    key={inputComp.id}
                                                    style={{
                                                        marginBottom: '8px',
                                                        padding: '8px',
                                                        background:
                                                            isLiveChecked
                                                                ? '#e8f5e9'
                                                                : '#fafafa',
                                                        borderRadius: '4px',
                                                        border: `1px solid ${
                                                            isLiveChecked
                                                                ? '#81c784'
                                                                : '#ddd'
                                                        }`,
                                                    }}
                                                >
                                                    <label
                                                        style={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            gap: '8px',
                                                            cursor: 'pointer',
                                                            marginBottom:
                                                                isLiveChecked
                                                                    ? '8px'
                                                                    : '0',
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={
                                                                isLiveChecked
                                                            }
                                                            onChange={(e) =>
                                                                handleLiveInputToggle(
                                                                    inputComp,
                                                                    e.target
                                                                        .checked
                                                                )
                                                            }
                                                        />
                                                        <strong>
                                                            {COMPONENT_LABELS[
                                                                inputComp.type
                                                            ] || inputComp.type}
                                                        </strong>
                                                        {inputComp.name ? (
                                                            <span
                                                                style={{
                                                                    color: '#1565c0',
                                                                    fontSize:
                                                                        '0.82rem',
                                                                }}
                                                            >
                                                                (
                                                                {inputComp.name}
                                                                )
                                                            </span>
                                                        ) : (
                                                            <span
                                                                style={{
                                                                    color: '#c62828',
                                                                    fontSize:
                                                                        '0.82rem',
                                                                    fontStyle:
                                                                        'italic',
                                                                }}
                                                            >
                                                                ⚠ Target
                                                                Hardware
                                                                Connection not
                                                                set
                                                            </span>
                                                        )}
                                                        {inputComp.properties
                                                            ?.label && (
                                                            <span
                                                                style={{
                                                                    color: '#ff6a00',
                                                                    fontStyle:
                                                                        'italic',
                                                                }}
                                                            >
                                                                -{' '}
                                                                {
                                                                    inputComp
                                                                        .properties
                                                                        .label
                                                                }
                                                            </span>
                                                        )}
                                                    </label>

                                                    {/* isFree toggle and dynamic fetch UI */}
                                                    {isLiveChecked && (
                                                        <div
                                                            style={{
                                                                marginTop:
                                                                    '8px',
                                                                padding:
                                                                    '6px 10px',
                                                                background:
                                                                    '#f3e5f5',
                                                                borderRadius:
                                                                    '4px',
                                                                border: '1px solid #ce93d8',
                                                            }}
                                                        >
                                                            <label
                                                                style={{
                                                                    display:
                                                                        'flex',
                                                                    alignItems:
                                                                        'center',
                                                                    gap: '8px',
                                                                    cursor: 'pointer',
                                                                    marginBottom:
                                                                        liveEntry?.isFree
                                                                            ? '0'
                                                                            : '10px',
                                                                }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={
                                                                        !!liveEntry?.isFree
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        handleLiveIsFreeToggle(
                                                                            inputComp.id,
                                                                            e
                                                                                .target
                                                                                .checked
                                                                        )
                                                                    }
                                                                    style={{
                                                                        width: '14px',
                                                                        height: '14px',
                                                                        cursor: 'pointer',
                                                                    }}
                                                                />
                                                                <span
                                                                    style={{
                                                                        fontSize:
                                                                            '0.82rem',
                                                                        color: '#6a1b9a',
                                                                        fontWeight:
                                                                            'bold',
                                                                    }}
                                                                >
                                                                    Free Mode
                                                                </span>
                                                                <span
                                                                    style={{
                                                                        fontSize:
                                                                            '0.78rem',
                                                                        color: '#888',
                                                                    }}
                                                                >
                                                                    Send these
                                                                    bytes
                                                                    directly
                                                                    without
                                                                    fetching
                                                                    dynamic
                                                                    values
                                                                </span>
                                                            </label>

                                                            {/* ON value editor: buttons in Free Mode */}
                                                            {liveEntry?.isFree &&
                                                                isButtonType && (
                                                                    <div
                                                                        style={{
                                                                            marginTop:
                                                                                '8px',
                                                                            padding:
                                                                                '8px 10px',
                                                                            background:
                                                                                '#ede7f6',
                                                                            borderRadius:
                                                                                '4px',
                                                                            border: '1px solid #b39ddb',
                                                                        }}
                                                                    >
                                                                        <p
                                                                            style={{
                                                                                margin: '0 0 8px',
                                                                                fontSize:
                                                                                    '0.8rem',
                                                                                fontWeight:
                                                                                    'bold',
                                                                                color: '#4527a0',
                                                                            }}
                                                                        >
                                                                            ON
                                                                            value
                                                                            (written
                                                                            into
                                                                            bytes
                                                                            when
                                                                            button
                                                                            is
                                                                            pressed):
                                                                        </p>
                                                                        <div
                                                                            style={{
                                                                                display:
                                                                                    'flex',
                                                                                gap: '12px',
                                                                                alignItems:
                                                                                    'center',
                                                                                flexWrap:
                                                                                    'wrap',
                                                                            }}
                                                                        >
                                                                            {isRgbFixedBytes ? (
                                                                                [
                                                                                    {
                                                                                        label: 'R',
                                                                                        byteIdx: 5,
                                                                                    },
                                                                                    {
                                                                                        label: 'G',
                                                                                        byteIdx: 6,
                                                                                    },
                                                                                    {
                                                                                        label: 'B',
                                                                                        byteIdx: 7,
                                                                                    },
                                                                                ].map(
                                                                                    ({
                                                                                        label: chLabel,
                                                                                        byteIdx,
                                                                                    }) => (
                                                                                        <label
                                                                                            key={
                                                                                                chLabel
                                                                                            }
                                                                                            style={{
                                                                                                display:
                                                                                                    'flex',
                                                                                                alignItems:
                                                                                                    'center',
                                                                                                gap: '4px',
                                                                                                fontSize:
                                                                                                    '0.82rem',
                                                                                                color: '#4527a0',
                                                                                                fontWeight:
                                                                                                    'bold',
                                                                                            }}
                                                                                        >
                                                                                            {
                                                                                                chLabel
                                                                                            }
                                                                                            <input
                                                                                                type="number"
                                                                                                min={
                                                                                                    0
                                                                                                }
                                                                                                max={
                                                                                                    100
                                                                                                }
                                                                                                value={
                                                                                                    liveEntry
                                                                                                        .fixedBytes[
                                                                                                        byteIdx
                                                                                                    ] ??
                                                                                                    0
                                                                                                }
                                                                                                onChange={(
                                                                                                    e
                                                                                                ) => {
                                                                                                    const v =
                                                                                                        Math.max(
                                                                                                            0,
                                                                                                            Math.min(
                                                                                                                100,
                                                                                                                Number(
                                                                                                                    e
                                                                                                                        .target
                                                                                                                        .value
                                                                                                                )
                                                                                                            )
                                                                                                        )
                                                                                                    const nb =
                                                                                                        [
                                                                                                            ...liveEntry.fixedBytes,
                                                                                                        ]
                                                                                                    nb[
                                                                                                        byteIdx
                                                                                                    ] =
                                                                                                        v
                                                                                                    handleLiveFixedBytesChange(
                                                                                                        inputComp.id,
                                                                                                        nb
                                                                                                    )
                                                                                                }}
                                                                                                style={{
                                                                                                    width: '58px',
                                                                                                    padding:
                                                                                                        '2px 5px',
                                                                                                    borderRadius:
                                                                                                        '4px',
                                                                                                    border: '1px solid #9575cd',
                                                                                                    fontSize:
                                                                                                        '0.82rem',
                                                                                                }}
                                                                                            />
                                                                                        </label>
                                                                                    )
                                                                                )
                                                                            ) : (
                                                                                <label
                                                                                    style={{
                                                                                        display:
                                                                                            'flex',
                                                                                        alignItems:
                                                                                            'center',
                                                                                        gap: '4px',
                                                                                        fontSize:
                                                                                            '0.82rem',
                                                                                        color: '#4527a0',
                                                                                        fontWeight:
                                                                                            'bold',
                                                                                    }}
                                                                                >
                                                                                    Intensity
                                                                                    <input
                                                                                        type="number"
                                                                                        min={
                                                                                            primaryRange.min
                                                                                        }
                                                                                        max={
                                                                                            primaryRange.max
                                                                                        }
                                                                                        value={
                                                                                            liveEntry
                                                                                                .fixedBytes[4] ??
                                                                                            0
                                                                                        }
                                                                                        onChange={(
                                                                                            e
                                                                                        ) => {
                                                                                            const raw =
                                                                                                Number(
                                                                                                    e
                                                                                                        .target
                                                                                                        .value
                                                                                                )
                                                                                            const v =
                                                                                                Math.max(
                                                                                                    primaryRange.min,
                                                                                                    Math.min(
                                                                                                        primaryRange.max,
                                                                                                        isNaN(
                                                                                                            raw
                                                                                                        )
                                                                                                            ? primaryRange.min
                                                                                                            : raw
                                                                                                    )
                                                                                                )
                                                                                            const nb =
                                                                                                [
                                                                                                    ...liveEntry.fixedBytes,
                                                                                                ]
                                                                                            nb[4] =
                                                                                                v
                                                                                            handleLiveFixedBytesChange(
                                                                                                inputComp.id,
                                                                                                nb
                                                                                            )
                                                                                        }}
                                                                                        style={{
                                                                                            width: '70px',
                                                                                            padding:
                                                                                                '2px 5px',
                                                                                            borderRadius:
                                                                                                '4px',
                                                                                            border: '1px solid #9575cd',
                                                                                            fontSize:
                                                                                                '0.82rem',
                                                                                        }}
                                                                                    />
                                                                                </label>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                            {inputComp.type !==
                                                                'rgb_control' &&
                                                                assemblyConnections.filter(
                                                                    (conn) =>
                                                                        conn.port !==
                                                                            inputComp.port &&
                                                                        !conn.component
                                                                            .toLowerCase()
                                                                            .includes(
                                                                                'rgb'
                                                                            )
                                                                ).length > 0 && (
                                                                    <div
                                                                        style={{
                                                                            background:
                                                                                '#e0f2f1',
                                                                            borderRadius:
                                                                                '4px',
                                                                            padding:
                                                                                '8px',
                                                                            border: '1px solid #4db6ac',
                                                                            marginBottom:
                                                                                '10px',
                                                                        }}
                                                                    >
                                                                        <p
                                                                            style={{
                                                                                margin: '0 0 8px',
                                                                                fontSize:
                                                                                    '0.82rem',
                                                                                fontWeight:
                                                                                    'bold',
                                                                                color: '#00695c',
                                                                            }}
                                                                        >
                                                                            Also
                                                                            drive
                                                                            these
                                                                            outputs
                                                                            (each
                                                                            fires
                                                                            at its
                                                                            own
                                                                            value
                                                                            when
                                                                            this
                                                                            input
                                                                            is
                                                                            active):
                                                                        </p>
                                                                        {assemblyConnections
                                                                            .filter(
                                                                                (
                                                                                    conn
                                                                                ) =>
                                                                                    conn.port !==
                                                                                        inputComp.port &&
                                                                                    !conn.component
                                                                                        .toLowerCase()
                                                                                        .includes(
                                                                                            'rgb'
                                                                                        )
                                                                            )
                                                                            .map(
                                                                                (
                                                                                    conn
                                                                                ) => {
                                                                                    const target =
                                                                                        (
                                                                                            liveEntry?.extraTargets ||
                                                                                            []
                                                                                        ).find(
                                                                                            (
                                                                                                t
                                                                                            ) =>
                                                                                                t.port ===
                                                                                                conn.port
                                                                                        )
                                                                                    const checked =
                                                                                        !!target
                                                                                    const normalized =
                                                                                        conn.component
                                                                                            ?.trim()
                                                                                            .toLowerCase()
                                                                                            .replace(
                                                                                                / /g,
                                                                                                '_'
                                                                                            ) ||
                                                                                        ''
                                                                                    const range =
                                                                                        COMPONENT_RANGES[
                                                                                            normalized
                                                                                        ] || {
                                                                                            min: 0,
                                                                                            max: 100,
                                                                                        }
                                                                                    return (
                                                                                        <div
                                                                                            key={`${conn.component}_${conn.port}`}
                                                                                            style={{
                                                                                                display:
                                                                                                    'flex',
                                                                                                alignItems:
                                                                                                    'center',
                                                                                                gap: '8px',
                                                                                                marginBottom:
                                                                                                    '4px',
                                                                                                fontSize:
                                                                                                    '0.82rem',
                                                                                            }}
                                                                                        >
                                                                                            <label
                                                                                                style={{
                                                                                                    display:
                                                                                                        'flex',
                                                                                                    alignItems:
                                                                                                        'center',
                                                                                                    gap: '6px',
                                                                                                    cursor: 'pointer',
                                                                                                    flex: 1,
                                                                                                }}
                                                                                            >
                                                                                                <input
                                                                                                    type="checkbox"
                                                                                                    checked={
                                                                                                        checked
                                                                                                    }
                                                                                                    onChange={(
                                                                                                        e
                                                                                                    ) =>
                                                                                                        handleLiveExtraTargetToggle(
                                                                                                            inputComp.id,
                                                                                                            conn,
                                                                                                            e
                                                                                                                .target
                                                                                                                .checked
                                                                                                        )
                                                                                                    }
                                                                                                />
                                                                                                <span>
                                                                                                    {conn.component.replace(
                                                                                                        /_/g,
                                                                                                        ' '
                                                                                                    )}{' '}
                                                                                                    (Port:{' '}
                                                                                                    {PORT_NAME_MAP[
                                                                                                        conn
                                                                                                            .port
                                                                                                    ] ||
                                                                                                        conn.port}

                                                                                                    )
                                                                                                </span>
                                                                                            </label>
                                                                                            {checked && (
                                                                                                <label
                                                                                                    style={{
                                                                                                        display:
                                                                                                            'flex',
                                                                                                        alignItems:
                                                                                                            'center',
                                                                                                        gap: '4px',
                                                                                                        color: '#00695c',
                                                                                                        fontWeight:
                                                                                                            'bold',
                                                                                                    }}
                                                                                                >
                                                                                                    Value
                                                                                                    <input
                                                                                                        type="number"
                                                                                                        min={
                                                                                                            range.min
                                                                                                        }
                                                                                                        max={
                                                                                                            range.max
                                                                                                        }
                                                                                                        value={
                                                                                                            target?.value ??
                                                                                                            range.max
                                                                                                        }
                                                                                                        onChange={(
                                                                                                            e
                                                                                                        ) => {
                                                                                                            const raw =
                                                                                                                Number(
                                                                                                                    e
                                                                                                                        .target
                                                                                                                        .value
                                                                                                                )
                                                                                                            const clamped =
                                                                                                                Math.max(
                                                                                                                    range.min,
                                                                                                                    Math.min(
                                                                                                                        range.max,
                                                                                                                        isNaN(
                                                                                                                            raw
                                                                                                                        )
                                                                                                                            ? range.min
                                                                                                                            : raw
                                                                                                                    )
                                                                                                                )
                                                                                                            handleLiveExtraTargetValue(
                                                                                                                inputComp.id,
                                                                                                                conn.port,
                                                                                                                clamped
                                                                                                            )
                                                                                                        }}
                                                                                                        style={{
                                                                                                            width: '64px',
                                                                                                            padding:
                                                                                                                '2px 5px',
                                                                                                            borderRadius:
                                                                                                                '4px',
                                                                                                            border: '1px solid #4db6ac',
                                                                                                            fontSize:
                                                                                                                '0.82rem',
                                                                                                        }}
                                                                                                    />
                                                                                                    <span
                                                                                                        style={{
                                                                                                            fontWeight:
                                                                                                                'normal',
                                                                                                            color: '#888',
                                                                                                            fontSize:
                                                                                                                '0.72rem',
                                                                                                        }}
                                                                                                    >
                                                                                                        (
                                                                                                        {
                                                                                                            range.min
                                                                                                        }

                                                                                                        –
                                                                                                        {
                                                                                                            range.max
                                                                                                        }

                                                                                                        )
                                                                                                    </span>
                                                                                                </label>
                                                                                            )}
                                                                                        </div>
                                                                                    )
                                                                                }
                                                                            )}
                                                                    </div>
                                                                )}

                                                            {!liveEntry?.isFree && (
                                                                <div
                                                                    style={{
                                                                        background:
                                                                            '#fce4ec',
                                                                        borderRadius:
                                                                            '4px',
                                                                        padding:
                                                                            '8px',
                                                                        border: '1px solid #f48fb1',
                                                                    }}
                                                                >
                                                                    <p
                                                                        style={{
                                                                            margin: '0 0 8px',
                                                                            fontSize:
                                                                                '0.82rem',
                                                                            fontWeight:
                                                                                'bold',
                                                                            color: '#880e4f',
                                                                        }}
                                                                    >
                                                                        Fetch
                                                                        values
                                                                        from
                                                                        other
                                                                        components:
                                                                    </p>
                                                                    {formData.components
                                                                        .filter(
                                                                            (
                                                                                fc
                                                                            ) =>
                                                                                CATEGORIES.input.includes(
                                                                                    fc.type
                                                                                ) &&
                                                                                fc.id !==
                                                                                    inputComp.id
                                                                        )
                                                                        .map(
                                                                            (
                                                                                fetchComp
                                                                            ) => {
                                                                                const isRgbFetch =
                                                                                    fetchComp.type ===
                                                                                    'rgb_control'
                                                                                const fetchChecked =
                                                                                    !!(liveEntry?.compsIds ||
                                                                                        {})[
                                                                                        fetchComp
                                                                                            .id
                                                                                    ]
                                                                                const fetchIndices =
                                                                                    (liveEntry?.compsIds ||
                                                                                        {})[
                                                                                        fetchComp
                                                                                            .id
                                                                                    ] ||
                                                                                    []
                                                                                const RGB_CHANNELS =
                                                                                    [
                                                                                        {
                                                                                            label: 'Red',
                                                                                            idx: 0,
                                                                                        },
                                                                                        {
                                                                                            label: 'Green',
                                                                                            idx: 1,
                                                                                        },
                                                                                        {
                                                                                            label: 'Blue',
                                                                                            idx: 2,
                                                                                        },
                                                                                    ]
                                                                                return (
                                                                                    <div
                                                                                        key={
                                                                                            fetchComp.id
                                                                                        }
                                                                                        style={{
                                                                                            marginBottom:
                                                                                                '6px',
                                                                                            padding:
                                                                                                '6px 8px',
                                                                                            background:
                                                                                                fetchChecked
                                                                                                    ? '#e8f5e9'
                                                                                                    : '#fafafa',
                                                                                            borderRadius:
                                                                                                '4px',
                                                                                            border: `1px solid ${fetchChecked ? '#81c784' : '#ddd'}`,
                                                                                        }}
                                                                                    >
                                                                                        <label
                                                                                            style={{
                                                                                                display:
                                                                                                    'flex',
                                                                                                alignItems:
                                                                                                    'center',
                                                                                                gap: '6px',
                                                                                                cursor: 'pointer',
                                                                                                marginBottom:
                                                                                                    fetchChecked
                                                                                                        ? '6px'
                                                                                                        : '0',
                                                                                            }}
                                                                                        >
                                                                                            <input
                                                                                                type="checkbox"
                                                                                                checked={
                                                                                                    fetchChecked
                                                                                                }
                                                                                                onChange={(
                                                                                                    e
                                                                                                ) =>
                                                                                                    handleLiveFetchCompToggle(
                                                                                                        inputComp.id,
                                                                                                        fetchComp.id,
                                                                                                        e
                                                                                                            .target
                                                                                                            .checked,
                                                                                                        isRgbFetch
                                                                                                    )
                                                                                                }
                                                                                            />
                                                                                            <strong
                                                                                                style={{
                                                                                                    fontSize:
                                                                                                        '0.82rem',
                                                                                                }}
                                                                                            >
                                                                                                {COMPONENT_LABELS[
                                                                                                    fetchComp
                                                                                                        .type
                                                                                                ] ||
                                                                                                    fetchComp.type}
                                                                                            </strong>
                                                                                            {fetchComp
                                                                                                .properties
                                                                                                ?.label && (
                                                                                                <span
                                                                                                    style={{
                                                                                                        fontSize:
                                                                                                            '0.78rem',
                                                                                                        color: '#ff6a00',
                                                                                                        fontStyle:
                                                                                                            'italic',
                                                                                                    }}
                                                                                                >
                                                                                                    –{' '}
                                                                                                    {
                                                                                                        fetchComp
                                                                                                            .properties
                                                                                                            .label
                                                                                                    }
                                                                                                </span>
                                                                                            )}
                                                                                        </label>
                                                                                        {fetchChecked &&
                                                                                            isRgbFetch && (
                                                                                                <div
                                                                                                    style={{
                                                                                                        paddingLeft:
                                                                                                            '22px',
                                                                                                    }}
                                                                                                >
                                                                                                    {RGB_CHANNELS.map(
                                                                                                        ({
                                                                                                            label: chLabel,
                                                                                                            idx: chIdx,
                                                                                                        }) => {
                                                                                                            const chEnabled =
                                                                                                                fetchIndices[
                                                                                                                    chIdx
                                                                                                                ] !==
                                                                                                                -1
                                                                                                            return (
                                                                                                                <div
                                                                                                                    key={
                                                                                                                        chIdx
                                                                                                                    }
                                                                                                                    style={{
                                                                                                                        display:
                                                                                                                            'flex',
                                                                                                                        alignItems:
                                                                                                                            'center',
                                                                                                                        gap: '8px',
                                                                                                                        marginBottom:
                                                                                                                            '4px',
                                                                                                                        fontSize:
                                                                                                                            '0.8rem',
                                                                                                                    }}
                                                                                                                >
                                                                                                                    <input
                                                                                                                        type="checkbox"
                                                                                                                        checked={
                                                                                                                            chEnabled
                                                                                                                        }
                                                                                                                        onChange={(
                                                                                                                            e
                                                                                                                        ) =>
                                                                                                                            handleLiveFetchIndexChange(
                                                                                                                                inputComp.id,
                                                                                                                                fetchComp.id,
                                                                                                                                chIdx,
                                                                                                                                e
                                                                                                                                    .target
                                                                                                                                    .checked
                                                                                                                                    ? chIdx +
                                                                                                                                          5
                                                                                                                                    : -1
                                                                                                                            )
                                                                                                                        }
                                                                                                                    />
                                                                                                                    <span
                                                                                                                        style={{
                                                                                                                            width: '44px',
                                                                                                                            color: '#444',
                                                                                                                        }}
                                                                                                                    >
                                                                                                                        {
                                                                                                                            chLabel
                                                                                                                        }

                                                                                                                        :
                                                                                                                    </span>
                                                                                                                    <span
                                                                                                                        style={{
                                                                                                                            fontSize:
                                                                                                                                '0.78rem',
                                                                                                                            color: '#888',
                                                                                                                        }}
                                                                                                                    >
                                                                                                                        Byte
                                                                                                                        index:
                                                                                                                    </span>
                                                                                                                    <input
                                                                                                                        type="number"
                                                                                                                        min={
                                                                                                                            0
                                                                                                                        }
                                                                                                                        max={
                                                                                                                            7
                                                                                                                        }
                                                                                                                        value={
                                                                                                                            chEnabled
                                                                                                                                ? fetchIndices[
                                                                                                                                      chIdx
                                                                                                                                  ]
                                                                                                                                : ''
                                                                                                                        }
                                                                                                                        disabled={
                                                                                                                            !chEnabled
                                                                                                                        }
                                                                                                                        onChange={(
                                                                                                                            e
                                                                                                                        ) =>
                                                                                                                            handleLiveFetchIndexChange(
                                                                                                                                inputComp.id,
                                                                                                                                fetchComp.id,
                                                                                                                                chIdx,
                                                                                                                                Number(
                                                                                                                                    e
                                                                                                                                        .target
                                                                                                                                        .value
                                                                                                                                )
                                                                                                                            )
                                                                                                                        }
                                                                                                                        style={{
                                                                                                                            width: '52px',
                                                                                                                            padding:
                                                                                                                                '2px 5px',
                                                                                                                            borderRadius:
                                                                                                                                '4px',
                                                                                                                            border: '1px solid #ccc',
                                                                                                                            fontSize:
                                                                                                                                '0.8rem',
                                                                                                                            background:
                                                                                                                                chEnabled
                                                                                                                                    ? '#fff'
                                                                                                                                    : '#eee',
                                                                                                                        }}
                                                                                                                    />
                                                                                                                </div>
                                                                                                            )
                                                                                                        }
                                                                                                    )}
                                                                                                </div>
                                                                                            )}
                                                                                        {fetchChecked &&
                                                                                            !isRgbFetch && (
                                                                                                <div
                                                                                                    style={{
                                                                                                        display:
                                                                                                            'flex',
                                                                                                        alignItems:
                                                                                                            'center',
                                                                                                        gap: '8px',
                                                                                                        paddingLeft:
                                                                                                            '22px',
                                                                                                        fontSize:
                                                                                                            '0.8rem',
                                                                                                    }}
                                                                                                >
                                                                                                    <span
                                                                                                        style={{
                                                                                                            color: '#444',
                                                                                                        }}
                                                                                                    >
                                                                                                        Target
                                                                                                        Byte
                                                                                                        Index:
                                                                                                    </span>
                                                                                                    <input
                                                                                                        type="number"
                                                                                                        min={
                                                                                                            0
                                                                                                        }
                                                                                                        max={
                                                                                                            7
                                                                                                        }
                                                                                                        value={
                                                                                                            fetchIndices[0] ??
                                                                                                            4
                                                                                                        }
                                                                                                        onChange={(
                                                                                                            e
                                                                                                        ) =>
                                                                                                            handleLiveFetchIndexChange(
                                                                                                                inputComp.id,
                                                                                                                fetchComp.id,
                                                                                                                0,
                                                                                                                Number(
                                                                                                                    e
                                                                                                                        .target
                                                                                                                        .value
                                                                                                                )
                                                                                                            )
                                                                                                        }
                                                                                                        style={{
                                                                                                            width: '52px',
                                                                                                            padding:
                                                                                                                '2px 5px',
                                                                                                            borderRadius:
                                                                                                                '4px',
                                                                                                            border: '1px solid #ccc',
                                                                                                            fontSize:
                                                                                                                '0.8rem',
                                                                                                        }}
                                                                                                    />
                                                                                                </div>
                                                                                            )}
                                                                                    </div>
                                                                                )
                                                                            }
                                                                        )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )
                                        })}
                                </div>
                            )}
                        </div>

                        {/* Free Step Toggle */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                marginBottom: '15px',
                                padding: '10px 14px',
                                background: activeStepData?.isFreeStep
                                    ? '#fff3e0'
                                    : '#fff8e1',
                                borderRadius: '6px',
                                border: activeStepData?.isFreeStep
                                    ? '1.5px solid #e65100'
                                    : '1px solid #ffe082',
                            }}
                        >
                            <input
                                type="checkbox"
                                id="isFreeStep"
                                checked={activeStepData?.isFreeStep || false}
                                onChange={(e) =>
                                    handleStepChange(
                                        'isFreeStep',
                                        e.target.checked
                                    )
                                }
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    cursor: 'pointer',
                                }}
                            />
                            <label
                                htmlFor="isFreeStep"
                                style={{
                                    fontWeight: 'bold',
                                    color: '#e65100',
                                    cursor: 'pointer',
                                    margin: 0,
                                }}
                            >
                                Free Step
                            </label>
                            <span
                                style={{ fontSize: '0.85rem', color: '#888' }}
                            >
                                {activeStepData?.isFreeStep
                                    ? '✓ No conditions — user proceeds freely. Add a condition below to enable validation.'
                                    : '(No validation required — user can proceed freely)'}
                            </span>
                        </div>

                        {/* Validations — OR/AND conditionBlocks — always visible, greyed when free */}
                        <div
                            style={{
                                background: '#e8f0fe',
                                padding: '15px',
                                borderRadius: '6px',
                                marginBottom: '10px',
                                opacity: activeStepData?.isFreeStep ? 0.45 : 1,
                                filter: activeStepData?.isFreeStep
                                    ? 'grayscale(70%)'
                                    : 'none',
                                pointerEvents: activeStepData?.isFreeStep
                                    ? 'auto'
                                    : 'auto',
                                transition: 'opacity 0.2s, filter 0.2s',
                                position: 'relative',
                            }}
                        >
                            {activeStepData?.isFreeStep && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '8px',
                                        right: '10px',
                                        fontSize: '0.75rem',
                                        color: '#888',
                                        fontStyle: 'italic',
                                    }}
                                >
                                    Add a condition to enable this section
                                </div>
                            )}
                            <label
                                style={{
                                    display: 'block',
                                    fontWeight: 'bold',
                                    color: '#1565c0',
                                    marginBottom: '10px',
                                }}
                            >
                                Success Validations (Auto-Advance Triggers)
                            </label>
                            {(activeStepData?.conditionBlocks || []).length ===
                                0 && (
                                <p
                                    style={{
                                        fontSize: '0.9rem',
                                        color: '#555',
                                        fontStyle: 'italic',
                                        margin: '0 0 8px 0',
                                    }}
                                >
                                    No validations. The user will just click
                                    "Next" manually.
                                </p>
                            )}

                            {(activeStepData?.conditionBlocks || []).map(
                                (block, bIdx) => (
                                    <div key={bIdx}>
                                        {bIdx > 0 && (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    margin: '10px 0',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        flex: 1,
                                                        height: '1px',
                                                        background: '#90a0c0',
                                                    }}
                                                />
                                                <span
                                                    style={{
                                                        fontSize: '0.75rem',
                                                        fontWeight: 'bold',
                                                        color: '#e65100',
                                                        padding: '2px 8px',
                                                        background: '#fff3e0',
                                                        border: '1px solid #e65100',
                                                        borderRadius: '10px',
                                                    }}
                                                >
                                                    OR
                                                </span>
                                                <div
                                                    style={{
                                                        flex: 1,
                                                        height: '1px',
                                                        background: '#90a0c0',
                                                    }}
                                                />
                                            </div>
                                        )}
                                        <div
                                            style={{
                                                padding: '8px',
                                                background: '#dce8fc',
                                                borderRadius: '4px',
                                                border: '1px solid #b0c4e8',
                                                marginTop:
                                                    bIdx === 0 ? '4px' : '0',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                    alignItems: 'center',
                                                    marginBottom: '6px',
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: '0.75rem',
                                                        color: '#1a3a6c',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    All of (AND):
                                                </span>
                                                <button
                                                    onClick={() =>
                                                        removeStepConditionBlock(
                                                            bIdx
                                                        )
                                                    }
                                                    title="Remove this OR block"
                                                    style={{
                                                        fontSize: '0.75rem',
                                                        color: '#c0392b',
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    ✕ Remove Block
                                                </button>
                                            </div>
                                            {block.map((cond, cIdx) => {
                                                const inputComps =
                                                    formData.components.filter(
                                                        (c) =>
                                                            CATEGORIES.input.includes(
                                                                c.type
                                                            )
                                                    )
                                                const selType =
                                                    cond.inputType || ''
                                                const isBtn =
                                                    BUTTON_INPUT_TYPES.includes(
                                                        selType
                                                    )
                                                const isSlider =
                                                    SLIDER_INPUT_TYPES.includes(
                                                        selType
                                                    )
                                                const isRgb =
                                                    RGB_SLIDER_INPUT_TYPES.includes(
                                                        selType
                                                    )
                                                return (
                                                    <div
                                                        key={cIdx}
                                                        style={{
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            gap: '5px',
                                                            marginBottom: '6px',
                                                            padding: '6px',
                                                            background: '#fff',
                                                            borderRadius: '4px',
                                                            border: '1px solid #c0d0ea',
                                                            alignItems:
                                                                'center',
                                                        }}
                                                    >
                                                        <select
                                                            value={
                                                                cond.inputId ||
                                                                ''
                                                            }
                                                            onChange={(e) =>
                                                                updateStepValidation(
                                                                    bIdx,
                                                                    cIdx,
                                                                    'inputId',
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            style={{
                                                                flex: 2,
                                                                minWidth:
                                                                    '140px',
                                                                padding: '4px',
                                                            }}
                                                        >
                                                            <option value="">
                                                                -- Select Input
                                                                --
                                                            </option>
                                                            {inputComps.map(
                                                                (ic) => (
                                                                    <option
                                                                        key={
                                                                            ic.id
                                                                        }
                                                                        value={
                                                                            ic.id
                                                                        }
                                                                    >
                                                                        {COMPONENT_LABELS[
                                                                            ic
                                                                                .type
                                                                        ] ||
                                                                            ic.type}{' '}
                                                                        (
                                                                        {ic
                                                                            .properties
                                                                            ?.label ||
                                                                            ic.name}
                                                                        ) (ID:{' '}
                                                                        {ic.id})
                                                                    </option>
                                                                )
                                                            )}
                                                        </select>

                                                        {isBtn && (
                                                            <select
                                                                value={
                                                                    cond.expectedStatus ||
                                                                    'ON'
                                                                }
                                                                onChange={(e) =>
                                                                    updateStepValidation(
                                                                        bIdx,
                                                                        cIdx,
                                                                        'expectedStatus',
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                style={{
                                                                    flex: 1,
                                                                    padding:
                                                                        '4px',
                                                                }}
                                                            >
                                                                <option value="ON">
                                                                    is ON
                                                                </option>
                                                                <option value="OFF">
                                                                    is OFF
                                                                </option>
                                                            </select>
                                                        )}

                                                        {isSlider && (
                                                            <>
                                                                <select
                                                                    value={
                                                                        cond.operator ||
                                                                        '>'
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateStepValidation(
                                                                            bIdx,
                                                                            cIdx,
                                                                            'operator',
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    style={{
                                                                        flex: 1,
                                                                        padding:
                                                                            '4px',
                                                                    }}
                                                                >
                                                                    {OPERATORS.map(
                                                                        (
                                                                            op
                                                                        ) => (
                                                                            <option
                                                                                key={
                                                                                    op
                                                                                }
                                                                                value={
                                                                                    op
                                                                                }
                                                                            >
                                                                                {
                                                                                    op
                                                                                }
                                                                            </option>
                                                                        )
                                                                    )}
                                                                </select>
                                                                <input
                                                                    type="number"
                                                                    placeholder="Value"
                                                                    value={
                                                                        cond.value ??
                                                                        0
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateStepValidation(
                                                                            bIdx,
                                                                            cIdx,
                                                                            'value',
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    style={{
                                                                        flex: 1,
                                                                        padding:
                                                                            '4px',
                                                                    }}
                                                                />
                                                            </>
                                                        )}

                                                        {isRgb && (
                                                            <>
                                                                <select
                                                                    value={
                                                                        cond.channel ||
                                                                        'red'
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateStepValidation(
                                                                            bIdx,
                                                                            cIdx,
                                                                            'channel',
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    style={{
                                                                        flex: 1,
                                                                        padding:
                                                                            '4px',
                                                                    }}
                                                                >
                                                                    <option value="red">
                                                                        Red
                                                                    </option>
                                                                    <option value="green">
                                                                        Green
                                                                    </option>
                                                                    <option value="blue">
                                                                        Blue
                                                                    </option>
                                                                </select>
                                                                <select
                                                                    value={
                                                                        cond.operator ||
                                                                        '>'
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateStepValidation(
                                                                            bIdx,
                                                                            cIdx,
                                                                            'operator',
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    style={{
                                                                        flex: 1,
                                                                        padding:
                                                                            '4px',
                                                                    }}
                                                                >
                                                                    {OPERATORS.map(
                                                                        (
                                                                            op
                                                                        ) => (
                                                                            <option
                                                                                key={
                                                                                    op
                                                                                }
                                                                                value={
                                                                                    op
                                                                                }
                                                                            >
                                                                                {
                                                                                    op
                                                                                }
                                                                            </option>
                                                                        )
                                                                    )}
                                                                </select>
                                                                <input
                                                                    type="number"
                                                                    placeholder="Value"
                                                                    value={
                                                                        cond.value ??
                                                                        0
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateStepValidation(
                                                                            bIdx,
                                                                            cIdx,
                                                                            'value',
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    style={{
                                                                        flex: 1,
                                                                        padding:
                                                                            '4px',
                                                                    }}
                                                                />
                                                            </>
                                                        )}

                                                        <button
                                                            onClick={() =>
                                                                removeStepValidation(
                                                                    bIdx,
                                                                    cIdx
                                                                )
                                                            }
                                                            style={{
                                                                color: 'red',
                                                                background:
                                                                    'none',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                                fontWeight:
                                                                    'bold',
                                                            }}
                                                        >
                                                            X
                                                        </button>
                                                    </div>
                                                )
                                            })}
                                            <button
                                                onClick={() =>
                                                    addStepValidation(bIdx)
                                                }
                                                style={{
                                                    fontSize: '0.78rem',
                                                    padding: '2px 8px',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    marginTop: '4px',
                                                }}
                                            >
                                                + Add Condition (AND)
                                            </button>
                                        </div>
                                    </div>
                                )
                            )}

                            <button
                                onClick={addStepConditionBlock}
                                style={{
                                    fontSize: '0.8rem',
                                    padding: '4px 10px',
                                    marginTop: '10px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    background: '#fff3e0',
                                    border: '1px solid #e65100',
                                    color: '#e65100',
                                    fontWeight: 'bold',
                                }}
                            >
                                + Add OR Block
                            </button>
                        </div>

                        {/* On Step Success Actions */}
                        <div
                            style={{
                                marginTop: '15px',
                                background: '#e8f5e9',
                                padding: '15px',
                                borderRadius: '6px',
                                border: '1px solid #a5d6a7',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '10px',
                                }}
                            >
                                <label
                                    style={{
                                        display: 'block',
                                        fontWeight: 'bold',
                                        color: '#2e7d32',
                                    }}
                                >
                                    Actions on Step Success
                                </label>
                                <button
                                    onClick={() =>
                                        addStepAction(activeStepIndex)
                                    }
                                    style={{
                                        padding: '4px 10px',
                                        background: '#388e3c',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                    }}
                                >
                                    + Add Action
                                </button>
                            </div>
                            {(activeStepData?.onSuccessActions || []).length ===
                                0 && (
                                <p
                                    style={{
                                        fontSize: '0.9rem',
                                        color: '#555',
                                        fontStyle: 'italic',
                                        margin: '0 0 8px 0',
                                    }}
                                >
                                    No actions. Add outputs to show when this
                                    step is completed.
                                </p>
                            )}
                            {(activeStepData?.onSuccessActions || []).map(
                                (action, actionIndex) => (
                                    <div
                                        key={actionIndex}
                                        style={{
                                            marginBottom: '10px',
                                            padding: '10px',
                                            background: '#f1f8e9',
                                            borderRadius: '6px',
                                            border: '1px solid #c8e6c9',
                                        }}
                                    >
                                        {/* Row 1: Target Output + Target Image + Remove */}
                                        <div
                                            style={{
                                                display: 'flex',
                                                gap: '8px',
                                                alignItems: 'flex-end',
                                                marginBottom: '10px',
                                            }}
                                        >
                                            <div style={{ flex: 1 }}>
                                                <label
                                                    style={{
                                                        display: 'block',
                                                        fontSize: '0.75rem',
                                                        color: '#555',
                                                        marginBottom: '2px',
                                                    }}
                                                >
                                                    Target Output
                                                </label>
                                                <select
                                                    value={
                                                        action.outputId || ''
                                                    }
                                                    onChange={(e) =>
                                                        updateStepAction(
                                                            activeStepIndex,
                                                            actionIndex,
                                                            'outputId',
                                                            e.target.value
                                                        )
                                                    }
                                                    style={{
                                                        width: '100%',
                                                        padding: '6px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #ccc',
                                                        fontSize: '0.85rem',
                                                    }}
                                                >
                                                    <option value="">
                                                        -- Select Output --
                                                    </option>
                                                    {(formData.components || [])
                                                        .filter(
                                                            (c) =>
                                                                c.type ===
                                                                    'output' ||
                                                                c.type ===
                                                                    'custom_output'
                                                        )
                                                        .map((oc) => (
                                                            <option
                                                                key={oc.id}
                                                                value={oc.id}
                                                            >
                                                                {oc.name ||
                                                                    oc.id}{' '}
                                                                (Port:{' '}
                                                                {PORT_NAME_MAP[
                                                                    oc.port
                                                                ] || oc.port}
                                                                )
                                                            </option>
                                                        ))}
                                                </select>
                                            </div>
                                            <div style={{ flex: 2 }}>
                                                <ImageUploadField
                                                    label="Target Image"
                                                    value={
                                                        action.targetImage || ''
                                                    }
                                                    onChange={(val) =>
                                                        updateStepAction(
                                                            activeStepIndex,
                                                            actionIndex,
                                                            'targetImage',
                                                            val
                                                        )
                                                    }
                                                />
                                            </div>
                                            <button
                                                onClick={() =>
                                                    removeStepAction(
                                                        activeStepIndex,
                                                        actionIndex
                                                    )
                                                }
                                                style={{
                                                    padding: '6px 10px',
                                                    background: '#f44336',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    flexShrink: 0,
                                                    marginBottom: '2px',
                                                }}
                                            >
                                                ✕
                                            </button>
                                        </div>

                                        {/* Row 2: Hardware Bytes config */}
                                        <div
                                            style={{
                                                padding: '10px',
                                                background: action.useRawBytes
                                                    ? '#fff3e0'
                                                    : '#f5f5f5',
                                                borderRadius: '6px',
                                                border: action.useRawBytes
                                                    ? '2px solid #ff9800'
                                                    : '1px solid #ddd',
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            <label
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold',
                                                    color: action.useRawBytes
                                                        ? '#e65100'
                                                        : '#555',
                                                    fontSize: '0.82rem',
                                                    marginBottom: '6px',
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={
                                                        action.useRawBytes ||
                                                        false
                                                    }
                                                    onChange={(e) => {
                                                        updateStepAction(
                                                            activeStepIndex,
                                                            actionIndex,
                                                            'useRawBytes',
                                                            e.target.checked
                                                        )
                                                        if (e.target.checked) {
                                                            updateStepAction(
                                                                activeStepIndex,
                                                                actionIndex,
                                                                'savedBytes',
                                                                null
                                                            )
                                                        } else {
                                                            updateStepAction(
                                                                activeStepIndex,
                                                                actionIndex,
                                                                'rawBytesText',
                                                                ''
                                                            )
                                                        }
                                                    }}
                                                />
                                                Send Custom / Raw Bytes on
                                                success
                                            </label>

                                            {action.useRawBytes ? (
                                                /* Raw bytes textarea + Send Live */
                                                <div>
                                                    <label
                                                        style={{
                                                            display: 'block',
                                                            fontSize: '0.78rem',
                                                            color: '#795548',
                                                            marginBottom: '4px',
                                                        }}
                                                    >
                                                        Directly paste the bytes
                                                        (e.g.{' '}
                                                        <code>[76, 87]</code> or{' '}
                                                        <code>
                                                            [[76, 87], [76, 0]]
                                                        </code>
                                                        )
                                                    </label>
                                                    <textarea
                                                        value={
                                                            action.rawBytesText ||
                                                            ''
                                                        }
                                                        onChange={(e) =>
                                                            updateStepAction(
                                                                activeStepIndex,
                                                                actionIndex,
                                                                'rawBytesText',
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder="e.g. [76, 87, 80, 5, 32] OR GAG,1"
                                                        style={{
                                                            width: '100%',
                                                            minHeight: '50px',
                                                            padding: '6px 8px',
                                                            borderRadius: '4px',
                                                            border: '1px solid #ff9800',
                                                            fontFamily:
                                                                'monospace',
                                                            fontSize: '0.82rem',
                                                            resize: 'vertical',
                                                            boxSizing:
                                                                'border-box',
                                                        }}
                                                    />
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            gap: '8px',
                                                            alignItems:
                                                                'center',
                                                            marginTop: '8px',
                                                        }}
                                                    >
                                                        <img
                                                            src={
                                                                webSerial?.isConnected
                                                                    ? renderImage(
                                                                          'buletooth'
                                                                      )
                                                                    : renderImage(
                                                                          'buletoothOff'
                                                                      )
                                                            }
                                                            onClick={
                                                                requestBluetoothConnection
                                                            }
                                                            alt="Bluetooth"
                                                            title={
                                                                webSerial?.isConnected
                                                                    ? 'Device connected'
                                                                    : 'Click to connect device'
                                                            }
                                                            style={{
                                                                width: 'auto',
                                                                height: '28px',
                                                                cursor: webSerial?.isConnected
                                                                    ? 'default'
                                                                    : 'pointer',
                                                            }}
                                                        />
                                                        <button
                                                            onClick={async () => {
                                                                if (
                                                                    !webSerial?.isConnected
                                                                ) {
                                                                    alert(
                                                                        'Connect your device first.'
                                                                    )
                                                                    return
                                                                }
                                                                const liveKey = `${activeStepIndex}-${actionIndex}`
                                                                const result =
                                                                    validateAndParseRawBytes(
                                                                        action.rawBytesText ||
                                                                            ''
                                                                    )
                                                                if (
                                                                    !result.valid ||
                                                                    result.value
                                                                        .length ===
                                                                        0
                                                                ) {
                                                                    alert(
                                                                        'Cannot send — invalid or empty bytes.'
                                                                    )
                                                                    return
                                                                }
                                                                setActionRawBytesLiveKey(
                                                                    liveKey
                                                                )
                                                                try {
                                                                    // result.value is always a 2D array; send each packet
                                                                    for (const packet of result.value) {
                                                                        worker.postMessage(
                                                                            {
                                                                                type: 'writeArray',
                                                                                value: packet,
                                                                            }
                                                                        )
                                                                        await new Promise(
                                                                            (
                                                                                r
                                                                            ) =>
                                                                                setTimeout(
                                                                                    r,
                                                                                    100
                                                                                )
                                                                        )
                                                                    }
                                                                } catch (err) {
                                                                    console.error(
                                                                        'Action raw bytes send error:',
                                                                        err
                                                                    )
                                                                } finally {
                                                                    setTimeout(
                                                                        () =>
                                                                            setActionRawBytesLiveKey(
                                                                                null
                                                                            ),
                                                                        600
                                                                    )
                                                                }
                                                            }}
                                                            style={{
                                                                padding:
                                                                    '4px 12px',
                                                                background:
                                                                    actionRawBytesLiveKey ===
                                                                    `${activeStepIndex}-${actionIndex}`
                                                                        ? '#ff9800'
                                                                        : '#34a853',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius:
                                                                    '4px',
                                                                cursor: 'pointer',
                                                                fontWeight:
                                                                    'bold',
                                                                fontSize:
                                                                    '0.8rem',
                                                            }}
                                                        >
                                                            {actionRawBytesLiveKey ===
                                                            `${activeStepIndex}-${actionIndex}`
                                                                ? 'Sending...'
                                                                : '⚡ Send Live'}
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                /* Record bytes via ByteRecorder */
                                                <div>
                                                    <button
                                                        onClick={() =>
                                                            openByteRecorderForAction(
                                                                activeStepIndex,
                                                                actionIndex
                                                            )
                                                        }
                                                        style={{
                                                            padding: '5px 12px',
                                                            background:
                                                                '#6200ea',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '0.82rem',
                                                            marginBottom: '5px',
                                                        }}
                                                    >
                                                        Click here to record
                                                    </button>
                                                    {action.savedBytes && (
                                                        <div
                                                            style={{
                                                                background:
                                                                    '#1e1e1e',
                                                                color: '#4ec9b0',
                                                                padding:
                                                                    '6px 10px',
                                                                borderRadius:
                                                                    '4px',
                                                                fontFamily:
                                                                    'monospace',
                                                                fontSize:
                                                                    '0.78rem',
                                                                marginTop:
                                                                    '4px',
                                                            }}
                                                        >
                                                            Saved bytes:{' '}
                                                            {action.savedBytes
                                                                .map(
                                                                    (b) =>
                                                                        `[${b.join(', ')}]`
                                                                )
                                                                .join(' → ')}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* --- NEW: Final Step Configuration --- */}
            {formData.totalSteps > 0 && (
                <div
                    style={{
                        marginBottom: '30px',
                        background: '#f6f9ff',
                        padding: '20px',
                        borderRadius: '8px',
                        border: '1px solid #dbe7ff',
                    }}
                >
                    <h2 style={{ margin: '0 0 15px 0', color: '#1f3b77' }}>
                        Final Step (Success)
                    </h2>

                    <div style={{ marginBottom: '15px' }}>
                        <label
                            style={{
                                display: 'block',
                                fontWeight: 'bold',
                                marginBottom: '5px',
                            }}
                        >
                            Final Step Title (Header)
                        </label>
                        <input
                            type="text"
                            value={formData.finalStepTitle || ''}
                            onChange={(e) =>
                                handleFinalStepChange(
                                    'finalStepTitle',
                                    e.target.value
                                )
                            }
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                boxSizing: 'border-box',
                            }}
                            placeholder="e.g. Tutorial Complete!"
                        />
                    </div>

                    <div>
                        <label
                            style={{
                                display: 'block',
                                fontWeight: 'bold',
                                marginBottom: '5px',
                            }}
                        >
                            Final Step Instruction (Voiceover/Subtitle)
                        </label>
                        <textarea
                            value={formData.finalStepInstruction || ''}
                            onChange={(e) =>
                                handleFinalStepChange(
                                    'finalStepInstruction',
                                    e.target.value
                                )
                            }
                            rows="3"
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                resize: 'none',
                                boxSizing: 'border-box',
                                fontFamily: 'inherit',
                            }}
                            placeholder="Explain what the user should do next..."
                        />
                    </div>
                </div>
            )}

            {/* Byte Recorder Popup */}
            <ByteRecorderPopup
                isOpen={byteRecorderOpen}
                onClose={() => {
                    setByteRecorderOpen(false)
                    setByteRecorderCompIndex(null)
                    setByteRecorderActionContext(null)
                }}
                onRecord={handleByteRecorded}
                componentType={(() => {
                    if (byteRecorderActionContext != null) {
                        const step =
                            formData.steps[byteRecorderActionContext.stepIndex]
                        const action = (step?.onSuccessActions || [])[
                            byteRecorderActionContext.actionIndex
                        ]
                        const outComp = (formData.components || []).find(
                            (c) => c.id === action?.outputId
                        )
                        return outComp?.name || ''
                    }
                    return byteRecorderCompIndex != null
                        ? formData.components[byteRecorderCompIndex]?.name
                        : ''
                })()}
                port={(() => {
                    if (byteRecorderActionContext != null) {
                        const step =
                            formData.steps[byteRecorderActionContext.stepIndex]
                        const action = (step?.onSuccessActions || [])[
                            byteRecorderActionContext.actionIndex
                        ]
                        const outComp = (formData.components || []).find(
                            (c) => c.id === action?.outputId
                        )
                        return outComp?.port || ''
                    }
                    return byteRecorderCompIndex != null
                        ? formData.components[byteRecorderCompIndex]?.port
                        : ''
                })()}
                boardType={boardDevice}
                initialValue={(() => {
                    if (byteRecorderActionContext != null) {
                        const step =
                            formData.steps[byteRecorderActionContext.stepIndex]
                        const action = (step?.onSuccessActions || [])[
                            byteRecorderActionContext.actionIndex
                        ]
                        return action?.recordedValue || 0
                    }
                    return (
                        formData.components[byteRecorderCompIndex]?.properties
                            ?.recordedValue || 0
                    )
                })()}
            />

            {/* ANIMATION SELECTION MODAL */}
            {/* Orientation Modal */}
            {orientationModalOpen &&
                (() => {
                    const ctx = orientationModalContext
                    const isMain = ctx.type === 'main'
                    const isButtonAction = ctx.type === 'buttonModelAction'
                    const isSliderAction = ctx.type === 'sliderModelAction'
                    const isLegacyInteraction = ctx.type === 'interaction'

                    // Derive the target action object for new context types
                    const inputCompForAction =
                        isButtonAction || isSliderAction
                            ? formData.components[ctx.compIndex]
                            : null
                    const buttonAction = isButtonAction
                        ? ((inputCompForAction?.properties?.modelActions || {})[
                              ctx.eventKey
                          ] || [])[ctx.actionIndex]
                        : null
                    const sliderAction = isSliderAction
                        ? (inputCompForAction?.properties?.modelActions || [])[
                              ctx.actionIndex
                          ]
                        : null

                    // Find the relevant model component for preview
                    const targetModelId =
                        buttonAction?.targetModelId ||
                        sliderAction?.targetModelId
                    const modelComp =
                        (targetModelId &&
                            formData.components.find(
                                (c) => c.id === targetModelId
                            )) ||
                        formData.components.find((c) => c.type === 'model')
                    if (!modelComp) return null

                    // Legacy interaction lookup
                    const interaction = isLegacyInteraction
                        ? (modelComp.interactions || [])[ctx.interactionIndex]
                        : null

                    const previewUrl = isMain
                        ? modelComp.modelUrl
                        : isButtonAction
                          ? buttonAction?.targetModelUrl || modelComp.modelUrl
                          : isSliderAction
                            ? sliderAction?.targetModelUrl || modelComp.modelUrl
                            : interaction?.targetModelUrl || modelComp.modelUrl
                    const stepAnim =
                        formData.steps[activeStepIndex]?.models?.animationName
                    const previewAnim = isMain
                        ? stepAnim
                        : isButtonAction
                          ? buttonAction?.targetAnimation || stepAnim
                          : isSliderAction
                            ? sliderAction?.targetAnimation || stepAnim
                            : interaction?.targetAnimation || stepAnim
                    const currentOrbit = isMain
                        ? modelComp.cameraOrbit
                        : isButtonAction
                          ? buttonAction?.targetOrbit
                          : isSliderAction
                            ? sliderAction?.targetOrbit
                            : interaction?.targetOrbit

                    return (
                        <OrientationModal // Note: If you renamed your file to CameraControlModal, update the tag name!
                            isOpen={true}
                            modelUrl={previewUrl}
                            animationName={previewAnim}
                            initialOrbit={
                                currentOrbit || {
                                    theta: 0,
                                    phi: 75,
                                    radius: 100,
                                }
                            } // Using new prop
                            onClose={() => setOrientationModalOpen(false)}
                            onSubmit={(orbit) => {
                                // Now receives theta, phi, radius
                                setFormData((prev) => {
                                    const newComps = [...prev.components]

                                    if (isMain) {
                                        const mIdx = newComps.findIndex(
                                            (c) => c.type === 'model'
                                        )
                                        if (mIdx === -1) return prev
                                        newComps[mIdx] = {
                                            ...newComps[mIdx],
                                            cameraOrbit: orbit,
                                        }
                                    } else if (isButtonAction) {
                                        const comp = {
                                            ...newComps[ctx.compIndex],
                                        }
                                        const modelActions = {
                                            ...(comp.properties.modelActions ||
                                                {}),
                                        }
                                        const actions = [
                                            ...(modelActions[ctx.eventKey] ||
                                                []),
                                        ]
                                        actions[ctx.actionIndex] = {
                                            ...actions[ctx.actionIndex],
                                            targetOrbit: orbit,
                                        }
                                        modelActions[ctx.eventKey] = actions
                                        comp.properties = {
                                            ...comp.properties,
                                            modelActions,
                                        }
                                        newComps[ctx.compIndex] = comp
                                    } else if (isSliderAction) {
                                        const comp = {
                                            ...newComps[ctx.compIndex],
                                        }
                                        const modelActions = [
                                            ...(comp.properties.modelActions ||
                                                []),
                                        ]
                                        modelActions[ctx.actionIndex] = {
                                            ...modelActions[ctx.actionIndex],
                                            targetOrbit: orbit,
                                        }
                                        comp.properties = {
                                            ...comp.properties,
                                            modelActions,
                                        }
                                        newComps[ctx.compIndex] = comp
                                    } else {
                                        // Legacy interaction path
                                        const mIdx = newComps.findIndex(
                                            (c) => c.type === 'model'
                                        )
                                        if (mIdx === -1) return prev
                                        const interactions = [
                                            ...(newComps[mIdx].properties
                                                .interactions || []),
                                        ]
                                        const iIdx = ctx.interactionIndex
                                        if (interactions[iIdx]) {
                                            interactions[iIdx] = {
                                                ...interactions[iIdx],
                                                targetOrbit: orbit,
                                            }
                                        }
                                        newComps[mIdx] = {
                                            ...newComps[mIdx],
                                            properties: {
                                                ...newComps[mIdx].properties,
                                                interactions,
                                            },
                                        }
                                    }
                                    return { ...prev, components: newComps }
                                })
                                setOrientationModalOpen(false)
                            }}
                        />
                    )
                })()}

            {showAnimModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                    }}
                >
                    <div
                        style={{
                            background: '#fff',
                            padding: '30px',
                            borderRadius: '8px',
                            width: '400px',
                            maxHeight: '80vh',
                            overflowY: 'auto',
                        }}
                    >
                        <h2 style={{ marginTop: 0 }}>Select Animation</h2>
                        <p style={{ color: '#666' }}>
                            Choose the animation that should play for this step.
                        </p>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                                margin: '20px 0',
                            }}
                        >
                            {availableAnimations.map((anim, idx) => (
                                <label
                                    key={idx}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        padding: '8px',
                                        background: '#f9f9f9',
                                        borderRadius: '4px',
                                    }}
                                >
                                    <input
                                        type="radio"
                                        name="single_animation_select"
                                        checked={selectedAnimation === anim}
                                        onChange={() =>
                                            setSelectedAnimation(anim)
                                        }
                                        style={{
                                            marginRight: '10px',
                                            width: '18px',
                                            height: '18px',
                                        }}
                                    />
                                    <strong>{idx + 1}.</strong>&nbsp; {anim}
                                </label>
                            ))}
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '10px',
                            }}
                        >
                            <button
                                onClick={() => setShowAnimModal(false)}
                                style={{
                                    padding: '8px 16px',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmAnimationSelection}
                                style={{
                                    padding: '8px 16px',
                                    background: '#1a73e8',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                }}
                            >
                                Confirm Selection
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default PlayAdmin
